import React, { useState, useEffect, useRef } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, where, orderBy, doc, getDoc, addDoc, setDoc, arrayUnion, serverTimestamp, updateDoc, Timestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  MagnifyingGlassIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  CommandLineIcon,
  ClockIcon,
  EnvelopeIcon,
  MapPinIcon,
  DocumentIcon,
  ExclamationCircleIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  PlusIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  GlobeAltIcon,
  PhotoIcon,
  CalendarDaysIcon,
  ChatBubbleLeftEllipsisIcon,
  PencilSquareIcon,
  PhoneArrowUpRightIcon,
  SparklesIcon,
  ArrowUpTrayIcon,
  FireIcon,
  ArrowRightIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/20/solid';
import FollowUpEmailModal from './FollowUpEmailModal';
import OffMarketModal from './OffMarketModal';
import PendingSearcherModal from './PendingSearcherModal';
import EmailHistoryPanel from './EmailHistoryPanel';
import BrokersTab from './BrokersTab';
import UploadBrokerPrequalificationModal from './UploadBrokerPrequalificationModal';
import BrokerHub from './BrokerHub';
import SalesCommissionHub from './SalesCommissionHub';

const tabs = [
  { name: "Today's Brief", section: 'brief', icon: SparklesIcon },
  { name: 'Broker Hub', section: 'brokerHub', icon: UserGroupIcon },
  { name: 'Borrower / Listing Prequalifications', section: 'prequalifications', icon: UserGroupIcon },
  { name: 'Full Deals', section: 'fullDeals', icon: BuildingOfficeIcon },
  { name: 'Brokers', section: 'brokers', icon: BriefcaseIcon },
  { name: 'Sales & Commissions', section: 'commissions', icon: CurrencyDollarIcon },
  { name: 'Follow-ups', section: 'followups', icon: PhoneIcon },
  { name: 'Scraper', section: 'scraper', icon: CommandLineIcon },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const BenPortal = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('brief');
  const [prequalFilter, setPrequalFilter] = useState('all');
  const [fullDeals, setFullDeals] = useState([]);
  const [recentUploads, setRecentUploads] = useState([]);
  const [recentPrequalCreations, setRecentPrequalCreations] = useState([]);
  const [recentBrokerSignups, setRecentBrokerSignups] = useState([]);
  const [docsByLoanId, setDocsByLoanId] = useState({});
  const [docsByPrequalId, setDocsByPrequalId] = useState({});
  const [copiedBorrowerLinkId, setCopiedBorrowerLinkId] = useState('');
  const [collapsedStages, setCollapsedStages] = useState(new Set());
  const toggleStageCollapsed = (key) => {
    setCollapsedStages(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const handleMarkAsFullDeal = async (item, kind, e) => {
    if (e) e.stopPropagation();
    const name = item.businessName || `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email || 'this record';
    if (!window.confirm(`Move ${name} to Full Deals? Isak will manage it from there. (You can flip the source back later if needed.)`)) return;
    try {
      if (kind === 'borrower') {
        await updateDoc(doc(db, 'loanApplications', item.id), {
          source: 'broker_deal',
          loanStatus: item.loanStatus || 'In progress',
          markedFullDealAt: serverTimestamp(),
          markedFullDealBy: 'Ben',
          hideFromPrequalTab: true,
        });
        setBorrowerSignups(prev => prev.filter(b => b.id !== item.id));
        await fetchFullDeals();
      } else if (kind === 'listing') {
        // For listings: if a chosen buyer's loan exists, promote that; otherwise
        // there's no loanApplication to promote — explain.
        const targetLoanId = item.chosenBuyer?.loanApplicationId
          || item.loanApplicationId
          || null;
        if (!targetLoanId) {
          alert("This listing doesn't have a borrower attached yet, so there's no loan to promote to Full Deals. Choose a buyer first, then mark as full deal.");
          return;
        }
        await updateDoc(doc(db, 'loanApplications', targetLoanId), {
          source: 'broker_deal',
          loanStatus: 'In progress',
          prequalificationId: item.id,
          brokerId: item.brokerId || null,
          markedFullDealAt: serverTimestamp(),
          markedFullDealBy: 'Ben',
        });
        if (item.source !== 'borrower') {
          await updateDoc(doc(db, 'prequalificationApplications', item.id), {
            hideFromPrequalTab: true,
            promotedToFullDealAt: serverTimestamp(),
          });
        }
        setBusinessPrequals(prev => prev.filter(p => p.id !== item.id));
        await fetchFullDeals();
      }
    } catch (err) {
      console.error('Error marking as full deal:', err);
      alert('Could not mark as full deal. ' + (err.message || ''));
    }
  };

  // Full Deals tab: select-mode + hide handlers (mirrors Brokers tab).
  // Sets hideFromFullDeals on the loanApplication doc, which fetchFullDeals
  // filters out. Underlying loan + deals stay intact.
  const [fullDealsSelectMode, setFullDealsSelectMode] = useState(false);
  const [selectedFullDealIds, setSelectedFullDealIds] = useState(() => new Set());
  const [fullDealsBulkRemoving, setFullDealsBulkRemoving] = useState(false);
  const toggleFullDealSelection = (id) => {
    setSelectedFullDealIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const exitFullDealsSelectMode = () => {
    setFullDealsSelectMode(false);
    setSelectedFullDealIds(new Set());
  };

  const handleDismissFullDeal = async (deal, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const name = `${deal.firstName || ''} ${deal.lastName || ''}`.trim() || deal.businessName || deal.email || 'this deal';
    if (!window.confirm(`Hide ${name} from Full Deals? The loan application stays intact — this just keeps the tab clean.`)) return;
    try {
      await updateDoc(doc(db, 'loanApplications', deal.id), {
        hideFromFullDeals: true,
        hiddenFromFullDealsAt: serverTimestamp(),
        hiddenFromFullDealsBy: 'Ben',
      });
      setFullDeals((prev) => prev.filter((d) => d.id !== deal.id));
    } catch (err) {
      console.error('Hide full deal failed:', err);
      alert('Could not hide this deal. ' + (err.message || ''));
    }
  };

  const handleBulkRemoveFullDeals = async () => {
    if (selectedFullDealIds.size === 0) return;
    if (!window.confirm(
      `Hide ${selectedFullDealIds.size} deal${selectedFullDealIds.size === 1 ? '' : 's'} from this tab?\n\nThe loan applications stay intact — they just won't show up on Full Deals anymore.`
    )) return;
    setFullDealsBulkRemoving(true);
    try {
      const ids = Array.from(selectedFullDealIds);
      await Promise.all(ids.map((id) =>
        updateDoc(doc(db, 'loanApplications', id), {
          hideFromFullDeals: true,
          hiddenFromFullDealsAt: serverTimestamp(),
          hiddenFromFullDealsBy: 'Ben',
        })
      ));
      const hidden = new Set(ids);
      setFullDeals((prev) => prev.filter((d) => !hidden.has(d.id)));
      exitFullDealsSelectMode();
    } catch (err) {
      console.error('Bulk hide full deals failed:', err);
      alert('Could not hide some deals: ' + (err.message || ''));
    } finally {
      setFullDealsBulkRemoving(false);
    }
  };

  // Prequalifications tab: select-mode + bulk hide. Mirrors the Brokers /
  // Full Deals patterns. Selection key is `${type}:${id}` since we mix
  // borrowers (loanApplications doc) and listings (prequalificationApplications
  // doc, or borrower-source listing with no own doc) in one feed.
  const [prequalSelectMode, setPrequalSelectMode] = useState(false);
  const [selectedPrequalKeys, setSelectedPrequalKeys] = useState(() => new Set());
  const [prequalBulkRemoving, setPrequalBulkRemoving] = useState(false);
  const prequalKey = (item) => `${item._type || 'unknown'}:${item.id}`;
  const togglePrequalSelection = (item) => {
    const key = prequalKey(item);
    setSelectedPrequalKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const exitPrequalSelectMode = () => {
    setPrequalSelectMode(false);
    setSelectedPrequalKeys(new Set());
  };
  const handleBulkRemovePrequals = async (allItems) => {
    if (selectedPrequalKeys.size === 0) return;
    if (!window.confirm(
      `Hide ${selectedPrequalKeys.size} record${selectedPrequalKeys.size === 1 ? '' : 's'} from this tab?\n\nThe underlying loan applications and prequalifications stay intact — they just won't show up here anymore.`
    )) return;
    setPrequalBulkRemoving(true);
    try {
      const targets = allItems.filter((i) => selectedPrequalKeys.has(prequalKey(i)));
      const borrowerIds = [];
      const listingIds = [];
      const borrowerSourceListingIds = []; // synthetic listings — local-only hide
      targets.forEach((item) => {
        if (item._type === 'borrower') {
          borrowerIds.push(item.id);
        } else if (item._type === 'listing' && item.source !== 'borrower') {
          listingIds.push(item.id);
        } else {
          borrowerSourceListingIds.push(item.id);
        }
      });
      await Promise.all([
        ...borrowerIds.map((id) =>
          updateDoc(doc(db, 'loanApplications', id), {
            hideFromPrequalTab: true,
            hiddenAt: serverTimestamp(),
            hiddenBy: 'Ben',
          })
        ),
        ...listingIds.map((id) =>
          updateDoc(doc(db, 'prequalificationApplications', id), {
            hideFromPrequalTab: true,
            hiddenAt: serverTimestamp(),
            hiddenBy: 'Ben',
          })
        ),
      ]);
      const hiddenBorrowers = new Set(borrowerIds);
      const hiddenListings = new Set([...listingIds, ...borrowerSourceListingIds]);
      setBorrowerSignups((prev) => prev.filter((b) => !hiddenBorrowers.has(b.id)));
      setBusinessPrequals((prev) => prev.filter((p) => !hiddenListings.has(p.id)));
      exitPrequalSelectMode();
    } catch (err) {
      console.error('Bulk hide prequals failed:', err);
      alert('Could not hide some records: ' + (err.message || ''));
    } finally {
      setPrequalBulkRemoving(false);
    }
  };

  const handleDismissFromPrequalTab = async (item, kind, e) => {
    if (e) e.stopPropagation();
    const name = item.businessName || `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email || 'this record';
    if (!window.confirm(`Hide ${name} from this tab? The underlying loan application is preserved — this just keeps the pipeline clean.`)) return;
    try {
      if (kind === 'borrower') {
        await updateDoc(doc(db, 'loanApplications', item.id), {
          hideFromPrequalTab: true,
          hiddenAt: serverTimestamp(),
          hiddenBy: 'Ben',
        });
        setBorrowerSignups(prev => prev.filter(b => b.id !== item.id));
      } else if (kind === 'listing' && item.source !== 'borrower') {
        await updateDoc(doc(db, 'prequalificationApplications', item.id), {
          hideFromPrequalTab: true,
          hiddenAt: serverTimestamp(),
          hiddenBy: 'Ben',
        });
        setBusinessPrequals(prev => prev.filter(p => p.id !== item.id));
      } else {
        // Borrower-source listing: just hide locally for this session
        setBusinessPrequals(prev => prev.filter(p => p.id !== item.id));
      }
    } catch (err) {
      console.error('Error hiding record:', err);
      alert('Could not hide this record. ' + (err.message || ''));
    }
  };

  // Map of "recently-active" identifiers -> latest activity timestamp.
  // Keys can be loanApplicationId, prequalId, brokerId, or lowercased email.
  const recentActivity = React.useMemo(() => {
    const map = new Map();
    const bump = (key, when) => {
      if (!key || !when) return;
      const existing = map.get(key);
      if (!existing || when > existing) map.set(key, when);
    };
    recentUploads.forEach(u => {
      const when = u.when || null;
      bump(u.loanApplicationId, when);
      bump(u.applicationId, when);
      bump(u.uploadLinkId, when);
      bump(u.brokerId, when);
      if (u.email) bump(u.email.toLowerCase(), when);
    });
    return map;
  }, [recentUploads]);

  const getActivityTime = (record) => {
    if (!record) return null;
    const ids = [record.id, record.loanApplicationId, record.applicationId, record.brokerId];
    const emails = [record.email, record.brokerEmail, record.borrowerEmail].filter(Boolean).map(e => e.toLowerCase());
    let latest = null;
    for (const id of ids) {
      if (!id) continue;
      const t = recentActivity.get(id);
      if (t && (!latest || t > latest)) latest = t;
    }
    for (const e of emails) {
      const t = recentActivity.get(e);
      if (t && (!latest || t > latest)) latest = t;
    }
    return latest;
  };

  const isHot = (record) => !!getActivityTime(record);
  const documentLooksLikeLoi = (file) => {
    const text = [file?.label, file?.documentName, file?.fileName].filter(Boolean).join(' ').toLowerCase();
    return /(^|\s|[_-])loi($|\s|[_-])|letter\s+of\s+intent/.test(text);
  };
  const docsForRecord = (record) => {
    if (!record) return [];
    if (record.source === 'borrower' && record.loanApplicationId) return docsByLoanId[record.loanApplicationId] || [];
    if (docsByLoanId[record.id]) return docsByLoanId[record.id];
    return docsByPrequalId[record.id] || [];
  };
  const isUnderLoi = (record) => record?.underLoi === true || docsForRecord(record).some(documentLooksLikeLoi);
  const copyBorrowerListingLink = async (listing, event) => {
    event?.stopPropagation();
    const url = `https://lended.ai/prequal-signup/${listing.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedBorrowerLinkId(listing.id);
      window.setTimeout(() => setCopiedBorrowerLinkId(current => current === listing.id ? '' : current), 2000);
    } catch {
      window.prompt('Copy this borrower signup link:', url);
    }
  };
  // Green glow shared across "new activity" and "ready for prequal" so the visual
  // language matches across tabs. Distinct shades + the badge text carry the
  // semantic difference.
  const hotCardClasses = 'ring-2 ring-teal-400 shadow-[0_0_18px_rgba(45,212,191,0.55)] animate-pulse-teal';
  const readyCardClasses = 'ring-2 ring-emerald-400 shadow-[0_0_18px_rgba(52,211,153,0.55)] animate-pulse-emerald';

  // Detect when a borrower has uploaded everything needed for Ben to run prequal
  // (PFS + 3 years of personal tax returns).
  const PFS_LABEL = 'Personal Financial Statement';
  const PERSONAL_TAX_LABELS = [
    'Personal Tax Return - Current Year',
    'Personal Tax Return - Last Year',
    'Personal Tax Return - 2 Years Ago',
  ];
  const matchDocLabel = (doc, target) => {
    const labels = [doc.label, doc.documentName, doc.fileName].filter(Boolean).map(s => s.toString().toLowerCase());
    const t = target.toLowerCase();
    if (labels.some(l => l === t)) return true;
    // Loose match for "Personal Tax Return YYYY" style labels
    if (target.toLowerCase().includes('personal tax return') && labels.some(l => /personal\s*tax\s*return/.test(l))) return true;
    return false;
  };
  const isBorrowerReadyForPrequal = (signup) => {
    if (!signup || signup.prequalified || signup.borrowerPrequalAmount > 0) return false;
    const docs = docsByLoanId[signup.id] || [];
    if (!docs.length) return false;
    const hasPFS = docs.some(d => matchDocLabel(d, PFS_LABEL));
    if (!hasPFS) return false;
    // Need at least 3 distinct personal tax returns
    const taxReturnLabels = new Set();
    docs.forEach(d => {
      const lbl = (d.label || d.documentName || d.fileName || '').toString().toLowerCase();
      if (/personal\s*tax\s*return/.test(lbl)) {
        taxReturnLabels.add(lbl);
      }
    });
    return taxReturnLabels.size >= 3;
  };
  const isBusinessReadyForPrequal = (prequal) => {
    if (!prequal) return false;
    if (prequal.status === 'prequalified' || prequal.prequalified || prequal.prequalifiedAmount) return false;
    if (prequal.allDocumentsUploaded === true) return true;
    if (prequal.source === 'borrower' && prequal.loanApplicationId) {
      const docs = docsByLoanId[prequal.loanApplicationId] || [];
      // Need at least PFS + 3 personal tax returns + some business docs (rough heuristic: 5+ files)
      const hasPFS = docs.some(d => matchDocLabel(d, PFS_LABEL));
      const taxCount = new Set(docs.filter(d => /personal\s*tax\s*return/.test((d.label || d.documentName || d.fileName || '').toString().toLowerCase())).map(d => (d.label || d.documentName || '').toLowerCase())).size;
      if (hasPFS && taxCount >= 3 && docs.length >= 5) return true;
      return false;
    }
    // Listing-side prequal
    const docs = docsByPrequalId[prequal.id] || [];
    if (!docs.length) return false;
    // Heuristic: a few files uploaded (business tax returns × 3 + maybe P&L)
    const businessTaxCount = new Set(docs.filter(d => /business\s*tax\s*return/.test((d.label || d.documentName || d.fileName || '').toString().toLowerCase())).map(d => (d.label || d.documentName || '').toLowerCase())).size;
    return businessTaxCount >= 3;
  };

  const sortHotFirst = (list) => [...list].sort((a, b) => {
    const ta = getActivityTime(a)?.getTime?.() || 0;
    const tb = getActivityTime(b)?.getTime?.() || 0;
    return tb - ta;
  });
  // Sort: ready-for-prequal first, then hot, then everything else.
  const sortReadyAndHotFirst = (list, readyFn) => [...list].sort((a, b) => {
    const ar = readyFn(a) ? 1 : 0;
    const br = readyFn(b) ? 1 : 0;
    if (ar !== br) return br - ar;
    const ta = getActivityTime(a)?.getTime?.() || 0;
    const tb = getActivityTime(b)?.getTime?.() || 0;
    return tb - ta;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [borrowerSignups, setBorrowerSignups] = useState([]);
  const [businessPrequals, setBusinessPrequals] = useState([]);
  const [loading, setLoading] = useState(true);

  // Follow-up states
  const [initialPrequalFilesNeeded, setInitialPrequalFilesNeeded] = useState([]); // Borrowers who need to upload personal files first
  const [intakeListFollowups, setIntakeListFollowups] = useState([]);
  const [newDealFollowups, setNewDealFollowups] = useState([]);
  const [businessFollowups, setBusinessFollowups] = useState([]);
  const [brokerFollowups, setBrokerFollowups] = useState([]);
  const [offMarketSearchers, setOffMarketSearchers] = useState([]);
  const [pendingSearchers, setPendingSearchers] = useState([]);
  const [pendingBrokers, setPendingBrokers] = useState([]);
  const [pendingMeetings, setPendingMeetings] = useState([]);

  // Modal states
  const [showOffMarketModal, setShowOffMarketModal] = useState(false);
  const [showPendingSearcherModal, setShowPendingSearcherModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalData, setEmailModalData] = useState({
    recipientEmail: '',
    recipientName: '',
    templateType: 'intake',
    businessName: '',
    customSubject: '',
    customBody: '',
    autoLogRecord: null  // record to auto-log follow-up on email send
  });
  const [currentEmailSearcher, setCurrentEmailSearcher] = useState(null); // Track which searcher we're emailing
  const [emailStatuses, setEmailStatuses] = useState({}); // Track email reply statuses by recipient email
  const [showEmailHistoryModal, setShowEmailHistoryModal] = useState(false);
  const [emailHistoryTarget, setEmailHistoryTarget] = useState(null);

  // Today's follow-ups
  const [todaysFollowups, setTodaysFollowups] = useState([]);
  const [todayFilter, setTodayFilter] = useState('all');
  const [expandedTodayId, setExpandedTodayId] = useState(null);

  // Follow-up log modal
  const [showLogModal, setShowLogModal] = useState(false);
  const [logModalRecord, setLogModalRecord] = useState(null);
  const [logFormData, setLogFormData] = useState({
    contactType: 'Call',
    notes: '',
    nextFollowUp: ''
  });
  const [savingLog, setSavingLog] = useState(false);

  // Notes modal for pending searchers
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesModalContent, setNotesModalContent] = useState('');
  const [notesModalName, setNotesModalName] = useState('');

  // Text modal (Project Blue)
  const [showTextModal, setShowTextModal] = useState(false);
  const [textModalRecord, setTextModalRecord] = useState(null);
  const [textMessage, setTextMessage] = useState('');
  const [textHistory, setTextHistory] = useState([]);
  const [sendingText, setSendingText] = useState(false);
  const [loadingTextHistory, setLoadingTextHistory] = useState(false);

  // Expanded log history per card
  const [expandedLogs, setExpandedLogs] = useState({});

  // Mark Complete dropdown state (tracks which record's dropdown is open by id)
  const [markCompleteDropdown, setMarkCompleteDropdown] = useState(null);

  // Auto-schedule follow-up interval (days)
  const DEFAULT_FOLLOWUP_INTERVAL_DAYS = 2;

  // Follow-up cadence: 1 day, 1 day, 2 days, 3 days, then newsletter
  const FOLLOWUP_CADENCE = [1, 1, 2, 3]; // days between follow-ups
  const getNextFollowupDays = (contactCount) => {
    if (contactCount >= FOLLOWUP_CADENCE.length) return null; // move to newsletter
    return FOLLOWUP_CADENCE[contactCount] || 2;
  };

  // Follow-up schedules per sequence type (days between follow-ups)
  const FOLLOWUP_SCHEDULES = {
    pending_searcher: [1, 2, 3, 4, 4],     // FU1: Day 1, FU2: Day 3, FU3: Day 6, FU4: Day 10, FU5: Day 14
    awaiting_docs: [1, 2, 4, 5, 6],         // FU1: Day 1, FU2: Day 3, FU3: Day 7, FU4: Day 12, FU5: Day 18
    broker_signup: [1, 2, 3, 4, 4],         // Same as pending_searcher
    broker_no_deals: [1, 3, 5, 7, 9],      // FU1: Day 1, FU2: Day 4, FU3: Day 9, FU4: Day 16, FU5: Day 25
    broker_awaiting_prequal: [1, 2, 4, 5, 6], // Same as awaiting_docs
    pending_meeting: [1, 1, 2, 3, 4],       // FU1: Day 1, FU2: Day 2, FU3: Day 4, FU4: Day 7, FU5: Day 11
    pending_franchise: [1, 2, 3, 4, 4],    // FU1: Day 1, FU2: Day 3, FU3: Day 6, FU4: Day 10, FU5: Day 14
    pending_startup: [1, 2, 3, 4, 4],      // Same cadence as franchise
  };

  // Auto-schedule interval when no date picked (days until next follow-up)
  const AUTO_INTERVAL = {
    pending_searcher: 3,
    awaiting_docs: 5,
    broker_signup: 3,
    broker_no_deals: 7,
    broker_awaiting_prequal: 4,
    pending_meeting: 2,
    pending_franchise: 3,
    pending_startup: 3,
  };

  // Determine the sequence type for a record
  const getSequenceType = (record) => {
    if ((record.collection === 'pendingSearcherSignups' || record.category === 'pending') && record.searcherType === 'franchise') {
      return 'pending_franchise';
    }
    if ((record.collection === 'pendingSearcherSignups' || record.category === 'pending') && record.searcherType === 'startup') {
      return 'pending_startup';
    }
    if ((record.collection === 'pendingSearcherSignups' || record.category === 'pending') && record.noShow !== true) {
      return 'pending_searcher';
    }
    if (record.collection === 'loanApplications') {
      const hasPersonalFiles = record.allPersonalFilesUploaded;
      const hasPrequal = record.borrowerPrequalAmount > 0 || record.prequalified;
      if (!hasPersonalFiles && !hasPrequal) return 'awaiting_docs';
      return null; // prequalified or BEN action items — no sequence
    }
    if (record.collection === 'pendingBrokerSignups') {
      return 'broker_signup';
    }
    if (record.collection === 'brokers') {
      const listingCount = record.totalListings || 0;
      if (listingCount === 0) return 'broker_no_deals';
      return 'broker_awaiting_prequal';
    }
    if (record.collection === 'pendingMeetings') {
      return 'pending_meeting';
    }
    return null;
  };

  // Email templates for each sequence type and follow-up number
  const getEmailTemplate = (sequenceType, followUpNumber, record) => {
    const firstName = record.firstName || record.name?.split(' ')[0] || '';
    const portalSignUpLink = 'https://lended.ai/searchersignup';
    const brokerPortalSignUpLink = 'https://lended.ai/broker-signup';
    const secureUploadLink = `https://lended.ai/upload/${record.sharedUploadPendingId || record.id}`;

    const templates = {
      pending_searcher: {
        1: {
          subject: 'Quick check-in — Lended Portal',
          body: `Hi ${firstName},\n\nJust following up on the email I sent over — wanted to make sure it landed okay and the sign-up link is working for you.\n\nHere it is again in case you need it:\n${portalSignUpLink}\n\nOnce you're in it's pretty quick. Let me know if anything's off.\n\nIsak\nisak@lended.ai | lended.ai`
        },
        2: {
          subject: 'Still need you in the portal — Lended',
          body: `Hi ${firstName},\n\nWanted to circle back — haven't seen your sign-up come through yet.\n\nNo worries at all, just want to make sure you're not running into any issues.\n\nHere's the link again:\n${portalSignUpLink}\n\nLiterally just name, email, and you're in. Once you're set up I can walk you through exactly what we need and we can move fast.\n\nIsak\nisak@lended.ai | lended.ai`
        },
        3: {
          subject: 'Is there anything I can help with? — Lended',
          body: `Hi ${firstName},\n\nJust checking in again on the portal sign-up.\n\nI know things get busy — if there's something making it tricky or you have questions about what the process looks like, happy to just jump on a quick call and walk you through it. Usually takes 10 minutes total.\n\nOr if you'd rather just do it now:\n${portalSignUpLink}\n\nEither way, let me know what works.\n\nIsak\nisak@lended.ai | lended.ai`
        },
        4: {
          subject: "Don't want to lose your spot — Lended",
          body: `Hi ${firstName},\n\nI'll keep this short — I don't want to keep following up if the timing just isn't right, but I also don't want you to miss out if you're still serious about moving on an acquisition.\n\nYou looked like someone we can actually help. The prequal process is fast once you're in the portal and the sooner we have your info the sooner we can tell you exactly what you qualify for.\n\nSign-up link:\n${portalSignUpLink}\n\nIf the timing's genuinely off, just let me know and I'll make a note.\n\nIsak\nisak@lended.ai | lended.ai`
        },
        5: {
          subject: 'Last one from me — Lended',
          body: `Hi ${firstName},\n\nThis is the last follow-up I'll send — I don't want to be that guy.\n\nIf you're still interested in SBA acquisition financing, we're here. The sign-up link is below and we can pick up right where we left off whenever the timing works for you.\n\n${portalSignUpLink}\n\nIf not, no hard feelings at all.\n\nIsak\nisak@lended.ai | lended.ai`
        }
      },
      awaiting_docs: {
        1: {
          subject: `${firstName}, just need a few documents to get you prequalified`,
          body: `Hi ${firstName},\n\nThanks for signing up! To get your prequalification started, we just need a few documents:\n\n- Personal tax returns (last 3 years)\n- Personal financial statement\n- Check your portal for the full list and current status\n\nYou can upload everything securely here: ${secureUploadLink}\n\nIt usually takes about 5 minutes. Let me know if you have any questions!\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        2: {
          subject: `Quick reminder — your documents`,
          body: `Hi ${firstName},\n\nJust a quick reminder that we're still waiting on a few documents to get your prequalification going. You can check what's needed and upload here:\n\n${secureUploadLink}\n\nMost people finish in under 5 minutes. Happy to hop on a quick call if you need any help!\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        3: {
          subject: `${firstName} — need help uploading?`,
          body: `Hi ${firstName},\n\nI noticed we're still missing some documents. I totally understand if you've been busy — I can walk you through exactly what we need on a quick call if that's easier.\n\nUpload link: ${secureUploadLink}\n\nThe main things we need are your personal tax returns and financial statement. Once we have those, we can get your prequal letter turned around fast.\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        4: {
          subject: `Checking in on your prequalification`,
          body: `Hi ${firstName},\n\nI've been trying to get your prequalification wrapped up but we still need a few documents from you. This is totally free and having a prequal letter makes a huge difference when you're making offers.\n\nUpload here: ${secureUploadLink}\n\nIf you're no longer interested, just let me know — no hard feelings!\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        5: {
          subject: `Final follow-up on your documents`,
          body: `Hi ${firstName},\n\nThis will be my last follow-up about your documents. I'll move you over to our newsletter so you'll still get useful SBA financing content.\n\nIf you ever want to pick things back up, just reply to this email and upload your docs here: ${secureUploadLink}\n\nWishing you the best!\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        }
      },
      broker_signup: {
        1: {
          subject: `${firstName}, welcome to the Lended broker network`,
          body: `Hi ${firstName},\n\nThanks for your interest in Lended! We help brokers get their buyers prequalified in 24 hours so deals close faster.\n\nHere's your broker portal sign-up link: ${brokerPortalSignUpLink}\n\nOnce you're set up, you can start uploading listings and we'll match them with prequalified buyers.\n\nLooking forward to working together!\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        2: {
          subject: `Following up — broker portal access`,
          body: `Hi ${firstName},\n\nJust wanted to follow up and make sure you got the broker portal link. We've been turning around prequals in 24 hours and brokers are telling us it's speeding up their deal flow significantly.\n\n${brokerPortalSignUpLink}\n\nWould love to get you set up — let me know if you have any questions!\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        3: {
          subject: `${firstName} — quick question`,
          body: `Hi ${firstName},\n\nI've been trying to connect — are you still looking for SBA financing support for your buyers? We work with a lot of brokers and our 24-hour prequal turnaround has been a big differentiator.\n\nHere's the portal link if you'd like to sign up: ${brokerPortalSignUpLink}\n\nHappy to jump on a quick call to show you how it works.\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        4: {
          subject: `Last check-in from Lended`,
          body: `Hi ${firstName},\n\nJust reaching out one more time. If you have any active deals where buyers need SBA financing, we can help get them prequalified fast.\n\n${brokerPortalSignUpLink}\n\nIf the timing isn't right, no worries — I'll keep you in the loop via our broker newsletter.\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        5: {
          subject: `Moving you to our broker newsletter`,
          body: `Hi ${firstName},\n\nI'm going to add you to our broker newsletter so you'll stay up to date on what we're doing. If you ever want to get set up on the portal, just reply and I'll get you going right away.\n\n${brokerPortalSignUpLink}\n\nAll the best!\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        }
      },
      broker_no_deals: {
        1: {
          subject: `${firstName}, let's get your first listing prequalified`,
          body: `Hi ${firstName},\n\nThanks for signing up on the broker portal! I'd love to get one of your listings prequalified — it usually takes 24 hours once we have the docs.\n\nWhat are you working on right now? Happy to take a look at anything you've got.\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        2: {
          subject: `Any listings to send over?`,
          body: `Hi ${firstName},\n\nJust checking in — do you have any active listings where buyers might need SBA financing? We can turn around prequals in 24 hours, which really helps move deals along.\n\nYou can upload listings directly through your portal, or just send me the details and I'll get it set up for you.\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        3: {
          subject: `${firstName} — how can we help?`,
          body: `Hi ${firstName},\n\nI wanted to check in and see if there's anything we can do to help with your deal flow. We've been helping brokers get buyers prequalified quickly so they can make competitive offers.\n\nIf you have any deals in the pipeline, I'd love to take a look. Even preliminary info helps us get started.\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        4: {
          subject: `Still here to help when you're ready`,
          body: `Hi ${firstName},\n\nJust a quick note — whenever you have a listing that could use SBA financing support, we're ready to go. Our turnaround is 24 hours on prequals.\n\nNo rush — just wanted you to know we're here.\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        5: {
          subject: `Adding you to our broker updates`,
          body: `Hi ${firstName},\n\nI'll move you over to our broker newsletter so you'll keep getting updates on our lending capabilities and market insights. Whenever you have a deal that needs SBA financing, just reply and we'll jump right in.\n\nAll the best!\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        }
      },
      pending_franchise: {
        1: {
          subject: `Checking in — franchise file uploads`,
          body: `Hi ${firstName},\n\nJust following up on the email I sent with the file checklist for your franchise financing. Wanted to make sure it came through okay.\n\nThe main things we need to get started are your personal tax returns (3 years), the Franchise Disclosure Document, and your business plan.\n\nUpload link: ${secureUploadLink}\n\nLet me know if you have any questions!\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        2: {
          subject: `Quick reminder — franchise documents`,
          body: `Hi ${firstName},\n\nJust checking in on those franchise files. Once we have your tax returns and FDD uploaded, we can start looking at what's possible on the financing side.\n\nUpload here: ${secureUploadLink}\n\nHappy to hop on a quick call if you need help with any of the items on the list.\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        3: {
          subject: `${firstName} — need help with the file list?`,
          body: `Hi ${firstName},\n\nI know the franchise file list can feel like a lot. Happy to jump on a quick call and figure out what you have ready — we can usually get started with just the tax returns and FDD while you pull the rest together.\n\nUpload link: ${secureUploadLink}\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        4: {
          subject: `Still interested in franchise financing?`,
          body: `Hi ${firstName},\n\nJust wanted to check in — are you still looking to move forward on the franchise financing? The upload link is still active whenever you're ready:\n\n${secureUploadLink}\n\nIf the timing isn't right, just let me know.\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        5: {
          subject: `Last follow-up — Lended`,
          body: `Hi ${firstName},\n\nThis is my last follow-up. No hard feelings if now isn't the right time — the upload link stays active whenever you're ready:\n\n${secureUploadLink}\n\nWishing you the best.\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        }
      },
      pending_startup: {
        1: {
          subject: `Checking in — startup file uploads`,
          body: `Hi ${firstName},\n\nJust following up on the email I sent with the full file list for your startup financing. Wanted to make sure it came through okay.\n\nThe main things to start with are your personal tax returns (3 years), business plan, and financial projections.\n\nUpload link: ${secureUploadLink}\n\nLet me know if you have any questions!\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        2: {
          subject: `Quick reminder — startup documents`,
          body: `Hi ${firstName},\n\nJust checking in on those startup docs. Once we have your tax returns, business plan, and projections, we can start looking at what's possible.\n\nUpload here: ${secureUploadLink}\n\nHappy to hop on a quick call if you want to walk through the list.\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        3: {
          subject: `${firstName} — need help with the file list?`,
          body: `Hi ${firstName},\n\nI know the startup file list is a lot. Happy to jump on a quick call and figure out what you have ready — we can usually get started with the core items (tax returns, business plan, projections) while you pull the rest together.\n\nUpload link: ${secureUploadLink}\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        4: {
          subject: `Still interested in startup financing?`,
          body: `Hi ${firstName},\n\nJust wanted to check in — are you still looking to move forward on the startup financing? The upload link is still active whenever you're ready:\n\n${secureUploadLink}\n\nIf the timing isn't right, just let me know.\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        5: {
          subject: `Last follow-up — Lended`,
          body: `Hi ${firstName},\n\nThis is my last follow-up. No hard feelings if now isn't the right time — the upload link stays active whenever you're ready:\n\n${secureUploadLink}\n\nWishing you the best.\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        }
      },
      pending_meeting: {
        1: {
          subject: `Following up from Alan's call`,
          body: `Hi ${firstName},\n\nIsak here from Lended — following up from Alan's call. He mentioned you were interested in learning more about SBA acquisition financing.\n\nWould love to connect and walk you through how we can help. Here's a link to grab 15 minutes:\nhttps://calendly.com/isak-lended/lended-sba-financing-intro-call\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        2: {
          subject: `Quick check-in — Lended`,
          body: `Hi ${firstName},\n\nJust circling back on the message I sent after Alan's call. Wanted to make sure it came through okay.\n\nIf you're still thinking about SBA financing for an acquisition, happy to walk you through it. Quick 15-minute call:\nhttps://calendly.com/isak-lended/lended-sba-financing-intro-call\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        3: {
          subject: `${firstName} — still interested in connecting?`,
          body: `Hi ${firstName},\n\nI know things get busy so I wanted to check in one more time. If SBA acquisition financing is still on your radar, I'm here to help.\n\nYou can grab a time here whenever works:\nhttps://calendly.com/isak-lended/lended-sba-financing-intro-call\n\nOr just reply and we can figure something out.\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        4: {
          subject: `Don't want to miss you — Lended`,
          body: `Hi ${firstName},\n\nI'll keep this short — I don't want to keep following up if the timing isn't right, but I also don't want you to miss out if you're still serious about acquiring a business.\n\nWe can get you prequalified fast and help you move confidently when the right deal comes along.\n\nhttps://calendly.com/isak-lended/lended-sba-financing-intro-call\n\nIf now's not the right time, just let me know.\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        5: {
          subject: `Last one from me — Lended`,
          body: `Hi ${firstName},\n\nThis is my last follow-up — I don't want to be that guy.\n\nIf you ever want to explore SBA financing for an acquisition, we're here. Just reply or book a call:\nhttps://calendly.com/isak-lended/lended-sba-financing-intro-call\n\nNo hard feelings at all.\n\nIsak\n(914) 414-1106\nisak@lended.ai`
        }
      },
      broker_awaiting_prequal: {
        1: {
          subject: `${firstName}, we need a few docs to prequalify your listing`,
          body: `Hi ${firstName},\n\nThanks for uploading your listing! To get the prequalification turned around, we just need a few documents:\n\n- Business tax returns (last 3 years)\n- Profit & loss statement\n- Balance sheet\n- Check the portal for the full checklist and status\n\nOnce we have everything, we'll have the prequal ready in 24 hours.\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        2: {
          subject: `Quick reminder — listing documents`,
          body: `Hi ${firstName},\n\nJust following up on the documents for your listing. We're ready to get the prequal done as soon as we have the business financials.\n\nYou can upload them through the broker portal, or send them to me directly and I'll get them in the system.\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        3: {
          subject: `${firstName} — need help with the listing docs?`,
          body: `Hi ${firstName},\n\nI know pulling together business financials can take some time. If you need help from the seller's side, I'm happy to reach out to them directly or walk you through exactly what we need.\n\nThe main items are business tax returns, P&L, and balance sheet. Once we have those, 24-hour turnaround on the prequal.\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        4: {
          subject: `Checking in on your listing documents`,
          body: `Hi ${firstName},\n\nWanted to follow up on the listing documents. We're still waiting on the business financials to complete the prequalification. Having a prequal letter can really help attract serious buyers.\n\nLet me know if there's anything I can do to help move things along!\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        },
        5: {
          subject: `Final follow-up on listing prequalification`,
          body: `Hi ${firstName},\n\nThis will be my last follow-up about the listing documents. If you're able to get the business financials together, just reply and we'll get the prequal turned around in 24 hours.\n\nIn the meantime, I'll keep you on our broker updates.\n\nBest,\nIsak\n(914) 414-1106\nisak@lended.ai`
        }
      }
    };

    const seqTemplates = templates[sequenceType];
    if (!seqTemplates) return null;
    const fuNum = Math.min(Math.max(followUpNumber, 1), 5);
    return seqTemplates[fuNum] || null;
  };

  // Determine detailed action needed for each record type
  const getDetailedAction = (record) => {
    const logs = record.followUpLog || [];
    const contactCount = record.followUpCount || logs.length;
    const daysSinceCreated = record.createdAt?.toDate ? Math.floor((new Date() - record.createdAt.toDate()) / (1000*60*60*24)) : 0;
    const lastLog = logs.length > 0 ? logs[logs.length - 1] : null;
    const firstName = record.firstName || record.name?.split(' ')[0] || 'them';
    const sequenceType = getSequenceType(record);

    // === SEQUENCE A: PENDING SEARCHER (not no-show) ===
    if (sequenceType === 'pending_searcher') {
      if (record.noShow) {
        // No-show sub-flow (kept as-is)
        if (contactCount === 0) return { action: `NO SHOW: Reschedule email sent to ${firstName}`, detail: `No-show (${daysSinceCreated}d ago). Auto-email sent from Isak to reschedule. Wait 1 day then follow up.`, priority: 'high', color: 'text-red-700' };
        if (contactCount === 1) return { action: `Text ${firstName}: "Hey, saw we missed each other — want to grab a quick call?"`, detail: `No-show, 1 follow-up sent. Try a casual text. Keep it short.`, priority: 'high', color: 'text-red-600' };
        if (contactCount === 2) return { action: `Call ${firstName} directly — leave voicemail if no answer`, detail: `No-show, 2 contacts. Script: "Hey ${firstName}, Isak from Lended — we had a call scheduled, would love to find 15 min to connect."`, priority: 'medium', color: 'text-red-600' };
        if (contactCount === 3) return { action: `Last attempt: "${firstName}, still interested in SBA financing?"`, detail: `No-show, 3 contacts over ${daysSinceCreated}d. Final reach-out before newsletter drip.`, priority: 'medium', color: 'text-orange-600' };
        return { action: 'Move no-show to Newsletter', detail: `No-show, ${contactCount} contacts, never met. Add to re-engagement drip.`, priority: 'low', color: 'text-gray-500' };
      }
      // Normal pending searcher sequence — they spoke with Isak (sales), got auto-email, haven't signed up yet
      if (contactCount === 0) {
        return { action: `FU 1 · Call/text ${firstName} — confirm they got the sign-up email`, detail: `Pending (${daysSinceCreated}d ago). Auto-email already sent. Call first, text if no answer.\nText: "Hey ${firstName}. Wanted to check in here to see if you had any questions/difficulties with the sign up. Let me know!"`, priority: 'high', color: 'text-orange-700', sequenceType: 'pending_searcher', fuNumber: 1 };
      }
      if (contactCount === 1) {
        return { action: `FU 2 · Follow up ${firstName} — no rush, add value`, detail: `Day 3. Haven't signed up yet.\nText: "Checking in here again. No big rush, but if you're able to get signed up and prequalified, it'll be a much smoother process once you do find a deal."`, priority: 'high', color: 'text-orange-700', sequenceType: 'pending_searcher', fuNumber: 2 };
      }
      if (contactCount === 2) {
        return { action: `FU 3 · Send ${firstName} secure upload link as alternative`, detail: `Day 6. Try different angle — offer the upload link directly.\nText: "Hey ${firstName}. Wanted to send you over a secure upload link in case that may be easier. See below: https://lended.ai/upload/${record.sharedUploadPendingId || record.id}"`, priority: 'medium', color: 'text-orange-600', sequenceType: 'pending_searcher', fuNumber: 3 };
      }
      if (contactCount === 3) {
        return { action: `FU 4 · Direct close ${firstName} — short and direct`, detail: `Day 10. ${daysSinceCreated}d since created. Keep it blunt.\nText: "Hey ${firstName} — is this still of interest?"`, priority: 'medium', color: 'text-orange-600', sequenceType: 'pending_searcher', fuNumber: 4 };
      }
      if (contactCount === 4) {
        return { action: `FU 5 · Last attempt ${firstName} — leave door open, no pressure`, detail: `Day 14. Final follow-up.\nText: "Hey ${firstName}, last follow-up from my end. No hard feelings if now isn't the right time. Door's open whenever you're ready."\nAfter logging → Move to Newsletter`, priority: 'low', color: 'text-orange-500', sequenceType: 'pending_searcher', fuNumber: 5 };
      }
      return { action: 'Move to Newsletter', detail: `${contactCount} contacts, no sign-up. Set followUpStatus to newsletter.`, priority: 'low', color: 'text-gray-500' };
    }

    // === FRANCHISE PENDING (sent file checklist, waiting for uploads) ===
    if (sequenceType === 'pending_franchise') {
      const uploadLink = `https://lended.ai/upload/${record.sharedUploadPendingId || record.id}`;
      if (contactCount === 0) {
        return { action: `FU 1 · Text ${firstName} — confirm they got the file checklist email`, detail: `Franchise searcher (${daysSinceCreated}d ago). Auto-email with file checklist already sent.\nText: "Hey ${firstName}, Ben from Lended — just sent you an email with everything we need to get started on your franchise financing. Let me know if you have any questions!"`, priority: 'high', color: 'text-purple-700', sequenceType: 'pending_franchise', fuNumber: 1 };
      }
      if (contactCount === 1) {
        return { action: `FU 2 · Follow up ${firstName} — check on file uploads`, detail: `Day 3. Files not uploaded yet.\nText: "Hey ${firstName}, checking in — were you able to start uploading those files? The main things we need first are your tax returns and the Franchise Disclosure Document. Upload link: ${uploadLink}"`, priority: 'high', color: 'text-purple-700', sequenceType: 'pending_franchise', fuNumber: 2 };
      }
      if (contactCount === 2) {
        return { action: `FU 3 · Call ${firstName} — offer to walk through file list`, detail: `Day 6. Try calling.\nScript: "Hey ${firstName}, Ben from Lended. Just wanted to check in on the franchise docs — I know it's a decent list. Happy to hop on a quick call and walk through exactly what you have vs. what we still need."`, priority: 'medium', color: 'text-purple-600', sequenceType: 'pending_franchise', fuNumber: 3 };
      }
      if (contactCount === 3) {
        return { action: `FU 4 · Text ${firstName} — is this still of interest?`, detail: `Day 10. ${daysSinceCreated}d since created. Keep it direct.\nText: "Hey ${firstName} — still interested in moving forward on the franchise financing? Just need those files uploaded and we can get to work: ${uploadLink}"`, priority: 'medium', color: 'text-purple-600', sequenceType: 'pending_franchise', fuNumber: 4 };
      }
      if (contactCount === 4) {
        return { action: `FU 5 · Last attempt ${firstName} — leave door open`, detail: `Day 14. Final follow-up.\nText: "Hey ${firstName}, last follow-up from my end. No pressure — whenever you're ready, the upload link is still active: ${uploadLink}"\nAfter logging → Move to Newsletter`, priority: 'low', color: 'text-purple-500', sequenceType: 'pending_franchise', fuNumber: 5 };
      }
      return { action: 'Move to Newsletter', detail: `${contactCount} contacts, no files uploaded. Set followUpStatus to newsletter.`, priority: 'low', color: 'text-gray-500' };
    }

    // === STARTUP PENDING (sent file checklist, waiting for uploads) ===
    if (sequenceType === 'pending_startup') {
      const uploadLink = `https://lended.ai/upload/${record.sharedUploadPendingId || record.id}`;
      if (contactCount === 0) {
        return { action: `FU 1 · Text ${firstName} — confirm they got the file checklist email`, detail: `Startup searcher (${daysSinceCreated}d ago). Auto-email with full file list already sent.\nText: "Hey ${firstName}, Ben from Lended — just sent you an email with everything we need to get a financing look done. Let me know if you have any questions!"`, priority: 'high', color: 'text-cyan-700', sequenceType: 'pending_startup', fuNumber: 1 };
      }
      if (contactCount === 1) {
        return { action: `FU 2 · Follow up ${firstName} — check on file uploads`, detail: `Day 3. Files not uploaded yet.\nText: "Hey ${firstName}, checking in — were you able to start uploading those files? The main things we need first are tax returns, your business plan, and projections. Upload link: ${uploadLink}"`, priority: 'high', color: 'text-cyan-700', sequenceType: 'pending_startup', fuNumber: 2 };
      }
      if (contactCount === 2) {
        return { action: `FU 3 · Call ${firstName} — offer help with the file list`, detail: `Day 6. Try calling.\nScript: "Hey ${firstName}, Ben from Lended. Just checking in on those startup docs — I know it's a lot. Happy to hop on a quick call and figure out what you have ready vs. what we still need. We can usually work with what you've got to start."`, priority: 'medium', color: 'text-cyan-600', sequenceType: 'pending_startup', fuNumber: 3 };
      }
      if (contactCount === 3) {
        return { action: `FU 4 · Text ${firstName} — is this still of interest?`, detail: `Day 10. ${daysSinceCreated}d since created. Keep it direct.\nText: "Hey ${firstName} — still looking to move forward on the startup financing? Upload link is still active whenever you're ready: ${uploadLink}"`, priority: 'medium', color: 'text-cyan-600', sequenceType: 'pending_startup', fuNumber: 4 };
      }
      if (contactCount === 4) {
        return { action: `FU 5 · Last attempt ${firstName} — leave door open`, detail: `Day 14. Final follow-up.\nText: "Hey ${firstName}, last follow-up from my end. No pressure — whenever you're ready, the upload link is still active: ${uploadLink}"\nAfter logging → Move to Newsletter`, priority: 'low', color: 'text-cyan-500', sequenceType: 'pending_startup', fuNumber: 5 };
      }
      return { action: 'Move to Newsletter', detail: `${contactCount} contacts, no files uploaded. Set followUpStatus to newsletter.`, priority: 'low', color: 'text-gray-500' };
    }

    // === SEQUENCE B: AWAITING DOCS (loanApplications — signed up but missing docs) ===
    if (sequenceType === 'awaiting_docs') {
      const isSharedUpload = record.sharedUploadRoute || record.source === 'shared_upload';
      const uploadLink = `https://lended.ai/upload/${record.sharedUploadPendingId || record.id}`;

      // If files were recently uploaded (today/recent) and not yet reviewed — action is to review, not follow up
      const lastUpload = record.lastUserUpdate?.toDate ? record.lastUserUpdate.toDate() : record.lastUserUpdate ? new Date(record.lastUserUpdate) : null;
      const uploadedToday = lastUpload && (new Date() - lastUpload) < 24 * 60 * 60 * 1000;
      const alreadyReviewed = record.filesReviewedAt?.toDate ? record.filesReviewedAt.toDate() >= lastUpload : false;
      if ((uploadedToday || record.stage === 'Recent Upload') && !alreadyReviewed) {
        return { action: `BEN: Review ${firstName}'s uploaded files`, detail: `${firstName} just uploaded documents. Review what came in before following up — they may still be uploading more.`, priority: 'high', color: 'text-red-700', benAction: true };
      }

      if (contactCount === 0) {
        return { action: `FU 1: Email ${firstName} — doc checklist + upload link`, detail: `New ${isSharedUpload ? 'shared upload' : 'signup'} (${daysSinceCreated}d ago). Send email with doc checklist (tax returns 3yr, PFS) and secure upload link: ${uploadLink}. Text: "Hey ${firstName}, just sent you an email with your secure upload link. Most people finish in under 5 min!"`, priority: 'high', color: 'text-blue-700', sequenceType: 'awaiting_docs', fuNumber: 1 };
      }
      if (contactCount === 1) {
        return { action: `FU 2: Call ${firstName} — check on uploads`, detail: `1 contact. Call to check progress. Script: "Hey ${firstName}, just wanted to see if you had a chance to upload those documents. I can walk you through it right now — it's really quick." If no answer, leave voicemail and send text.`, priority: 'high', color: 'text-blue-700', sequenceType: 'awaiting_docs', fuNumber: 2 };
      }
      if (contactCount === 2) {
        return { action: `FU 3: Text ${firstName} — offer help uploading`, detail: `2 contacts. Text: "Hey ${firstName}, just following up on your documents. I can hop on a quick call and walk you through exactly what we need — it's usually just tax returns and a PFS. Upload link: ${uploadLink}"`, priority: 'medium', color: 'text-blue-600', sequenceType: 'awaiting_docs', fuNumber: 3 };
      }
      if (contactCount === 3) {
        return { action: `FU 4: Email ${firstName} — checking in`, detail: `3 contacts over ${daysSinceCreated}d. Send "checking in" email. Mention prequal is free, takes 5 min to upload docs, and helps them move fast on deals. Upload link: ${uploadLink}`, priority: 'medium', color: 'text-blue-600', sequenceType: 'awaiting_docs', fuNumber: 4 };
      }
      if (contactCount === 4) {
        return { action: `FU 5: Final email to ${firstName} — moving to newsletter`, detail: `4 contacts. Send final follow-up. Moving to newsletter after this. Upload link still available: ${uploadLink}`, priority: 'low', color: 'text-blue-500', sequenceType: 'awaiting_docs', fuNumber: 5 };
      }
      return { action: 'Move to Newsletter — no docs uploaded', detail: `${contactCount} follow-ups, still no uploads. Add to re-engagement drip.`, priority: 'low', color: 'text-gray-500' };
    }

    // === SEARCHER (loanApplications) — BEN ACTION ITEMS & PREQUALIFIED ===
    if (record.collection === 'loanApplications') {
      const hasPersonalFiles = record.allPersonalFilesUploaded;
      const hasIntakeFiles = record.borrowerIntakeComplete;
      const hasDeal = record.businesses?.length > 0 || record.loanStatus === 'deal_uploaded';
      const hasPrequal = record.borrowerPrequalAmount > 0 || record.prequalified;
      const workbookDone = record.workbookUploaded || record.workbookParsedAt;
      const isSharedUpload = record.sharedUploadRoute || record.source === 'shared_upload';
      const prequalAmt = record.borrowerPrequalAmount ? '$' + Number(record.borrowerPrequalAmount).toLocaleString() : '';

      // --- BEN ACTION ITEMS ---
      if (hasPersonalFiles && !hasPrequal && !workbookDone) {
        return { action: `BEN: Run prequal for ${firstName}`, detail: `Personal files in. Upload prequal workbook to prequalify. ${isSharedUpload ? '(Shared upload searcher)' : ''}`, priority: 'high', color: 'text-red-700', benAction: true };
      }
      if (hasDeal && record.allDocumentsUploaded && !workbookDone) {
        return { action: `BEN: Complete workbook for ${firstName}'s deal`, detail: `All deal files uploaded. Complete the full workbook and upload to send to Max.`, priority: 'high', color: 'text-red-700', benAction: true };
      }

      // --- PREQUALIFIED — MULTIPLE ACTIONS ---
      if (hasPrequal) {
        const actions = [];
        if (!hasIntakeFiles) {
          actions.push(`Collect borrower intake docs (bank statements, credit report, resume, ID, proof of funds)`);
        }
        if (!hasDeal) {
          const twoWeeksMs = 14 * 24 * 60 * 60 * 1000;
          const lastContact = record.lastContactDate?.toDate ? record.lastContactDate.toDate() : null;
          const dealCheckDue = !lastContact || (new Date() - lastContact) > twoWeeksMs;
          if (dealCheckDue) {
            actions.push(`Check in on new deals — "Any businesses you're looking at? ${prequalAmt ? `You're prequalified for ${prequalAmt}` : 'Your prequal is ready'}"`);
          }
        }
        if (hasDeal && !record.allDocumentsUploaded) {
          if (contactCount === 0) {
            actions.push(`Get deal files uploaded — LOI, CIM, business tax returns, P&L, balance sheet`);
          } else {
            actions.push(`Follow-up #${contactCount + 1} on deal files — "Hey ${firstName}, just need a few more documents to get your term sheet going"`);
          }
        }
        if (actions.length === 0) {
          const twoWeeksMs = 14 * 24 * 60 * 60 * 1000;
          const lastContact = record.lastContactDate?.toDate ? record.lastContactDate.toDate() : null;
          if (!lastContact || (new Date() - lastContact) > twoWeeksMs) {
            return { action: `Bi-weekly check-in with ${firstName}`, detail: `Prequalified ${prequalAmt}. No active needs. "Hey ${firstName}, just checking in — how's the search going?"`, priority: 'low', color: 'text-indigo-600' };
          }
          return null;
        }
        const actionStr = actions.join(' + ');
        const priorityLevel = hasDeal ? 'high' : !hasIntakeFiles ? 'medium' : 'low';
        return {
          action: actionStr,
          detail: `Prequalified ${prequalAmt}. ${isSharedUpload ? '(Shared upload) ' : ''}${hasDeal ? 'Active deal — chase files.' : 'Keep them engaged.'}`,
          priority: priorityLevel,
          color: hasDeal ? 'text-red-600' : 'text-purple-600'
        };
      }

      return { action: `Follow up with ${firstName}`, detail: `Status: ${record.loanStatus || record.status || 'Unknown'}`, priority: 'medium', color: 'text-gray-600' };
    }

    // === SEQUENCE C: BROKER SIGNUP ===
    if (sequenceType === 'broker_signup') {
      const brokerFirst = record.name?.split(' ')[0] || 'broker';
      if (contactCount === 0) {
        return { action: `FU 1: Email + text ${brokerFirst} — broker portal link`, detail: `Pending broker (${daysSinceCreated}d ago). Send intro email with broker portal sign-up link. Text: "Hey ${brokerFirst}, it's Ben from Lended! Sent you an email about our broker portal — we turn around prequals in 24 hours. Let me know if you have questions!"`, priority: 'high', color: 'text-purple-700', sequenceType: 'broker_signup', fuNumber: 1 };
      }
      if (contactCount === 1) {
        return { action: `FU 2: Call ${brokerFirst} — mention 24hr prequal turnaround`, detail: `1 contact. Call and pitch value. Script: "Hey ${brokerFirst}, it's Ben from Lended — just wanted to follow up. Once you're signed up we can turn around prequals in 24 hours, which really helps your buyers compete." Leave voicemail if no answer.`, priority: 'medium', color: 'text-purple-600', sequenceType: 'broker_signup', fuNumber: 2 };
      }
      if (contactCount === 2) {
        return { action: `FU 3: Text ${brokerFirst} — easy to upload a listing`, detail: `2 contacts. Text: "Hey ${brokerFirst}, just following up — once you're on the portal you can upload a listing in under 2 min and we'll get the prequal done in 24 hours. Link: https://lended.ai/broker-signup"`, priority: 'medium', color: 'text-purple-600', sequenceType: 'broker_signup', fuNumber: 3 };
      }
      if (contactCount === 3) {
        return { action: `FU 4: Email ${brokerFirst} — last check-in`, detail: `3 contacts over ${daysSinceCreated}d. Send "last check-in" email. Offer to meet or jump on a quick call.`, priority: 'medium', color: 'text-purple-600', sequenceType: 'broker_signup', fuNumber: 4 };
      }
      if (contactCount === 4) {
        return { action: `FU 5: Final email to ${brokerFirst} — moving to newsletter`, detail: `4 contacts. Send final email. Moving to broker newsletter after this.`, priority: 'low', color: 'text-purple-500', sequenceType: 'broker_signup', fuNumber: 5 };
      }
      return { action: 'Move broker to Newsletter', detail: `${contactCount} contacts, hasn't signed up. Add to broker drip.`, priority: 'low', color: 'text-gray-500' };
    }

    // === SEQUENCE D: BROKER — NO DEALS ===
    if (sequenceType === 'broker_no_deals') {
      const brokerFirst = record.firstName || record.name?.split(' ')[0] || 'broker';
      if (contactCount === 0) {
        return { action: `FU 1: Call ${brokerFirst} — get first listing`, detail: `New broker, no listings. Script: "Hey ${brokerFirst}, I'd love to get one of your listings prequalified. What are you working on right now? We turn things around in 24 hours." If no answer, leave voicemail and send email.`, priority: 'high', color: 'text-purple-700', sequenceType: 'broker_no_deals', fuNumber: 1 };
      }
      if (contactCount === 1) {
        return { action: `FU 2: Email ${brokerFirst} — any listings to send over?`, detail: `1 contact. Send email asking about active listings. Mention you can upload for them or they can do it through the portal.`, priority: 'medium', color: 'text-purple-600', sequenceType: 'broker_no_deals', fuNumber: 2 };
      }
      if (contactCount === 2) {
        return { action: `FU 3: Text ${brokerFirst} — offer value`, detail: `2 contacts. Text: "Hey ${brokerFirst}, we can usually get a prequal turned around in 24 hours — happy to take a look at anything you're working on. Even preliminary info helps us get started."`, priority: 'medium', color: 'text-purple-600', sequenceType: 'broker_no_deals', fuNumber: 3 };
      }
      if (contactCount === 3) {
        return { action: `FU 4: Call ${brokerFirst} — different angle`, detail: `3 contacts over ${daysSinceCreated}d. Try calling again with a different angle. Script: "Hey ${brokerFirst}, just wanted to check in — we've been helping a lot of brokers speed up their deal flow with fast prequals. Anything I can help with?"`, priority: 'medium', color: 'text-purple-600', sequenceType: 'broker_no_deals', fuNumber: 4 };
      }
      if (contactCount === 4) {
        return { action: `FU 5: Final email to ${brokerFirst} — moving to newsletter`, detail: `4 contacts. Send final email. Moving to broker newsletter. Mention you're always available when they have a deal.`, priority: 'low', color: 'text-purple-500', sequenceType: 'broker_no_deals', fuNumber: 5 };
      }
      return { action: 'Move broker to Newsletter', detail: `${contactCount} contacts, no listings. Add to broker drip.`, priority: 'low', color: 'text-gray-500' };
    }

    // === SEQUENCE E: BROKER — AWAITING PREQUAL DOCS ===
    if (sequenceType === 'broker_awaiting_prequal') {
      const brokerFirst = record.firstName || record.name?.split(' ')[0] || 'broker';
      const listingCount = record.totalListings || 0;
      if (contactCount === 0) {
        return { action: `FU 1: Email ${brokerFirst} — need docs for ${listingCount} listing(s)`, detail: `Broker with listings but missing prequal docs. Send email with doc checklist (business tax returns 3yr, P&L, balance sheet). Offer to reach out to seller directly.`, priority: 'high', color: 'text-teal-700', sequenceType: 'broker_awaiting_prequal', fuNumber: 1 };
      }
      if (contactCount === 1) {
        return { action: `FU 2: Call ${brokerFirst} — follow up on listing docs`, detail: `1 contact. Call to check progress on documents. Script: "Hey ${brokerFirst}, just checking in on the listing documents — once we have the business financials we can turn around the prequal in 24 hours. Need any help getting them from the seller?"`, priority: 'high', color: 'text-teal-600', sequenceType: 'broker_awaiting_prequal', fuNumber: 2 };
      }
      if (contactCount === 2) {
        return { action: `FU 3: Text ${brokerFirst} — offer to contact seller`, detail: `2 contacts. Text: "Hey ${brokerFirst}, following up on the listing docs. I'm happy to reach out to the seller directly if that would help speed things up. Just need business tax returns, P&L, and balance sheet."`, priority: 'medium', color: 'text-teal-600', sequenceType: 'broker_awaiting_prequal', fuNumber: 3 };
      }
      if (contactCount === 3) {
        return { action: `FU 4: Email ${brokerFirst} — checking in on docs`, detail: `3 contacts over ${daysSinceCreated}d. Send "checking in" email. Emphasize having a prequal letter helps attract serious buyers.`, priority: 'medium', color: 'text-teal-600', sequenceType: 'broker_awaiting_prequal', fuNumber: 4 };
      }
      if (contactCount === 4) {
        return { action: `FU 5: Final follow-up with ${brokerFirst} on listing docs`, detail: `4 contacts. Send final follow-up email. Mention you'll keep their listing active and they can send docs anytime.`, priority: 'low', color: 'text-teal-500', sequenceType: 'broker_awaiting_prequal', fuNumber: 5 };
      }
      // After FU 5, check in weekly if they have listings
      const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
      const lastActivity = record.lastActivity || record.lastContactDate?.toDate?.() || record.createdAt?.toDate?.();
      const daysSinceActivity = lastActivity ? Math.floor((new Date() - new Date(lastActivity)) / (1000*60*60*24)) : 0;
      if (lastActivity && (new Date() - new Date(lastActivity)) > oneWeekMs) {
        return { action: `Weekly check-in with ${brokerFirst} — ${listingCount} listing(s)`, detail: `${daysSinceActivity}d since last activity. "Hey ${brokerFirst}, any updates on the listing docs? We're ready to turn around the prequal as soon as we have them."`, priority: 'medium', color: 'text-purple-600' };
      }
      return null;
    }

    // === SEQUENCE F: PENDING MEETING (cold call contacts needing to book a strategy call) ===
    if (sequenceType === 'pending_meeting') {
      if (contactCount === 0) {
        return { action: `FU 1 · Call/text ${firstName} — confirm they got the outreach`, detail: `Cold call contact (${daysSinceCreated}d ago). Auto-outreach already sent via ${record.outreachChannel || 'text'}. Call first, text if no answer.\nScript: "Hey ${firstName}, Ben from Lended — we chatted the other day. Just wanted to make sure you got my message. Would love to set up a quick strategy call when you have 15 minutes."`, priority: 'high', color: 'text-yellow-700', sequenceType: 'pending_meeting', fuNumber: 1 };
      }
      if (contactCount === 1) {
        return { action: `FU 2 · Follow up ${firstName} — re-send booking link`, detail: `Day 2. Haven't booked yet.\nScript: "Hey ${firstName}, Ben again from Lended — still would love to connect. Here's my calendar link if it's easier to just grab a time: calendly.com/isak-lended/lended-sba-financing-intro-call"`, priority: 'high', color: 'text-yellow-700', sequenceType: 'pending_meeting', fuNumber: 2 };
      }
      if (contactCount === 2) {
        return { action: `FU 3 · Try different channel with ${firstName}`, detail: `Day 4. Try different channel (if called before, try text/email).\nScript: "Hey ${firstName}, Ben from Lended. Just checking in — is there a better time to connect? Happy to work around your schedule."`, priority: 'medium', color: 'text-yellow-600', sequenceType: 'pending_meeting', fuNumber: 3 };
      }
      if (contactCount === 3) {
        return { action: `FU 4 · Direct close ${firstName} — create urgency`, detail: `Day 7. ${daysSinceCreated}d since cold call.\nScript: "Hey ${firstName}, I don't want to keep bugging you but I also don't want you to miss out. We can get you prequalified fast so you're ready when the right deal comes. Quick 15-min call — calendly.com/isak-lended/lended-sba-financing-intro-call"`, priority: 'medium', color: 'text-yellow-600', sequenceType: 'pending_meeting', fuNumber: 4 };
      }
      if (contactCount === 4) {
        return { action: `FU 5 · Last attempt ${firstName} — leave door open`, detail: `Day 11. Final follow-up.\nScript: "Hey ${firstName}, last follow-up from my end. No hard feelings if now isn't the right time. Door's always open — calendly.com/isak-lended/lended-sba-financing-intro-call"\nAfter logging → Move to Newsletter`, priority: 'low', color: 'text-yellow-500', sequenceType: 'pending_meeting', fuNumber: 5 };
      }
      return { action: 'Move to Newsletter', detail: `${contactCount} contacts, no meeting booked. Set followUpStatus to newsletter.`, priority: 'low', color: 'text-gray-500' };
    }

    // LISTING (prequalificationApplications) — unchanged
    if (record.collection === 'prequalificationApplications') {
      const listingWorkbookDone = record.workbookFileUploaded || record.workbookParsedAt;
      const hasListingDocs = record.documents?.length > 0 || record.allDocumentsUploaded;
      const bizName = record.businessName || 'this listing';

      if (hasListingDocs && !listingWorkbookDone && record.status !== 'prequalified') {
        return { action: `BEN: Upload prequal workbook for ${bizName}`, detail: `Listing docs are in. Complete the prequal workbook and upload.`, priority: 'high', color: 'text-red-700', benAction: true };
      }
      if (record.status === 'prequalified') {
        return { action: `Get searchers/borrowers uploaded for ${bizName}`, detail: `Prequalified listing. Follow up with broker to get interested buyers.`, priority: 'medium', color: 'text-teal-600' };
      }
      return { action: `New listing: ${bizName} — review and process`, detail: `Status: ${record.status || 'New'}. Get broker to upload docs so we can prequalify.`, priority: 'high', color: 'text-teal-700' };
    }

    return { action: record.nextAction || 'Follow up', detail: '', priority: 'medium', color: 'text-gray-600' };
  };

  // Broker logo upload
  const brokerLogoInputRef = useRef(null);
  const [uploadingBrokerId, setUploadingBrokerId] = useState(null);

  // Recently prequalified states
  const [recentlyPrequalifiedBorrowers, setRecentlyPrequalifiedBorrowers] = useState([]);
  const [recentlyPrequalifiedBusinesses, setRecentlyPrequalifiedBusinesses] = useState([]);

  // New Listing modal states
  const [showNewListingModal, setShowNewListingModal] = useState(false);
  const [newListingType, setNewListingType] = useState(null); // 'broker' or 'borrower'
  const [allBrokers, setAllBrokers] = useState([]);
  const [allBorrowers, setAllBorrowers] = useState([]);
  const [selectedBrokerId, setSelectedBrokerId] = useState('');
  const [selectedBorrowerId, setSelectedBorrowerId] = useState('');
  const [isCreatingListing, setIsCreatingListing] = useState(false);
  const [showUploadPrequalModal, setShowUploadPrequalModal] = useState(false);

  // Scraper states
  const [scraperLogs, setScraperLogs] = useState([]);
  const [isScraperRunning, setIsScraperRunning] = useState(false);
  const [scraperConnected, setScraperConnected] = useState(false);
  const [showConversations, setShowConversations] = useState(false);
  const [textedBrokersToday, setTextedBrokersToday] = useState([]);
  const [showTextedBrokers, setShowTextedBrokers] = useState(true);
  const [startListingId, setStartListingId] = useState('');
  const [savedStartId, setSavedStartId] = useState(null);
  const scraperLogsEndRef = React.useRef(null);
  const scraperTerminalRef = React.useRef(null);

  // Cutoff date - only show data from after this date
  const DATA_CUTOFF_DATE = new Date('2026-01-01T00:00:00');

  // Fetch borrower signups (searchers who haven't been prequalified)
  const fetchBorrowerSignups = async () => {
    try {
      const loansRef = collection(db, 'loanApplications');
      // Get all loan applications that are searchers/acquisition
      const q = query(
        loansRef,
        where('loanPurpose', '==', 'Acquisition'),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const allLoans = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(loan => {
        // Filter for data after cutoff date
        const createdAt = loan.createdAt?.toDate ? loan.createdAt.toDate() : null;
        return createdAt && createdAt >= DATA_CUTOFF_DATE;
      });

      // Calculate 2 days ago
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

      // Filter for awaiting prequalification (skip dismissed-from-tab records).
      // A borrower needs their OWN prequalification (borrowerPrequalAmount or
      // explicit prequalified flag) — joining via a prequalified listing doesn't
      // mean they themselves are prequalified yet, so they should still appear
      // here and flow through the upload stages.
      const signups = allLoans.filter(loan => {
        if (loan.hideFromPrequalTab === true) return false;
        if (loan.source === 'broker_deal') return false;
        const isPrequalified = loan.prequalified
          || loan.loanStatus === 'prequalified_for'
          || (loan.borrowerPrequalAmount && loan.borrowerPrequalAmount > 0);
        return !isPrequalified;
      });

      // Filter for recently prequalified (in past 2 days)
      const recentlyPrequal = allLoans.filter(loan => {
        const hasPrequalifiedBusiness = loan.businesses?.some(b => b.prequalified);
        const isPrequalified = loan.prequalified || loan.loanStatus === 'prequalified_for';
        if (!hasPrequalifiedBusiness && !isPrequalified) return false;

        // Check if prequalified in last 2 days
        const prequalDate = loan.prequalifiedAt?.toDate ? loan.prequalifiedAt.toDate() :
                          loan.lastUpdated?.toDate ? loan.lastUpdated.toDate() : null;
        return prequalDate && prequalDate >= twoDaysAgo;
      });

      setBorrowerSignups(signups);
      setRecentlyPrequalifiedBorrowers(recentlyPrequal);
    } catch (error) {
      console.error('Error fetching borrower signups:', error);
    }
  };

  // Fetch business prequalifications (broker-uploaded AND borrower-created that haven't been prequalified)
  const fetchBusinessPrequals = async () => {
    try {
      // 1. Fetch broker-uploaded prequalification applications
      const prequalsRef = collection(db, 'prequalificationApplications');
      const q = query(
        prequalsRef,
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const brokerPrequals = querySnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        source: 'broker'
      })).filter(prequal => {
        if (prequal.hideFromPrequalTab === true) return false;
        if (prequal.archived === true) return false;
        // Filter for data after cutoff date
        const createdAt = prequal.createdAt?.toDate ? prequal.createdAt.toDate() : null;
        return createdAt && createdAt >= DATA_CUTOFF_DATE;
      });

      // 2. Fetch borrower-created businesses from loanApplications
      const loansRef = collection(db, 'loanApplications');
      const loansQuery = query(
        loansRef,
        where('loanPurpose', '==', 'Acquisition'),
        orderBy('createdAt', 'desc')
      );
      const loansSnapshot = await getDocs(loansQuery);

      const borrowerBusinesses = [];
      loansSnapshot.docs.forEach(loanDoc => {
        const loanData = loanDoc.data();

        // Filter for data after cutoff date
        const loanCreatedAt = loanData.createdAt?.toDate ? loanData.createdAt.toDate() : null;
        if (!loanCreatedAt || loanCreatedAt < DATA_CUTOFF_DATE) return;

        // Check if this loan has businesses array
        if (loanData.businesses && Array.isArray(loanData.businesses)) {
          loanData.businesses.forEach((business, index) => {
            borrowerBusinesses.push({
              id: `${loanDoc.id}_business_${index}`,
              loanApplicationId: loanDoc.id,
              businessName: business.businessName || business.name || 'Unnamed Business',
              businessState: business.businessState || business.state || loanData.businessState,
              projectedSalePrice: business.purchasePrice || business.projectedSalePrice,
              ebitda: business.ebitda,
              status: business.prequalified ? 'prequalified' : (business.status || 'pending'),
              prequalified: business.prequalified,
              prequalifiedAmount: business.prequalifiedAmount,
              prequalifiedAt: business.prequalifiedAt,
              createdAt: business.addedAt || loanData.createdAt,
              source: 'borrower',
              borrowerName: `${loanData.firstName || ''} ${loanData.lastName || ''}`.trim() || 'Unknown Borrower',
              borrowerEmail: loanData.email,
              businessIndex: index
            });
          });
        }
      });

      // Combine both sources
      const allPrequals = [...brokerPrequals, ...borrowerBusinesses];

      // Calculate 2 days ago
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

      // Filter for awaiting prequalification
      const pendingPrequals = allPrequals.filter(prequal => {
        if (prequal.source === 'borrower') {
          return !prequal.prequalified;
        }
        return prequal.status !== 'prequalified' && prequal.status !== 'buyer_selected';
      });

      // Filter for recently prequalified (in past 2 days)
      const recentlyPrequal = allPrequals.filter(prequal => {
        if (prequal.source === 'borrower') {
          if (!prequal.prequalified) return false;
        } else {
          if (prequal.status !== 'prequalified' && prequal.status !== 'buyer_selected') return false;
        }

        // Check if prequalified in last 2 days
        const prequalDate = prequal.prequalifiedAt?.toDate ? prequal.prequalifiedAt.toDate() :
                          prequal.lastUpdated?.toDate ? prequal.lastUpdated.toDate() : null;
        return prequalDate && prequalDate >= twoDaysAgo;
      });

      // Fetch broker info for pending prequalifications
      const prequalsWithBrokers = await Promise.all(pendingPrequals.map(async (prequal) => {
        if (prequal.brokerId || prequal.userId) {
          try {
            // Try brokers collection first, then users
            let brokerData = null;
            const brokerId = prequal.brokerId || prequal.userId;
            try {
              const brokerSnap = await getDoc(doc(db, 'brokers', brokerId));
              if (brokerSnap.exists()) brokerData = brokerSnap.data();
            } catch (e) {}
            if (!brokerData) {
              try {
                const userSnap = await getDoc(doc(db, 'users', brokerId));
                if (userSnap.exists()) brokerData = userSnap.data();
              } catch (e) {}
            }
            if (brokerData) {
              return {
                ...prequal,
                brokerName: (brokerData.firstName && brokerData.lastName ? `${brokerData.firstName} ${brokerData.lastName}` : '') || brokerData.brokerAgeName || brokerData.name || brokerData.businessName || brokerData.companyName || 'Unknown Broker',
                brokerEmail: brokerData.email
              };
            }
          } catch (e) {
            console.error('Error fetching broker:', e);
          }
        }
        return { ...prequal, brokerName: 'Unknown Broker' };
      }));

      // Fetch broker info for recently prequalified
      const recentWithBrokers = await Promise.all(recentlyPrequal.map(async (prequal) => {
        if (prequal.brokerId || prequal.userId) {
          try {
            // Try brokers collection first, then users
            let brokerData = null;
            const brokerId = prequal.brokerId || prequal.userId;
            try {
              const brokerSnap = await getDoc(doc(db, 'brokers', brokerId));
              if (brokerSnap.exists()) brokerData = brokerSnap.data();
            } catch (e) {}
            if (!brokerData) {
              try {
                const userSnap = await getDoc(doc(db, 'users', brokerId));
                if (userSnap.exists()) brokerData = userSnap.data();
              } catch (e) {}
            }
            if (brokerData) {
              return {
                ...prequal,
                brokerName: (brokerData.firstName && brokerData.lastName ? `${brokerData.firstName} ${brokerData.lastName}` : '') || brokerData.brokerAgeName || brokerData.name || brokerData.businessName || brokerData.companyName || 'Unknown Broker',
                brokerEmail: brokerData.email
              };
            }
          } catch (e) {
            console.error('Error fetching broker:', e);
          }
        }
        return { ...prequal, brokerName: 'Unknown Broker' };
      }));

      setBusinessPrequals(prequalsWithBrokers);
      setRecentlyPrequalifiedBusinesses(recentWithBrokers);
    } catch (error) {
      console.error('Error fetching business prequalifications:', error);
    }
  };

  // Fetch follow-up data (optimized to reduce queries)
  const fetchFollowups = async () => {
    try {
      const loansRef = collection(db, 'loanApplications');
      const prequalsRef = collection(db, 'prequalificationApplications');
      const brokersRef = collection(db, 'brokers');
      const documentsRef = collection(db, 'documentMetadata');

      // Calculate date thresholds
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

      // BATCH FETCH: Get all data upfront in parallel
      const [loansSnapshot, allDocsSnapshot, allPrequalsSnapshot, brokersSnapshot] = await Promise.all([
        getDocs(query(loansRef, where('loanPurpose', '==', 'Acquisition'))),
        getDocs(documentsRef),
        getDocs(prequalsRef),
        getDocs(brokersRef)
      ]);

      // Build a map of documents by loanApplicationId for quick lookup
      const docsByLoanId = {};
      allDocsSnapshot.docs.forEach(d => {
        const data = d.data();
        const loanId = data.loanApplicationId;
        if (loanId) {
          if (!docsByLoanId[loanId]) docsByLoanId[loanId] = [];
          docsByLoanId[loanId].push(data);
        }
      });
      setDocsByLoanId(docsByLoanId);

      // Build a map of all prequalifications
      const allPrequals = allPrequalsSnapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(item => item.archived !== true);

      // Build a map of brokers for quick lookup
      const brokersMap = {};
      brokersSnapshot.docs.forEach(d => {
        brokersMap[d.id] = { id: d.id, ...d.data() };
      });

      const initialPrequalList = [];
      const intakeList = [];
      const newDealList = [];

      // Process each loan using pre-fetched data
      for (const docSnap of loansSnapshot.docs) {
        const loan = { id: docSnap.id, ...docSnap.data() };

        // Skip shell loanApps for franchise/startup that have no uploads yet — managed via pendingSearcherSignups
        if ((loan.searcherType === 'franchise' || loan.searcherType === 'startup') && !(docsByLoanId[loan.id]?.length > 0)) continue;

        // Filter for data after cutoff date
        const loanCreatedAt = loan.createdAt?.toDate ? loan.createdAt.toDate() : null;
        if (!loanCreatedAt || loanCreatedAt < DATA_CUTOFF_DATE) continue;

        const hasPrequalifiedBusiness = loan.businesses?.some(b => b.prequalified);
        const isPrequalified = loan.prequalified || loan.loanStatus === 'prequalified_for';
        const isBorrowerPrequalified = loan.borrowerPrequalAmount && loan.borrowerPrequalAmount > 0;

        // Use pre-fetched documents
        const uploadedDocs = docsByLoanId[loan.id] || [];
        const hasPersonalFinancialStatement = uploadedDocs.some(d =>
          d.documentName === 'Personal Financial Statement'
        );

        // 0. Initial Prequal Files: Borrowers who haven't uploaded personal files yet
        if (!hasPrequalifiedBusiness && !isPrequalified && !isBorrowerPrequalified && !hasPersonalFinancialStatement) {
          initialPrequalList.push({
            ...loan,
            followUpType: 'initialPrequal',
            reason: 'Needs to upload Personal Financial Statement',
            uploadedDocsCount: uploadedDocs.length
          });
        }

        // 1. Intake List Follow-up: Borrowers who are prequalified (have borrowerPrequalAmount) but haven't completed intake
        if (isBorrowerPrequalified) {
          const hasCompletedIntake = loan.borrowerIntakeComplete;
          if (!hasCompletedIntake) {
            intakeList.push({
              ...loan,
              followUpType: 'intake',
              reason: 'Borrower intake documents needed',
              prequalAmount: loan.borrowerPrequalAmount
            });
          }
        }

        // 2. New Deal Follow-up: Borrowers who haven't uploaded a new deal in a while
        const lastUpdate = loan.lastUpdated?.toDate ? loan.lastUpdated.toDate() :
                          loan.createdAt?.toDate ? loan.createdAt.toDate() : new Date(0);

        if (lastUpdate < sevenDaysAgo && !hasPrequalifiedBusiness && !isPrequalified && !isBorrowerPrequalified && hasPersonalFinancialStatement) {
          newDealList.push({
            ...loan,
            followUpType: 'newDeal',
            lastActivity: lastUpdate,
            reason: 'No new deal uploaded recently'
          });
        }
      }

      setInitialPrequalFilesNeeded(initialPrequalList);
      setIntakeListFollowups(intakeList);
      setNewDealFollowups(newDealList);

      // 3. Business Follow-up: Prequalified businesses with intake not collected
      const businessList = [];
      for (const prequal of allPrequals) {
        // Filter for data after cutoff date
        const prequalCreatedAt = prequal.createdAt?.toDate ? prequal.createdAt.toDate() : null;
        if (!prequalCreatedAt || prequalCreatedAt < DATA_CUTOFF_DATE) continue;

        if (prequal.status !== 'prequalified') continue;

        const hasCompletedIntake = prequal.intakeComplete || prequal.borrowerDocsCollected;
        if (!hasCompletedIntake) {
          // Use pre-fetched broker data
          const brokerId = prequal.brokerId || prequal.userId;
          const broker = brokersMap[brokerId];
          const brokerName = (broker?.firstName && broker?.lastName ? `${broker.firstName} ${broker.lastName}` : '') || broker?.brokerAgeName || broker?.name || broker?.businessName || broker?.companyName || 'Unknown Broker';

          businessList.push({
            ...prequal,
            brokerName,
            followUpType: 'businessIntake',
            reason: 'Prequalified but intake not collected'
          });
        }
      }
      setBusinessFollowups(businessList);

      // 4. Broker Follow-up: weekly cadence on every active broker so Ben pushes
      //    for first deal / weekly buyer progress / new listings.
      // Group prequalifications by brokerId.
      const prequalsByBroker = {};
      allPrequals.forEach(p => {
        const bId = p.brokerId;
        if (bId) {
          if (!prequalsByBroker[bId]) prequalsByBroker[bId] = [];
          prequalsByBroker[bId].push(p);
        }
      });

      const oneDayAgo = new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000);
      const todayMidnight = new Date(); todayMidnight.setHours(0, 0, 0, 0);
      // sevenDaysAgo is already declared at the top of fetchFollowups.

      const brokerList = [];
      for (const brokerId in brokersMap) {
        const broker = brokersMap[brokerId];

        // Filter for brokers created after cutoff date
        const brokerCreatedAt = broker.createdAt?.toDate ? broker.createdAt.toDate() : null;
        if (!brokerCreatedAt || brokerCreatedAt < DATA_CUTOFF_DATE) continue;
        if (broker.followUpStatus === 'newsletter') continue;

        // Last time Ben logged a contact with this broker
        const lastContact = broker.lastContactDate?.toDate
          ? broker.lastContactDate.toDate()
          : (broker.lastContactDate ? new Date(broker.lastContactDate) : null);

        // Skip if already contacted today (don't bug Ben twice in one day)
        if (lastContact && lastContact >= todayMidnight) continue;

        const brokerPrequals = prequalsByBroker[brokerId] || [];

        // Cadence:
        //   never contacted → first nudge after 1 day
        //   already contacted → weekly nudge after 7 days
        const dueForNudge = lastContact
          ? lastContact < sevenDaysAgo
          : brokerCreatedAt < oneDayAgo;
        if (!dueForNudge) continue;

        // Find most recent activity for context shown on the card
        let lastActivity = brokerCreatedAt;
        brokerPrequals.forEach(p => {
          const pDate = p.createdAt?.toDate ? p.createdAt.toDate() : new Date(0);
          if (pDate > lastActivity) lastActivity = pDate;
        });
        if (lastContact && lastContact > lastActivity) lastActivity = lastContact;

        brokerList.push({
          ...broker,
          lastActivity,
          totalListings: brokerPrequals.length,
          followUpType: 'broker',
          reason: brokerPrequals.length === 0
            ? 'Push for first listing'
            : 'Weekly check — buyer progress + any new listings',
        });
      }
      setBrokerFollowups(brokerList);

    } catch (error) {
      console.error('Error fetching follow-ups:', error);
    }
  };

  // Fetch off-market searchers
  const fetchOffMarketSearchers = async () => {
    try {
      const offMarketRef = collection(db, 'offMarketSearchers');
      // Simple query without complex index requirements
      const q = query(offMarketRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      // Filter out closed ones client-side
      const searchers = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(s => s.status !== 'closed');

      setOffMarketSearchers(searchers);
    } catch (error) {
      console.error('Error fetching off-market searchers:', error);
    }
  };

  // Fetch pending searchers (those who haven't signed up yet)
  // Auto-detects signups by checking if email exists in loanApplications
  const fetchPendingSearchers = async () => {
    try {
      const pendingRef = collection(db, 'pendingSearcherSignups');
      const q = query(pendingRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const searchers = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(s => s.signedUp !== true);

      if (searchers.length === 0) {
        setPendingSearchers([]);
        return;
      }

      // Get all loan application emails to check for signups
      const loansRef = collection(db, 'loanApplications');
      const loansSnapshot = await getDocs(query(loansRef, where('loanPurpose', '==', 'Acquisition')));
      const signedUpEmails = new Set(
        loansSnapshot.docs.map(doc => doc.data().email?.toLowerCase().trim()).filter(Boolean)
      );

      // Build a set of loanApp IDs that have uploaded documents
      const docsRef = collection(db, 'documentMetadata');
      const docsSnapshot = await getDocs(docsRef);
      const loanIdsWithDocs = new Set();
      docsSnapshot.docs.forEach(d => {
        const loanId = d.data().loanApplicationId;
        if (loanId) loanIdsWithDocs.add(loanId);
      });

      // Check each pending searcher - auto-transition when appropriate
      const stillPending = [];
      for (const searcher of searchers) {
        const searcherEmail = searcher.email?.toLowerCase().trim();

        // Franchise/startup: transition when files are uploaded to their loanApp
        if (searcher.searcherType && searcher.sharedUploadPendingId) {
          if (loanIdsWithDocs.has(searcher.sharedUploadPendingId)) {
            const searcherRef = doc(db, 'pendingSearcherSignups', searcher.id);
            await updateDoc(searcherRef, {
              signedUp: true,
              signedUpAt: serverTimestamp(),
              autoDetected: true
            });
            // Copy follow-up history to the loanApp
            const loanRef = doc(db, 'loanApplications', searcher.sharedUploadPendingId);
            const updateData = { searcherType: searcher.searcherType };
            if (searcher.followUpLog) updateData.followUpLog = searcher.followUpLog;
            if (searcher.lastLogNotes) updateData.lastLogNotes = searcher.lastLogNotes;
            if (searcher.NextActionDate) updateData.NextActionDate = searcher.NextActionDate;
            if (searcher.lastContactDate) updateData.lastContactDate = searcher.lastContactDate;
            await updateDoc(loanRef, updateData);
            continue;
          }
          stillPending.push(searcher);
          continue;
        }

        // Default pending searchers: transition when email matches a loanApp (they signed up)
        if (searcherEmail && signedUpEmails.has(searcherEmail)) {
          const searcherRef = doc(db, 'pendingSearcherSignups', searcher.id);
          await updateDoc(searcherRef, {
            signedUp: true,
            signedUpAt: serverTimestamp(),
            autoDetected: true
          });

          // Copy follow-up history to the loan application so it carries over
          const matchingLoan = loansSnapshot.docs.find(d =>
            d.data().email?.toLowerCase().trim() === searcherEmail
          );
          if (matchingLoan && searcher.followUpLog) {
            const loanRef = doc(db, 'loanApplications', matchingLoan.id);
            const updateData = { followUpLog: searcher.followUpLog };
            if (searcher.lastLogNotes) updateData.lastLogNotes = searcher.lastLogNotes;
            if (searcher.NextActionDate) updateData.NextActionDate = searcher.NextActionDate;
            if (searcher.lastContactDate) updateData.lastContactDate = searcher.lastContactDate;
            await updateDoc(loanRef, updateData);
          }
        } else {
          stillPending.push(searcher);
        }
      }

      setPendingSearchers(stillPending);
    } catch (error) {
      console.error('Error fetching pending searchers:', error);
    }
  };

  // Fetch pending brokers
  const fetchPendingBrokers = async () => {
    try {
      const pendingRef = collection(db, 'pendingBrokerSignups');
      const q = query(pendingRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const brokers = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        // hideFromBrokerTab drops them from both the Brokers tab AND the
        // Follow-ups pending broker list — set by the bulk-remove action.
        .filter(b => b.signedUp !== true && b.hideFromBrokerTab !== true);

      // Auto-detect if broker signed up by checking brokers collection
      const brokersRef = collection(db, 'brokers');
      const brokersSnap = await getDocs(brokersRef);
      const signedUpEmails = new Set(
        brokersSnap.docs.map(d => d.data().email?.toLowerCase().trim()).filter(Boolean)
      );

      const stillPending = [];
      for (const broker of brokers) {
        const brokerEmail = broker.email?.toLowerCase().trim();
        if (brokerEmail && signedUpEmails.has(brokerEmail)) {
          await updateDoc(doc(db, 'pendingBrokerSignups', broker.id), {
            signedUp: true, signedUpAt: serverTimestamp(), autoDetected: true
          });
        } else {
          stillPending.push(broker);
        }
      }
      setPendingBrokers(stillPending);
    } catch (error) {
      console.error('Error fetching pending brokers:', error);
    }
  };

  // Fetch pending meetings (from Slack cold call intake)
  const fetchPendingMeetings = async () => {
    try {
      const meetingsRef = collection(db, 'pendingMeetings');
      const q = query(meetingsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const meetings = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(m => m.followUpStatus !== 'newsletter' && m.status !== 'booked');
      setPendingMeetings(meetings);
    } catch (error) {
      console.error('Error fetching pending meetings:', error);
    }
  };

  // Fetch today's follow-ups: scheduled NextActionDate items + recent activity (signups, uploads, brokers, listings)
  const fetchTodaysFollowups = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const threeDaysAgo = new Date(today.getTime() - 3 * 24 * 60 * 60 * 1000);

      const todayStart = Timestamp.fromDate(today);
      const todayEnd = Timestamp.fromDate(tomorrow);

      // Helper: check if a Firestore or string date is recent (within last 3 days)
      const isRecent = (dateField) => {
        if (!dateField) return false;
        const d = dateField.toDate ? dateField.toDate() : new Date(dateField);
        return d >= threeDaysAgo;
      };

      // Helper: check if already contacted today (logged follow-up)
      const wasContactedToday = (record) => {
        if (record.lastContactDate) {
          const d = record.lastContactDate.toDate ? record.lastContactDate.toDate() : new Date(record.lastContactDate);
          return d >= today && d < tomorrow;
        }
        return false;
      };

      const results = [];
      const seenIds = new Set();

      const addResult = (item) => {
        const key = `${item.collection}-${item.id}`;
        if (!seenIds.has(key)) {
          seenIds.add(key);
          results.push(item);
        }
      };

      // --- Fetch all data in parallel to avoid composite index issues ---
      const pendingRef = collection(db, 'pendingSearcherSignups');
      const loansRef = collection(db, 'loanApplications');
      const brokersRef = collection(db, 'brokers');
      const prequalsRef = collection(db, 'prequalificationApplications');
      const docsRef = collection(db, 'documentMetadata');
      const pendingMeetingsRef = collection(db, 'pendingMeetings');

      const [pendingSnap, loansSnap, brokersSnap, prequalsSnap, docsSnap, pendingMeetingsSnap] = await Promise.all([
        getDocs(pendingRef),
        getDocs(query(loansRef, where('loanPurpose', '==', 'Acquisition'))),
        getDocs(brokersRef),
        getDocs(prequalsRef),
        getDocs(docsRef),
        getDocs(pendingMeetingsRef)
      ]);

      // --- Today's Action Items cutoff: only show records created after 3/27/2026 ---
      const todayActionsCutoff = new Date('2026-03-27T00:00:00');

      // Helper: check if first follow-up is due (created at least 1 day ago)
      const isFirstFUDue = (createdAt) => {
        if (!createdAt) return false;
        const created = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
        // FU1 is Day 1 = next day after creation. So it's due if created before today.
        return created < today;
      };

      // Helper: check if follow-up is due based on NextActionDate or creation date
      const isFollowUpDue = (data) => {
        const hasScheduledToday = data.NextActionDate?.toDate && data.NextActionDate.toDate() >= today && data.NextActionDate.toDate() < tomorrow;
        const isOverdue = data.NextActionDate?.toDate && data.NextActionDate.toDate() < today;
        // If never contacted and no NextActionDate, check if FU1 is due (created before today)
        const neverContactedAndDue = !data.lastContactDate && !data.NextActionDate && isFirstFUDue(data.createdAt);
        return hasScheduledToday || isOverdue || neverContactedAndDue;
      };

      // Pending searcher signups
      pendingSnap.docs.forEach(d => {
        const data = d.data();
        if (data.signedUp === true) return;
        if (data.followUpStatus === 'newsletter') return;
        if (wasContactedToday(data)) return;
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : null;
        if (!createdAt || createdAt < todayActionsCutoff) return;
        if (!isFollowUpDue(data)) return;
        addResult({
          id: d.id,
          collection: 'pendingSearcherSignups',
          name: data.name || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
          type: 'Searcher',
          stage: data.noShow ? 'No Show' : data.searcherType === 'franchise' ? 'Franchise' : data.searcherType === 'startup' ? 'Startup' : 'Pending Signup',
          nextAction: data.lastLogNotes || 'Follow up',
          ...data
        });
      });

      // Build set of loanApp IDs that have uploaded docs
      const loanIdsWithUploads = new Set();
      docsSnap.docs.forEach(d => {
        const loanId = d.data().loanApplicationId;
        if (loanId) loanIdsWithUploads.add(loanId);
      });

      // Loan applications (skip franchise/startup with no uploads — managed via pendingSearcherSignups)
      loansSnap.docs.forEach(d => {
        const data = d.data();
        if ((data.searcherType === 'franchise' || data.searcherType === 'startup') && !loanIdsWithUploads.has(d.id)) return;
        if (data.followUpStatus === 'newsletter') return;
        if (wasContactedToday(data)) return;
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : null;
        if (!createdAt || createdAt < todayActionsCutoff) return;
        if (!isFollowUpDue(data)) return;
        addResult({
          id: d.id,
          collection: 'loanApplications',
          name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.name || 'Unknown',
          type: 'Searcher',
          stage: data.loanStatus || 'Active',
          nextAction: data.lastLogNotes || 'Follow up',
          ...data
        });
      });

      // Recent document uploads - group by loan, add loans with recent uploads (skip if contacted today)
      const recentDocsByLoan = {};
      docsSnap.docs.forEach(d => {
        const data = d.data();
        if (isRecent(data.uploadedAt) && data.loanApplicationId) {
          if (!recentDocsByLoan[data.loanApplicationId]) recentDocsByLoan[data.loanApplicationId] = [];
          recentDocsByLoan[data.loanApplicationId].push(data.documentName || 'Document');
        }
      });

      // Look up loan details for recent uploads
      for (const loanId of Object.keys(recentDocsByLoan)) {
        if (seenIds.has(`loanApplications-${loanId}`)) continue;
        const loanDoc = loansSnap.docs.find(d => d.id === loanId);
        if (loanDoc) {
          const data = loanDoc.data();
          if (data.followUpStatus === 'newsletter') continue;
          if (wasContactedToday(data)) continue;
          const uploadLoanCreated = data.createdAt?.toDate ? data.createdAt.toDate() : null;
          if (!uploadLoanCreated || uploadLoanCreated < todayActionsCutoff) continue;
          addResult({
            id: loanId,
            collection: 'loanApplications',
            name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.name || 'Unknown',
            type: 'Searcher',
            stage: 'Recent Upload',
            nextAction: `Uploaded: ${recentDocsByLoan[loanId].join(', ')}`,
            ...data
          });
        }
      }

      // Brokers: follow-up due and created after cutoff
      brokersSnap.docs.forEach(d => {
        const data = d.data();
        if (data.followUpStatus === 'newsletter') return;
        if (wasContactedToday(data)) return;
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : null;
        if (!createdAt || createdAt < todayActionsCutoff) return;
        if (!isFollowUpDue(data)) return;
        addResult({
          id: d.id,
          collection: 'brokers',
          name: (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : '') || data.brokerAgeName || data.name || data.businessName || data.companyName || 'Unknown Broker',
          type: 'Broker',
          stage: data.status || 'Active',
          nextAction: data.lastLogNotes || 'Follow up',
          ...data
        });
      });

      // Listings: created after cutoff and either needs prequal work or follow-up is due
      prequalsSnap.docs.forEach(d => {
        const data = d.data();
        if (data.followUpStatus === 'newsletter') return;
        if (wasContactedToday(data)) return;
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : null;
        if (!createdAt || createdAt < todayActionsCutoff) return;
        // Show listings that: need work (not prequalified), have follow-up due, or are new
        const needsWork = data.status !== 'prequalified' && data.status !== 'buyer_selected';
        if (!isFollowUpDue(data) && !isRecent(data.createdAt) && !needsWork) return;
        addResult({
          id: d.id,
          collection: 'prequalificationApplications',
          name: data.businessName || data.name || 'New Listing',
          type: 'Listing',
          stage: 'New Listing',
          nextAction: data.lastLogNotes || 'New listing submitted',
          ...data
        });
      });

      // Pending meetings (cold call contacts needing to book)
      pendingMeetingsSnap.docs.forEach(d => {
        const data = d.data();
        if (data.followUpStatus === 'newsletter') return;
        if (data.status === 'booked') return;
        if (wasContactedToday(data)) return;
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : null;
        if (!createdAt || createdAt < todayActionsCutoff) return;
        if (!isFollowUpDue(data)) return;
        addResult({
          id: d.id,
          collection: 'pendingMeetings',
          name: data.name || 'Unknown',
          type: 'Meeting',
          stage: 'Pending Meeting',
          nextAction: data.lastLogNotes || 'Follow up to book meeting',
          email: data.email,
          phoneNumber: data.phone,
          ...data
        });
      });

      // Sort: Searchers first, then Brokers, then Listings. Alphabetical within each group.
      const typeOrder = { 'Searcher': 0, 'Meeting': 1, 'Broker': 2, 'Listing': 3 };
      results.sort((a, b) => {
        const orderA = typeOrder[a.type] ?? 3;
        const orderB = typeOrder[b.type] ?? 3;
        if (orderA !== orderB) return orderA - orderB;
        return (a.name || '').localeCompare(b.name || '');
      });

      setTodaysFollowups(results);
    } catch (error) {
      console.error('Error fetching today\'s follow-ups:', error);
    }
  };

  // Handle opening the follow-up log modal
  const openLogModal = (record) => {
    setLogModalRecord(record);
    setLogFormData({
      contactType: 'Call',
      notes: '',
      nextFollowUp: ''
    });
    setShowLogModal(true);
  };

  // Handle saving a follow-up log entry
  const handleSaveLog = async () => {
    if (!logModalRecord) return;
    setSavingLog(true);

    try {
      const collectionName = logModalRecord.collection || logModalRecord.followUpCollection;
      const recordId = logModalRecord.id;

      if (!collectionName || !recordId) {
        alert('Cannot save log: missing record info');
        setSavingLog(false);
        return;
      }

      const recordRef = doc(db, collectionName, recordId);

      // Calculate next action date using sequence-aware scheduling
      const currentLogs = logModalRecord.followUpLog || [];
      const currentContactCount = logModalRecord.followUpCount || currentLogs.length; // BEFORE the new log
      const newContactCount = currentContactCount + 1;
      const sequenceType = getSequenceType(logModalRecord);

      let nextActionDate;
      if (newContactCount >= 5) {
        // After FU 5: don't auto-schedule (move to newsletter)
        // Still set a date far out so it doesn't show as overdue
        if (logFormData.nextFollowUp) {
          const picked = new Date(logFormData.nextFollowUp + 'T00:00:00');
          nextActionDate = Timestamp.fromDate(picked);
        } else {
          const farDate = new Date();
          farDate.setDate(farDate.getDate() + 365);
          farDate.setHours(9, 0, 0, 0);
          nextActionDate = Timestamp.fromDate(farDate);
        }
      } else if (logFormData.nextFollowUp) {
        const picked = new Date(logFormData.nextFollowUp + 'T00:00:00');
        nextActionDate = Timestamp.fromDate(picked);
      } else {
        // Use sequence-specific auto-interval, fall back to schedule, then default
        const interval = (sequenceType && AUTO_INTERVAL[sequenceType])
          ? AUTO_INTERVAL[sequenceType]
          : (sequenceType && FOLLOWUP_SCHEDULES[sequenceType] && FOLLOWUP_SCHEDULES[sequenceType][currentContactCount])
            ? FOLLOWUP_SCHEDULES[sequenceType][currentContactCount]
            : DEFAULT_FOLLOWUP_INTERVAL_DAYS;
        const autoDate = new Date();
        autoDate.setDate(autoDate.getDate() + interval);
        autoDate.setHours(9, 0, 0, 0);
        nextActionDate = Timestamp.fromDate(autoDate);
      }

      const nextDateStr = logFormData.nextFollowUp || new Date(nextActionDate.toDate()).toISOString().split('T')[0];

      const logEntry = {
        timestamp: new Date().toISOString(),
        type: logFormData.contactType,
        notes: logFormData.notes,
        loggedBy: 'Ben',
        nextDateSet: nextDateStr
      };

      await updateDoc(recordRef, {
        followUpLog: arrayUnion(logEntry),
        followUpCount: newContactCount,
        lastContactDate: serverTimestamp(),
        lastContactType: logFormData.contactType,
        NextActionDate: nextActionDate,
        lastLogNotes: logFormData.notes,
        ...(newContactCount >= 5 && !logFormData.nextFollowUp ? { status: 'newsletter' } : {})
      });

      setShowLogModal(false);
      setLogModalRecord(null);

      // Refresh data
      await Promise.all([fetchFollowups(), fetchPendingSearchers(), fetchPendingBrokers(), fetchPendingMeetings(), fetchTodaysFollowups(), fetchOffMarketSearchers()]);
    } catch (error) {
      console.error('Error saving follow-up log:', error);
      alert('Error saving log. Please try again.');
    } finally {
      setSavingLog(false);
    }
  };

  // Toggle log history expansion for a card
  const toggleLogExpansion = (recordId) => {
    setExpandedLogs(prev => ({
      ...prev,
      [recordId]: !prev[recordId]
    }));
  };

  // Open notes modal for pending searcher
  const openNotesModal = (name, notes) => {
    setNotesModalName(name);
    setNotesModalContent(notes || '');
    setShowNotesModal(true);
  };

  // Open text modal — loads history and pre-fills based on follow-up script
  const openTextModal = async (record) => {
    const phone = record.phoneNumber || record.phone;
    if (!phone) { alert('No phone number on this contact'); return; }
    setTextModalRecord(record);
    setTextMessage('');
    setTextHistory([]);
    setShowTextModal(true);
    setLoadingTextHistory(true);

    // Load text history
    try {
      const getTextHistoryFn = httpsCallable(functions, 'getTextHistory');
      const result = await getTextHistoryFn({ phone: phone.replace(/\D/g, '') });
      setTextHistory(result.data.logs || []);
    } catch (err) {
      console.error('Error loading text history:', err);
    }
    setLoadingTextHistory(false);
  };

  // Send text via Project Blue
  const handleSendText = async () => {
    if (!textModalRecord || !textMessage.trim()) return;
    setSendingText(true);
    const phone = textModalRecord.phoneNumber || textModalRecord.phone;
    try {
      const sendTextFn = httpsCallable(functions, 'sendText');
      await sendTextFn({
        phone: phone.replace(/\D/g, ''),
        message: textMessage.trim(),
        contactName: textModalRecord.name || null,
        recordId: textModalRecord.id || null,
        recordCollection: textModalRecord.collection || null,
      });
      // Add to local history
      setTextHistory(prev => [{ message: textMessage.trim(), sentAt: new Date().toISOString(), sentBy: 'Ben', status: 'sent' }, ...prev]);

      // Auto-log the text as a follow-up
      if (textModalRecord.collection && textModalRecord.id) {
        try {
          const recordRef = doc(db, textModalRecord.collection, textModalRecord.id);
          const currentLogs = textModalRecord.followUpLog || [];
          const newContactCount = (textModalRecord.followUpCount || currentLogs.length) + 1;
          const sequenceType = getSequenceType(textModalRecord);
          const interval = (sequenceType && AUTO_INTERVAL[sequenceType]) || DEFAULT_FOLLOWUP_INTERVAL_DAYS;
          const nextDate = new Date();
          nextDate.setDate(nextDate.getDate() + interval);
          nextDate.setHours(9, 0, 0, 0);
          const logEntry = { timestamp: new Date().toISOString(), type: 'Text', notes: textMessage.trim(), loggedBy: 'Ben', nextDateSet: nextDate.toISOString().split('T')[0] };
          await updateDoc(recordRef, {
            followUpLog: arrayUnion(logEntry),
            followUpCount: newContactCount,
            lastContactDate: serverTimestamp(),
            lastContactType: 'Text',
            NextActionDate: Timestamp.fromDate(nextDate),
            lastLogNotes: textMessage.trim(),
          });
          await Promise.all([fetchFollowups(), fetchPendingSearchers(), fetchPendingBrokers(), fetchPendingMeetings(), fetchTodaysFollowups(), fetchOffMarketSearchers()]);
        } catch (logErr) {
          console.error('Auto-log text failed:', logErr);
        }
      }
      setTextMessage('');
    } catch (err) {
      console.error('Send text error:', err);
      alert('Failed to send text: ' + (err.message || 'Unknown error'));
    }
    setSendingText(false);
  };

  // Initiate call via Twilio
  const handleInitiateCall = async (record) => {
    const phone = record.phoneNumber || record.phone;
    if (!phone) { alert('No phone number on this contact'); return; }
    if (!window.confirm(`Call ${record.name || phone}?`)) return;
    try {
      const initiateCallFn = httpsCallable(functions, 'initiateCall');
      await initiateCallFn({ phone: phone.replace(/\D/g, ''), contactName: record.name });
      alert(`Call initiated to ${record.name || phone}`);

      // Auto-log the call as a follow-up
      if (record.collection && record.id) {
        try {
          const recordRef = doc(db, record.collection, record.id);
          const currentLogs = record.followUpLog || [];
          const newContactCount = (record.followUpCount || currentLogs.length) + 1;
          const sequenceType = getSequenceType(record);
          const interval = (sequenceType && AUTO_INTERVAL[sequenceType]) || DEFAULT_FOLLOWUP_INTERVAL_DAYS;
          const nextDate = new Date();
          nextDate.setDate(nextDate.getDate() + interval);
          nextDate.setHours(9, 0, 0, 0);
          const logEntry = { timestamp: new Date().toISOString(), type: 'Call', notes: `Called ${record.name || phone}`, loggedBy: 'Ben', nextDateSet: nextDate.toISOString().split('T')[0] };
          await updateDoc(recordRef, {
            followUpLog: arrayUnion(logEntry),
            followUpCount: newContactCount,
            lastContactDate: serverTimestamp(),
            lastContactType: 'Call',
            NextActionDate: Timestamp.fromDate(nextDate),
            lastLogNotes: `Called ${record.name || phone}`,
          });
          await Promise.all([fetchFollowups(), fetchPendingSearchers(), fetchPendingBrokers(), fetchPendingMeetings(), fetchTodaysFollowups(), fetchOffMarketSearchers()]);
        } catch (logErr) {
          console.error('Auto-log call failed:', logErr);
        }
      }
    } catch (err) {
      console.error('Call error:', err);
      alert('Failed to initiate call: ' + (err.message || 'Unknown error'));
    }
  };

  // Fetch all brokers for the new listing modal
  const fetchAllBrokers = async () => {
    try {
      const brokersRef = collection(db, 'brokers');
      const brokersSnapshot = await getDocs(brokersRef);
      const brokers = [];

      for (const brokerDoc of brokersSnapshot.docs) {
        const brokerData = brokerDoc.data();
        // Also try to get more info from users collection
        let name = (brokerData.firstName && brokerData.lastName ? `${brokerData.firstName} ${brokerData.lastName}` : '') || brokerData.brokerAgeName || brokerData.name || brokerData.businessName || brokerData.companyName || '';
        let email = brokerData.email || '';

        if (!name || !email) {
          try {
            const userRef = doc(db, 'users', brokerDoc.id);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = userSnap.data();
              name = name || (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') || userData.name || '';
              email = email || userData.email || '';
            }
          } catch (e) {}
        }

        brokers.push({
          id: brokerDoc.id,
          name: name || 'Unknown Broker',
          email: email || 'No email',
          ...brokerData
        });
      }

      // Sort by name
      brokers.sort((a, b) => a.name.localeCompare(b.name));
      setAllBrokers(brokers);
    } catch (error) {
      console.error('Error fetching brokers:', error);
    }
  };

  // Fetch all borrowers (searchers) for the new listing modal
  const fetchAllBorrowers = async () => {
    try {
      const loansRef = collection(db, 'loanApplications');
      const loansQuery = query(
        loansRef,
        where('loanPurpose', '==', 'Acquisition'),
        orderBy('createdAt', 'desc')
      );
      const loansSnapshot = await getDocs(loansQuery);

      const borrowers = loansSnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        displayName: `${docSnap.data().firstName || ''} ${docSnap.data().lastName || ''}`.trim() || 'Unknown Borrower'
      }));

      setAllBorrowers(borrowers);
    } catch (error) {
      console.error('Error fetching borrowers:', error);
    }
  };

  // Open new listing modal
  const openNewListingModal = () => {
    setShowNewListingModal(true);
    setNewListingType(null);
    setSelectedBrokerId('');
    setSelectedBorrowerId('');
    // Fetch brokers and borrowers when modal opens
    fetchAllBrokers();
    fetchAllBorrowers();
  };

  // Create a new listing for a broker - navigate to form page
  const createBrokerListing = () => {
    if (!selectedBrokerId) {
      alert('Please select a broker');
      return;
    }
    setShowNewListingModal(false);
    // Navigate to the new listing form with broker info
    navigate(`/admin/new-listing?type=broker&id=${selectedBrokerId}`);
  };

  // Add a business listing for a borrower - navigate to form page
  const createBorrowerListing = () => {
    if (!selectedBorrowerId) {
      alert('Please select a borrower');
      return;
    }
    setShowNewListingModal(false);
    // Navigate to the new listing form with borrower info
    navigate(`/admin/new-listing?type=borrower&id=${selectedBorrowerId}`);
  };

  // Fetch email reply statuses for follow-up tracking
  const fetchEmailStatuses = async (emails) => {
    if (!emails || emails.length === 0) return;

    try {
      const emailRef = collection(db, 'email');
      const statuses = {};

      // Query for emails sent to these recipients
      for (const email of emails) {
        if (!email) continue;
        const q = query(
          emailRef,
          where('to', '==', email),
          orderBy('sentAt', 'desc')
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const latestEmail = snapshot.docs[0].data();
          statuses[email] = {
            lastSentAt: latestEmail.sentAt,
            replied: latestEmail.replied || false,
            repliedAt: latestEmail.repliedAt || null
          };
        }
      }

      setEmailStatuses(prev => ({ ...prev, ...statuses }));
    } catch (error) {
      console.error('Error fetching email statuses:', error);
    }
  };

  const fetchFullDeals = async () => {
    try {
      const loansRef = collection(db, 'loanApplications');
      // Pull all loanApplications; classify as "full deal" if it has the broker_deal source,
      // or has personal/all docs uploaded, or sits past prequalification in the funnel.
      const snap = await getDocs(query(loansRef, orderBy('createdAt', 'desc')));
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(loan => {
        if (loan.hideFromFullDeals === true) return false;
        const createdAt = loan.createdAt?.toDate ? loan.createdAt.toDate() : null;
        return createdAt && createdAt >= DATA_CUTOFF_DATE;
      });
      const postPrequalStatuses = new Set([
        'document clarification', 'closing', 'term sheet extended', 'term sheet received',
        'underwriting', 'in_underwriting', 'in underwriting', 'assigned to bank', 'funded',
      ]);
      const isFullDeal = (loan) => {
        if (loan.source === 'broker_deal') return true;
        if (loan.allPersonalFilesUploaded || loan.allDocumentsUploaded) return true;
        const s = (loan.loanStatus || '').toString().toLowerCase();
        return postPrequalStatuses.has(s);
      };
      // Attach broker info where we can (best-effort)
      const deals = await Promise.all(all.filter(isFullDeal).map(async (loan) => {
        let brokerName = '';
        let brokerEmail = loan.brokerEmail || '';
        if (loan.brokerId) {
          try {
            const bSnap = await getDoc(doc(db, 'brokers', loan.brokerId));
            if (bSnap.exists()) {
              const bd = bSnap.data();
              brokerName = (bd.firstName && bd.lastName ? `${bd.firstName} ${bd.lastName}` : '')
                || bd.brokerAgeName || bd.name || bd.businessName || bd.companyName || '';
              brokerEmail = brokerEmail || bd.email || '';
            }
          } catch {}
        }
        return { ...loan, brokerName, brokerEmail };
      }));
      setFullDeals(deals);
    } catch (err) {
      console.error('Error fetching full deals:', err);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const since = new Date();
      since.setHours(since.getHours() - 48);
      const sinceTs = Timestamp.fromDate(since);
      const sinceIso = since.toISOString();

      // Recent uploads (documentMetadata + prequalificationMetadata)
      const [docMetaSnap, prequalMetaSnap] = await Promise.all([
        getDocs(query(collection(db, 'documentMetadata'))).catch(() => ({ docs: [] })),
        getDocs(query(collection(db, 'prequalificationMetadata'))).catch(() => ({ docs: [] })),
      ]);
      const parseUpload = (d, source) => {
        const data = d.data();
        const rawTs = data.uploadedAt;
        let when = null;
        if (rawTs?.toDate) when = rawTs.toDate();
        else if (typeof rawTs === 'string') when = new Date(rawTs);
        else if (rawTs?.seconds) when = new Date(rawTs.seconds * 1000);
        return { id: d.id, source, when, ...data };
      };
      const allLoanDocs = docMetaSnap.docs.map(d => parseUpload(d, 'loan'));
      const allPrequalDocs = prequalMetaSnap.docs.map(d => parseUpload(d, 'prequal'));

      // Persist full doc maps for "ready for prequal" detection
      const loanMap = {};
      allLoanDocs.forEach(d => {
        const k = d.loanApplicationId || d.uploadLinkId;
        if (!k) return;
        if (!loanMap[k]) loanMap[k] = [];
        loanMap[k].push(d);
      });
      setDocsByLoanId(prev => ({ ...prev, ...loanMap }));

      const prequalMap = {};
      allPrequalDocs.forEach(d => {
        const k = d.applicationId;
        if (!k) return;
        if (!prequalMap[k]) prequalMap[k] = [];
        prequalMap[k].push(d);
      });
      setDocsByPrequalId(prequalMap);

      const recentUps = [...allLoanDocs, ...allPrequalDocs]
        .filter(u => u.when && u.when >= since)
        .sort((a, b) => b.when - a.when)
        .slice(0, 25);

      // Hydrate each upload with the parent loan/prequal so the card can show
      // (a) the account that owns the file and (b) whether it's a broker
      // listing, a broker-managed deal, or a regular borrower upload.
      const loanCache = new Map();
      const prequalCache = new Map();
      const getLoan = async (id) => {
        if (!id) return null;
        if (loanCache.has(id)) return loanCache.get(id);
        try {
          const snap = await getDoc(doc(db, 'loanApplications', id));
          const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
          loanCache.set(id, data);
          return data;
        } catch { loanCache.set(id, null); return null; }
      };
      const getPrequal = async (id) => {
        if (!id) return null;
        if (prequalCache.has(id)) return prequalCache.get(id);
        try {
          const snap = await getDoc(doc(db, 'prequalificationApplications', id));
          const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
          prequalCache.set(id, data);
          return data;
        } catch { prequalCache.set(id, null); return null; }
      };

      const hydrated = await Promise.all(recentUps.map(async (u) => {
        let kind = 'borrower';   // 'broker_listing' | 'broker_deal' | 'borrower'
        let accountName = '';
        let accountEmail = u.email || '';

        if (u.source === 'prequal') {
          kind = 'broker_listing';
          const prequal = await getPrequal(u.applicationId);
          if (prequal) {
            accountName = prequal.businessName || '';
            accountEmail = accountEmail || prequal.brokerEmail || '';
          }
        } else {
          const loan = await getLoan(u.loanApplicationId || u.uploadLinkId);
          if (loan) {
            const fullName = `${loan.firstName || ''} ${loan.lastName || ''}`.trim();
            accountName = fullName || loan.businessName || '';
            accountEmail = accountEmail || loan.email || '';
            if (loan.source === 'broker_deal' || loan.brokerId) {
              kind = 'broker_deal';
            }
          }
        }

        return { ...u, _kind: kind, _accountName: accountName, _accountEmail: accountEmail };
      }));

      setRecentUploads(hydrated);

      // Recent prequalifications created (last 48h)
      const prequalSnap = await getDocs(query(
        collection(db, 'prequalificationApplications'),
        where('createdAt', '>=', sinceTs),
        orderBy('createdAt', 'desc')
      )).catch(() => ({ docs: [] }));
      const recentPq = prequalSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(item => item.archived !== true)
        .slice(0, 25);
      setRecentPrequalCreations(recentPq);

      // Recent broker signups (last 14 days — brokers sign up less often
      // than borrowers so the window is wider).
      const brokerSince = new Date();
      brokerSince.setDate(brokerSince.getDate() - 14);
      const brokerSinceTs = Timestamp.fromDate(brokerSince);
      try {
        const brokerSnap = await getDocs(query(
          collection(db, 'brokers'),
          where('createdAt', '>=', brokerSinceTs),
          orderBy('createdAt', 'desc')
        ));
        setRecentBrokerSignups(brokerSnap.docs.map(d => ({ id: d.id, ...d.data() })).slice(0, 12));
      } catch (brokerErr) {
        // Fallback: some brokers may have non-Timestamp createdAt — pull all and
        // filter client-side.
        try {
          const brokerSnap = await getDocs(collection(db, 'brokers'));
          const cutoff = brokerSince.getTime();
          const filtered = brokerSnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(b => {
              const ts = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : null);
              return ts && ts.getTime() >= cutoff;
            })
            .sort((a, b) => {
              const at = a.createdAt?.toDate?.()?.getTime?.() || 0;
              const bt = b.createdAt?.toDate?.()?.getTime?.() || 0;
              return bt - at;
            })
            .slice(0, 12);
          setRecentBrokerSignups(filtered);
        } catch (fallbackErr) {
          console.error('Error fetching recent brokers:', fallbackErr);
        }
      }
    } catch (err) {
      console.error('Error fetching recent activity:', err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchBorrowerSignups(), fetchBusinessPrequals(), fetchFullDeals(), fetchFollowups(), fetchOffMarketSearchers(), fetchPendingSearchers(), fetchPendingBrokers(), fetchPendingMeetings(), fetchTodaysFollowups(), fetchRecentActivity()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  // Fetch email statuses when follow-up data changes
  useEffect(() => {
    const allEmails = [
      ...intakeListFollowups.map(i => i.email),
      ...newDealFollowups.map(i => i.email),
      ...businessFollowups.map(i => i.brokerEmail),
      ...brokerFollowups.map(i => i.email),
      ...offMarketSearchers.map(i => i.email),
      ...pendingSearchers.map(i => i.email),
      ...pendingMeetings.map(i => i.email)
    ].filter(Boolean);

    if (allEmails.length > 0) {
      fetchEmailStatuses([...new Set(allEmails)]); // Remove duplicates
    }
  }, [intakeListFollowups, newDealFollowups, businessFollowups, brokerFollowups, offMarketSearchers, pendingSearchers, pendingMeetings]);

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        navigate('/adminlogin');
      })
      .catch((error) => {
        console.error('Sign out error:', error);
      });
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filterBySearch = (items, fields) => {
    if (!searchTerm) return items;
    const term = searchTerm.toLowerCase();
    return items.filter(item =>
      fields.some(field => {
        const value = field.split('.').reduce((obj, key) => obj?.[key], item);
        return value?.toString().toLowerCase().includes(term);
      })
    );
  };

  // Helper function to open email modal
  const openEmailModal = (email, name, templateType, businessName = '', customSubject = '', customBody = '', autoLogRecord = null) => {
    setEmailModalData({
      recipientEmail: email,
      recipientName: name,
      templateType,
      businessName,
      customSubject,
      customBody,
      autoLogRecord
    });
    setShowEmailModal(true);
  };

  const openEmailHistory = (email, name) => {
    setEmailHistoryTarget({ email, name });
    setShowEmailHistoryModal(true);
  };

  // Handle marking off-market searcher as responded
  const handleMarkResponded = async (searcherId) => {
    try {
      const searcherRef = doc(db, 'offMarketSearchers', searcherId);
      await updateDoc(searcherRef, {
        responded: true,
        respondedAt: serverTimestamp(),
        lastRespondedAt: serverTimestamp(),
        status: 'responded'
      });
      fetchOffMarketSearchers();
    } catch (error) {
      console.error('Error marking as responded:', error);
      alert('Error updating status. Please try again.');
    }
  };

  // Handle sending follow-up email to off-market searcher
  const handleOffMarketFollowUp = async (searcher) => {
    const firstName = searcher.name?.split(' ')[0] || searcher.name;
    setCurrentEmailSearcher(searcher); // Store searcher for callback
    openEmailModal(searcher.email, firstName, 'offMarketFollowUp');
  };

  // Callback when off-market email is successfully sent
  const handleOffMarketEmailSent = async () => {
    if (currentEmailSearcher) {
      try {
        const searcherRef = doc(db, 'offMarketSearchers', currentEmailSearcher.id);
        await updateDoc(searcherRef, {
          lastEmailSentAt: serverTimestamp(),
          emailCount: (currentEmailSearcher.emailCount || 0) + 1,
          status: currentEmailSearcher.status === 'pending_email' ? 'awaiting_response' : currentEmailSearcher.status
        });
        fetchOffMarketSearchers();
      } catch (error) {
        console.error('Error updating searcher after email:', error);
      }
    }
    setCurrentEmailSearcher(null);
  };

  // Handle closing off-market searcher
  const handleCloseOffMarket = async (searcherId) => {
    if (!window.confirm('Are you sure you want to close this off-market searcher?')) return;
    try {
      const searcherRef = doc(db, 'offMarketSearchers', searcherId);
      await updateDoc(searcherRef, {
        status: 'closed'
      });
      fetchOffMarketSearchers();
    } catch (error) {
      console.error('Error closing off-market searcher:', error);
      alert('Error closing. Please try again.');
    }
  };

  // Handle broker logo upload
  const handleBrokerLogoClick = (brokerId) => {
    setUploadingBrokerId(brokerId);
    brokerLogoInputRef.current?.click();
  };

  const handleBrokerLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !uploadingBrokerId) return;

    try {
      const storage = getStorage();
      const storageRefPath = ref(storage, `brokerLogos/${uploadingBrokerId}`);
      await uploadBytes(storageRefPath, file);
      const url = await getDownloadURL(storageRefPath);

      // Update the broker document with the logo URL
      const brokerRef = doc(db, 'brokers', uploadingBrokerId);
      await updateDoc(brokerRef, { logoUrl: url });

      alert('Logo uploaded successfully!');
      fetchFollowups(); // Refresh to show the new logo
    } catch (error) {
      console.error('Error uploading broker logo:', error);
      alert('Error uploading logo. Please try again.');
    } finally {
      setUploadingBrokerId(null);
      // Reset file input
      if (brokerLogoInputRef.current) {
        brokerLogoInputRef.current.value = '';
      }
    }
  };

  // Handle sending reminder to pending searcher
  const handleSendPendingReminder = async (searcher) => {
    try {
      const firstName = searcher.name?.split(' ')[0] || searcher.name;
      const emailData = {
        to: searcher.email,
        from: 'Isak @ Lended <isak@lended.ai>',
        replyTo: 'isak@lended.ai',
        subject: 'Complete Your Lended Account Setup',
        message: {
          subject: 'Complete Your Lended Account Setup',
          html: `
            <p>Hi ${firstName},</p>
            <p>Just wanted to follow up and see if you had any questions about getting started with Lended.</p>
            <p>To begin the prequalification process, please create your account here:</p>
            <p><a href="https://lended.ai/searchersignup" style="background-color: #4F46E5; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block;">Create Your Account</a></p>
            <p>Once you've signed up, we can get you prequalified and ready to pursue deals.</p>
            <p>Let me know if you have any questions!</p>
            <p>Best regards,<br>Isak<br>Lended</p>
          `
        }
      };

      await addDoc(collection(db, 'email'), emailData);

      // Update the pending searcher record
      const searcherRef = doc(db, 'pendingSearcherSignups', searcher.id);
      await updateDoc(searcherRef, {
        lastReminderSentAt: serverTimestamp(),
        reminderCount: (searcher.reminderCount || 0) + 1
      });

      fetchPendingSearchers();
      alert('Reminder email sent!');
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Error sending reminder. Please try again.');
    }
  };

  // Same flow as the searcher reminder but for pending brokers.
  const handleSendPendingBrokerReminder = async (broker) => {
    try {
      const firstName = broker.firstName || (broker.name?.split(' ')[0]) || broker.name || 'there';
      await addDoc(collection(db, 'email'), {
        to: broker.email,
        from: 'Isak @ Lended <isak@lended.ai>',
        replyTo: 'isak@lended.ai',
        subject: 'Complete Your Lended Broker Portal Setup',
        message: {
          subject: 'Complete Your Lended Broker Portal Setup',
          html: `
            <p>Hey ${firstName},</p>
            <p>Just following up on getting you set up in the broker portal — let me know if anything's blocking you.</p>
            <p>Here's the signup link again:</p>
            <p><a href="https://lended.ai/broker-signup" style="background-color: #4F46E5; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block;">Sign Up for Broker Portal</a></p>
            <p>Once you're in, you can submit deals directly through the portal for prequalification and term sheet requests. If you've already got files organized in a data room, just send them over and we'll handle the upload on our end.</p>
            <p>Best,<br>Isak<br>(914) 414-1106<br>isak@lended.ai | lended.ai</p>
          `
        }
      });
      await updateDoc(doc(db, 'pendingBrokerSignups', broker.id), {
        lastReminderSentAt: serverTimestamp(),
        reminderCount: (broker.reminderCount || 0) + 1,
      });
      fetchPendingBrokers();
      alert('Reminder email sent!');
    } catch (error) {
      console.error('Error sending broker reminder:', error);
      alert('Error sending reminder. Please try again.');
    }
  };

  const handleRemovePendingBroker = async (brokerId, markAsSignedUp = false) => {
    const confirmMsg = markAsSignedUp
      ? 'Mark this broker as signed up? They will be removed from follow-ups.'
      : 'Remove this pending broker?';
    if (!window.confirm(confirmMsg)) return;
    try {
      await updateDoc(doc(db, 'pendingBrokerSignups', brokerId), {
        signedUp: true,
        signedUpAt: serverTimestamp(),
      });
      fetchPendingBrokers();
    } catch (error) {
      console.error('Error updating pending broker:', error);
      alert('Error updating. Please try again.');
    }
  };

  // Handle removing pending searcher (mark as signed up or remove)
  const handleRemovePendingSearcher = async (searcherId, markAsSignedUp = false) => {
    const confirmMsg = markAsSignedUp
      ? 'Mark this searcher as signed up? They will be removed from follow-ups.'
      : 'Remove this pending searcher?';
    if (!window.confirm(confirmMsg)) return;
    try {
      const searcherRef = doc(db, 'pendingSearcherSignups', searcherId);
      await updateDoc(searcherRef, {
        signedUp: true,
        signedUpAt: serverTimestamp()
      });
      fetchPendingSearchers();
    } catch (error) {
      console.error('Error updating pending searcher:', error);
      alert('Error updating. Please try again.');
    }
  };

  // Render log button and collapsed log history for any card
  const renderLogSection = (record, collectionName) => {
    const recordWithCollection = { ...record, collection: collectionName, followUpCollection: collectionName };
    const logs = record.followUpLog || [];
    const isExpanded = expandedLogs[record.id];
    const lastLog = logs.length > 0 ? logs[logs.length - 1] : null;

    const contactTypeBadgeColor = (type) => {
      switch (type) {
        case 'Call': return 'bg-blue-100 text-blue-700';
        case 'Email': return 'bg-purple-100 text-purple-700';
        case 'Text': return 'bg-green-100 text-green-700';
        default: return 'bg-gray-100 text-gray-700';
      }
    };

    return (
      <div className="mt-2 border-t border-gray-200 pt-2">
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openLogModal(recordWithCollection);
            }}
            className="flex items-center text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
          >
            <PencilSquareIcon className="h-3.5 w-3.5 mr-1" />
            Log
          </button>
          {logs.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleLogExpansion(record.id);
              }}
              className="flex items-center text-xs text-gray-500 hover:text-gray-700"
            >
              {isExpanded ? (
                <ChevronUpIcon className="h-3.5 w-3.5 mr-0.5" />
              ) : (
                <ChevronDownIcon className="h-3.5 w-3.5 mr-0.5" />
              )}
              Logs ({logs.length})
            </button>
          )}
          {!isExpanded && lastLog && (
            <span className="text-xs text-gray-500 truncate">
              Last contact: {lastLog.type} — {lastLog.timestamp ? new Date(lastLog.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
            </span>
          )}
        </div>
        {isExpanded && logs.length > 0 && (
          <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto">
            {[...logs].reverse().map((log, idx) => (
              <div key={idx} className="text-xs bg-gray-50 rounded p-2 border border-gray-100">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-gray-400">
                    {log.timestamp ? new Date(log.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${contactTypeBadgeColor(log.type)}`}>
                    {log.type}
                  </span>
                </div>
                <p className="text-gray-700">{log.notes}</p>
                {log.nextDateSet && (
                  <p className="text-gray-400 mt-0.5">Next: {log.nextDateSet}</p>
                )}
              </div>
            ))}
          </div>
        )}
        {isExpanded && logs.length === 0 && (
          <p className="text-xs text-gray-400 mt-1 italic">No follow-ups logged yet</p>
        )}
      </div>
    );
  };

  const renderTodaysBrief = () => {
    const today = new Date();
    const dateLabel = today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    const hour = today.getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

    const totalFollowups = intakeListFollowups.length + newDealFollowups.length + businessFollowups.length + brokerFollowups.length + pendingMeetings.length + initialPrequalFilesNeeded.length;
    const newUploadsCount = recentUploads.filter(u => u.seenByAdmin !== true).length;

    const heroStats = [
      { label: 'Follow-ups due', value: totalFollowups, icon: PhoneIcon, accent: 'from-rose-500 to-pink-600', tabKey: 'followups' },
      { label: 'New file uploads', value: newUploadsCount, icon: ArrowUpTrayIcon, accent: 'from-orange-500 to-amber-600' },
      { label: 'New prequalifications', value: recentPrequalCreations.length, icon: DocumentIcon, accent: 'from-indigo-500 to-violet-600', tabKey: 'prequalifications' },
      { label: 'Open full deals', value: fullDeals.length, icon: BuildingOfficeIcon, accent: 'from-emerald-500 to-teal-600', tabKey: 'fullDeals' },
    ];

    const formatRelativeTime = (date) => {
      if (!date) return '';
      const d = date.toDate ? date.toDate() : (typeof date === 'string' ? new Date(date) : date);
      const diffMs = Date.now() - d.getTime();
      const min = Math.floor(diffMs / 60000);
      if (min < 1) return 'just now';
      if (min < 60) return `${min}m ago`;
      const hr = Math.floor(min / 60);
      if (hr < 24) return `${hr}h ago`;
      const days = Math.floor(hr / 24);
      return `${days}d ago`;
    };

    const sortedRecentPrequals = sortReadyAndHotFirst(recentPrequalCreations, isBusinessReadyForPrequal).slice(0, 8);

    // Unified recent-signups feed: borrowers + brokers + listing prequals,
    // tagged with type and sorted by createdAt desc.
    const recentSignupsFeed = (() => {
      const items = [];
      (borrowerSignups || []).forEach((b) => {
        items.push({
          _kind: 'Borrower',
          _color: 'bg-blue-100 text-blue-700',
          _id: `borrower-${b.id}`,
          _onClick: () => navigate(`/loan-details/${b.id}`),
          title: `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.email || 'Unknown',
          subtitle: b.email || '',
          meta: b.businessState || '',
          createdAt: b.createdAt,
          _record: b,
          _readyCheck: () => isBorrowerReadyForPrequal(b),
        });
      });
      (recentBrokerSignups || []).forEach((b) => {
        const fullName = (b.firstName && b.lastName ? `${b.firstName} ${b.lastName}` : '')
          || b.brokerAgeName || b.name || b.businessName || b.companyName || 'Unknown Broker';
        items.push({
          _kind: 'Broker',
          _color: 'bg-purple-100 text-purple-700',
          _id: `broker-${b.id}`,
          _onClick: () => setActiveSection('brokers'),
          title: fullName,
          subtitle: b.brokerageName || b.brokerAgeName || b.companyName || '',
          meta: b.email || '',
          createdAt: b.createdAt,
          _record: b,
          _readyCheck: () => false,
        });
      });
      (recentPrequalCreations || []).forEach((p) => {
        items.push({
          _kind: 'Listing',
          _color: 'bg-green-100 text-green-700',
          _id: `listing-${p.id}`,
          _onClick: () => navigate(`/prequalification-details/${p.id}`),
          title: p.businessName || 'Untitled Listing',
          subtitle: p.brokerName ? `Broker: ${p.brokerName}` : '',
          meta: p.businessState || p.industry || '',
          createdAt: p.createdAt,
          _record: p,
          _readyCheck: () => isBusinessReadyForPrequal(p),
        });
      });
      // Full deals — broker_deal loan submissions. New ones surface here so
      // Ben/Isak see them right alongside borrower signups and listings.
      (fullDeals || []).forEach((d) => {
        const buyerName = `${d.firstName || ''} ${d.lastName || ''}`.trim();
        const title = d.businessName || buyerName || d.email || 'Full Deal';
        const subtitleParts = [];
        if (buyerName && d.businessName) subtitleParts.push(`Buyer: ${buyerName}`);
        if (d.brokerName) subtitleParts.push(`Broker: ${d.brokerName}`);
        items.push({
          _kind: 'Full Deal',
          _color: 'bg-emerald-100 text-emerald-700',
          _id: `fulldeal-${d.id}`,
          _onClick: () => navigate(`/loan-details/${d.id}`),
          title,
          subtitle: subtitleParts.join(' · '),
          meta: d.businessState || (d.purchasePrice ? `$${Number(d.purchasePrice).toLocaleString()}` : ''),
          createdAt: d.createdAt,
          _record: d,
          _readyCheck: () => false,
        });
      });
      return items
        .sort((a, b) => {
          const aReady = a._readyCheck?.() ? 1 : 0;
          const bReady = b._readyCheck?.() ? 1 : 0;
          if (aReady !== bReady) return bReady - aReady;
          const ad = a.createdAt?.toDate?.() || (a.createdAt ? new Date(a.createdAt) : new Date(0));
          const bd = b.createdAt?.toDate?.() || (b.createdAt ? new Date(b.createdAt) : new Date(0));
          return bd - ad;
        })
        .slice(0, 12);
    })();

    return (
      <div className="space-y-6">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 text-white p-6 shadow-lg">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <SparklesIcon className="absolute right-6 top-6 h-32 w-32" />
          </div>
          <div className="relative">
            <p className="text-xs uppercase tracking-widest opacity-80">{dateLabel}</p>
            <h2 className="mt-1 text-2xl font-bold">{greeting}, Ben.</h2>
            <p className="mt-1 text-sm opacity-90">
              {totalFollowups > 0
                ? `You have ${totalFollowups} follow-up${totalFollowups === 1 ? '' : 's'} due and ${newUploadsCount} new file${newUploadsCount === 1 ? '' : 's'} waiting on review.`
                : 'Inbox is clear. Use the brief below to spot what is moving today.'}
            </p>
          </div>
        </div>

        {/* Hero stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {heroStats.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.label}
                onClick={() => s.tabKey && setActiveSection(s.tabKey)}
                className={`relative overflow-hidden rounded-xl p-4 text-left text-white bg-gradient-to-br ${s.accent} shadow hover:shadow-md transition-shadow ${s.tabKey ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider opacity-80">{s.label}</p>
                    <p className="mt-1 text-3xl font-bold leading-none">{s.value}</p>
                  </div>
                  <Icon className="h-8 w-8 opacity-70" />
                </div>
                {s.tabKey && (
                  <div className="mt-3 text-xs opacity-80 flex items-center gap-1">
                    Open tab <ArrowRightIcon className="h-3 w-3" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Recent signups — unified feed of borrowers, brokers, and listing prequals */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-indigo-500" />
                <h3 className="text-base font-semibold text-gray-900">Recent signups</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">{recentSignupsFeed.length}</span>
              </div>
              <span className="text-[10px] text-gray-400 uppercase tracking-wider">Borrowers · Brokers · Listings</span>
            </div>
            {recentSignupsFeed.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No new signups yet.</p>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[28rem] overflow-y-auto">
                {recentSignupsFeed.map((item) => {
                  const ready = item._readyCheck && item._readyCheck();
                  const hot = !ready && isHot(item._record);
                  return (
                    <div
                      key={item._id}
                      onClick={item._onClick}
                      className={`py-2.5 flex items-center justify-between gap-3 hover:bg-gray-50 -mx-2 px-2 rounded cursor-pointer ${ready ? readyCardClasses : hot ? hotCardClasses : ''}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${item._color}`}>{item._kind}</span>
                          <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                          {ready && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-bold flex items-center gap-0.5">
                              <SparklesIcon className="h-2.5 w-2.5" /> READY
                            </span>
                          )}
                          {hot && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-teal-500 text-white font-bold flex items-center gap-0.5">
                              <FireIcon className="h-2.5 w-2.5" /> NEW UPLOAD
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{item.subtitle || item.meta || '—'}</p>
                      </div>
                      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                        <span className="text-[11px] text-gray-400">{formatRelativeTime(item.createdAt)}</span>
                        {item.meta && item.subtitle && (
                          <span className="text-[10px] text-gray-400 truncate max-w-[120px]">{item.meta}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent uploads */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ArrowUpTrayIcon className="h-5 w-5 text-orange-500" />
                <h3 className="text-base font-semibold text-gray-900">Recent uploads</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">{recentUploads.length}</span>
              </div>
            </div>
            {recentUploads.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No files uploaded in the last 48h.</p>
            ) : (
              <div className="space-y-2 max-h-[26rem] overflow-y-auto pr-1">
                {recentUploads.slice(0, 12).map((u) => {
                  const kindTag = u._kind === 'broker_listing'
                    ? { label: 'Broker Listing', cls: 'bg-green-100 text-green-700' }
                    : u._kind === 'broker_deal'
                      ? { label: 'Broker Deal', cls: 'bg-indigo-100 text-indigo-700' }
                      : { label: 'Borrower', cls: 'bg-blue-100 text-blue-700' };
                  const account = u._accountName || u._accountEmail || u.email || 'Unknown account';
                  return (
                    <div key={u.id} className={`rounded-lg border p-2.5 ${u.seenByAdmin !== true ? 'border-orange-300 bg-orange-50' : 'border-gray-100 bg-gray-50'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${kindTag.cls}`}>{kindTag.label}</span>
                            <p className="text-xs font-semibold text-gray-900 truncate">{account}</p>
                          </div>
                          <p className="text-xs text-gray-700 truncate">{u.label || u.documentName || u.fileName || 'Untitled file'}</p>
                          <p className="text-[10px] text-gray-500 truncate">
                            {u.fileName ? u.fileName : ''}
                            {u._accountEmail && u._accountEmail !== account ? ` · ${u._accountEmail}` : ''}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{formatRelativeTime(u.when)}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {u.seenByAdmin !== true && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-200 text-orange-800 font-bold">NEW</span>
                          )}
                          {u.fileUrl && (
                            <a href={u.fileUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-600 hover:text-indigo-800 font-medium">View</a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Newly created prequalifications */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <DocumentIcon className="h-5 w-5 text-indigo-500" />
                <h3 className="text-base font-semibold text-gray-900">New prequalifications</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium">{recentPrequalCreations.length}</span>
              </div>
              <button onClick={() => setActiveSection('prequalifications')} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                See all →
              </button>
            </div>
            {sortedRecentPrequals.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No new prequalifications in the last 48h.</p>
            ) : (
              <div className="space-y-2">
                {sortedRecentPrequals.map(p => {
                  const ready = isBusinessReadyForPrequal(p);
                  const hot = !ready && isHot(p);
                  return (
                    <div
                      key={p.id}
                      onClick={() => navigate(`/prequalification-details/${p.id}`)}
                      className={`rounded-lg border border-gray-100 hover:border-indigo-300 hover:bg-indigo-50 cursor-pointer p-2.5 transition-colors ${ready ? readyCardClasses : hot ? hotCardClasses : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900 truncate">{p.businessName || 'Untitled Prequalification'}</p>
                        {ready ? (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-bold flex items-center gap-0.5 flex-shrink-0">
                            <SparklesIcon className="h-2.5 w-2.5" /> NEEDS PREQUAL
                          </span>
                        ) : hot ? (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-teal-500 text-white font-bold flex items-center gap-0.5 flex-shrink-0">
                            <FireIcon className="h-2.5 w-2.5" /> NEW
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-gray-500 mt-0.5">
                        <span className="truncate">{p.businessState || p.industry || '—'}</span>
                        <span>{formatRelativeTime(p.createdAt)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pending signups — people who got the cold call but haven't created an account yet */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <PhoneIcon className="h-5 w-5 text-orange-500" />
                <h3 className="text-base font-semibold text-gray-900">Pending signups — needs a nudge</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">{pendingSearchers.length + pendingBrokers.length}</span>
              </div>
              <button onClick={() => setActiveSection('followups')} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                Open Follow-ups →
              </button>
            </div>
            {(pendingSearchers.length + pendingBrokers.length) === 0 ? (
              <p className="text-sm text-gray-400 italic">No one's stuck at the signup step. Nice.</p>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[26rem] overflow-y-auto">
                {[
                  ...pendingSearchers.map(s => ({ ...s, _kind: 'Searcher', _color: 'bg-orange-100 text-orange-700' })),
                  ...pendingBrokers.map(b => ({ ...b, _kind: 'Broker', _color: 'bg-purple-100 text-purple-700' })),
                ]
                  .sort((a, b) => {
                    const ad = a.createdAt?.toDate?.() || (a.createdAt ? new Date(a.createdAt) : new Date(0));
                    const bd = b.createdAt?.toDate?.() || (b.createdAt ? new Date(b.createdAt) : new Date(0));
                    return bd - ad;
                  })
                  .slice(0, 12)
                  .map((p) => {
                    const displayName = (p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : '')
                      || p.name
                      || p.brokerageName
                      || p.email
                      || 'Unknown';
                    return (
                      <div
                        key={`pending-${p._kind}-${p.id}`}
                        onClick={() => setActiveSection('followups')}
                        className="py-2.5 flex items-center justify-between gap-3 hover:bg-gray-50 -mx-2 px-2 rounded cursor-pointer"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${p._color}`}>{p._kind}</span>
                            <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                            {(p.reminderCount || 0) > 0 && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
                                {p.reminderCount} reminder{p.reminderCount === 1 ? '' : 's'}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate mt-0.5">{p.email}</p>
                        </div>
                        <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                          <span className="text-[11px] text-gray-400">{formatRelativeTime(p.createdAt)}</span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (p._kind === 'Broker') handleSendPendingBrokerReminder(p);
                              else handleSendPendingReminder(p);
                            }}
                            className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 font-medium"
                          >
                            Nudge
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

        </div>
      </div>
    );
  };

  const renderPrequalifiedFollowUpCard = (item, kind) => {
    const accent = kind === 'intake'
      ? { border: 'border-amber-200', bg: 'bg-amber-50', label: 'Awaiting intake docs', labelClr: 'text-amber-700', tagClr: 'bg-amber-100 text-amber-700', icon: ExclamationCircleIcon, iconClr: 'text-amber-500' }
      : { border: 'border-blue-200', bg: 'bg-blue-50', label: 'Push for new deal', labelClr: 'text-blue-700', tagClr: 'bg-blue-100 text-blue-700', icon: ClockIcon, iconClr: 'text-blue-500' };
    const Icon = accent.icon;
    return (
      <div key={`${kind}-${item.id}`} className={`border ${accent.border} ${accent.bg} rounded-lg p-3`}>
        <div className="flex items-start justify-between cursor-pointer" onClick={() => navigate(`/loan-details/${item.id}`)}>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 text-sm truncate">{item.firstName} {item.lastName}</p>
            <p className="text-xs text-gray-500 truncate">{item.email}</p>
          </div>
          <Icon className={`h-4 w-4 ${accent.iconClr} flex-shrink-0`} />
        </div>
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${accent.tagClr}`}>{accent.label}</span>
          {(item.prequalAmount || item.borrowerPrequalAmount) && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
              ${(item.prequalAmount || item.borrowerPrequalAmount).toLocaleString()}
            </span>
          )}
        </div>
        <p className={`mt-2 text-xs ${accent.labelClr}`}>{item.reason}</p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openEmailModal(item.email, item.firstName, kind === 'intake' ? 'intake' : 'newDeal');
            }}
            className={`flex-1 flex items-center justify-center text-xs px-2 py-1.5 rounded ${accent.tagClr} hover:opacity-80`}
          >
            <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
            {emailStatuses[item.email] ? 'Send Another' : 'Send Email'}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openEmailHistory(item.email, item.firstName);
            }}
            className="text-xs px-2 py-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
          >
            History
          </button>
        </div>
      </div>
    );
  };

  const renderPipelineCard = (item) => {
    const isBorrower = item._type === 'borrower';
    const ready = isBorrower ? isBorrowerReadyForPrequal(item) : isBusinessReadyForPrequal(item);
    const hot = !ready && isHot(item);
    const name = isBorrower
      ? `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email || 'Unnamed'
      : item.businessName || 'Unnamed Business';
    const subtitle = isBorrower
      ? item.email
      : item._type === 'listing' && item.source === 'borrower'
        ? `Borrower: ${item.borrowerName || item.borrowerEmail || '—'}`
        : `Broker: ${item.brokerName || '—'}`;
    const target = isBorrower ? `/loan-details/${item.id}`
      : item.source === 'borrower' && item.loanApplicationId ? `/loan-details/${item.loanApplicationId}`
      : `/prequalification-details/${item.id}`;
    const docCount = isBorrower
      ? (docsByLoanId[item.id]?.length || 0)
      : (item.source === 'borrower' && item.loanApplicationId ? docsByLoanId[item.loanApplicationId]?.length || 0 : docsByPrequalId[item.id]?.length || 0);
    const typeBadge = isBorrower
      ? { label: 'Borrower', color: 'bg-indigo-100 text-indigo-700' }
      : { label: 'Listing', color: 'bg-green-100 text-green-700' };
    const cardClasses = ready ? readyCardClasses : hot ? hotCardClasses : '';
    const selectKey = prequalKey(item);
    const isSelected = selectedPrequalKeys.has(selectKey);
    const underLoi = isUnderLoi(item);
    const handleCardClick = () => {
      if (prequalSelectMode) {
        togglePrequalSelection(item);
      } else {
        navigate(target);
      }
    };
    return (
      <div
        key={`${item._type}-${item.id}`}
        onClick={handleCardClick}
        className={`group relative bg-white rounded-xl border transition-all cursor-pointer p-3 ${
          isSelected
            ? 'border-indigo-500 ring-2 ring-indigo-300'
            : `border-gray-200 hover:border-indigo-300 hover:shadow-md ${cardClasses}`
        }`}
      >
        {prequalSelectMode ? (
          <div
            className={`absolute top-1.5 right-1.5 h-5 w-5 rounded border-2 flex items-center justify-center z-10 ${
              isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 bg-white'
            }`}
            aria-hidden="true"
          >
            {isSelected && <CheckCircleIcon className="h-3.5 w-3.5" />}
          </div>
        ) : (
          <button
            onClick={(e) => handleDismissFromPrequalTab(item, item._type, e)}
            title="Hide from this tab (doesn't delete the loan application)"
            className="absolute top-1.5 right-1.5 p-1 rounded-full text-gray-300 hover:bg-gray-100 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}
        <div className="flex items-start justify-between gap-2 mb-2 pr-5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${typeBadge.color}`}>{typeBadge.label}</span>
              {ready && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-bold flex items-center gap-0.5">
                  <SparklesIcon className="h-2.5 w-2.5" /> READY
                </span>
              )}
              {hot && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-500 text-white font-bold flex items-center gap-0.5">
                  <FireIcon className="h-2.5 w-2.5" /> NEW
                </span>
              )}
              {underLoi && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-600 text-white font-bold flex items-center gap-0.5">
                  <DocumentIcon className="h-2.5 w-2.5" /> UNDER LOI
                </span>
              )}
            </div>
            <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
            {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
          </div>
        </div>
        <div className="flex items-center justify-between text-[11px] text-gray-500">
          <span>{docCount > 0 ? `${docCount} file${docCount === 1 ? '' : 's'}` : 'No files yet'}</span>
          <span>{formatDate(item.createdAt)}</span>
        </div>
        {!prequalSelectMode && (
          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-end gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
            {!isBorrower && item.source !== 'borrower' && (
              <button
                onClick={(e) => copyBorrowerListingLink(item, e)}
                title="Copy the borrower signup link for this specific listing"
                className="text-[10px] px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 font-semibold hover:bg-indigo-200"
              >
                {copiedBorrowerLinkId === item.id ? 'Copied!' : 'Copy borrower link'}
              </button>
            )}
            <button
              onClick={(e) => handleMarkAsFullDeal(item, item._type, e)}
              title="Promote to Full Deals — Isak takes it from there. Useful when a client mentions a deal but hasn't uploaded yet."
              className="text-[10px] px-2 py-1 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-700"
            >
              → Full Deal
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderPrequalifiedRecentCard = (item) => {
    const isBorrower = item._type === 'borrower';
    const name = isBorrower
      ? `${item.firstName || ''} ${item.lastName || ''}`.trim() || item.email
      : item.businessName || 'Unnamed Business';
    const target = isBorrower ? `/loan-details/${item.id}` : `/prequalification-details/${item.id}`;
    return (
      <div
        key={`recent-${item._type}-${item.id}`}
        onClick={() => navigate(target)}
        className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 cursor-pointer hover:bg-emerald-100 transition-colors"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
            <p className="text-xs text-gray-500 truncate">{isBorrower ? item.email : (item.brokerName || '—')}</p>
          </div>
          <CheckCircleIcon className="h-5 w-5 text-emerald-500 flex-shrink-0" />
        </div>
        <p className="text-[11px] text-emerald-700 font-medium mt-1">Prequalified {formatDate(item.prequalifiedAt || item.lastUpdated)}</p>
      </div>
    );
  };

  const classifyBorrowerStage = (b) => {
    const docs = docsByLoanId[b.id] || [];
    if (isBorrowerReadyForPrequal(b)) return 'ready_for_prequal';
    if (docs.length > 0) return 'uploading';
    return 'signed_up';
  };
  const classifyListingStage = (l) => {
    if (l.status === 'prequalified' || l.prequalified || l.prequalifiedAmount) return 'awaiting_borrowers';
    if (isBusinessReadyForPrequal(l)) return 'ready_for_prequal';
    const docs = l.source === 'borrower' && l.loanApplicationId
      ? (docsByLoanId[l.loanApplicationId] || [])
      : (docsByPrequalId[l.id] || []);
    if (docs.length > 0) return 'awaiting_files';
    return 'just_created';
  };

  const StageSection = ({ stage, items, kind }) => {
    const empty = items.length === 0;
    const stageKey = `${kind}-${stage.key}`;
    const collapsed = collapsedStages.has(stageKey);
    return (
      <section className={`rounded-xl border ${stage.border} ${stage.bg} overflow-hidden`}>
        <button
          type="button"
          onClick={() => toggleStageCollapsed(stageKey)}
          className={`w-full px-4 py-2.5 flex items-center justify-between gap-2 ${collapsed ? '' : 'border-b ' + stage.border} hover:bg-black/[0.02] transition-colors`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {collapsed ? (
              <ChevronRightIcon className={`h-4 w-4 flex-shrink-0 ${stage.iconColor}`} />
            ) : (
              <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 ${stage.iconColor}`} />
            )}
            <stage.icon className={`h-4 w-4 flex-shrink-0 ${stage.iconColor}`} />
            <h4 className={`text-sm font-semibold ${stage.titleColor} truncate`}>{stage.label}</h4>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${stage.badgeBg} ${stage.badgeText} flex-shrink-0`}>{items.length}</span>
          </div>
          {!collapsed && (
            <span className={`text-[10px] italic ${stage.actionColor} truncate ml-2 hidden md:inline`}>{stage.action}</span>
          )}
        </button>
        {!collapsed && (
          <div className="p-2 space-y-2">
            {empty ? (
              <p className="text-xs text-gray-400 italic text-center py-3">{stage.emptyLabel || 'Nothing here.'}</p>
            ) : (
              items.map(item => (
                <div key={`${kind}-${stage.key}-${item.id}`} className="bg-white rounded-lg">
                  {renderPipelineCard({ ...item, _type: kind })}
                </div>
              ))
            )}
          </div>
        )}
      </section>
    );
  };

  const renderCombinedPrequalifications = () => {
    // Search helper
    const q = searchTerm.trim().toLowerCase();
    const matches = (i) => {
      if (!q) return true;
      const haystack = [
        i.firstName, i.lastName, i.email, i.businessName,
        i.brokerName, i.brokerEmail, i.borrowerName, i.borrowerEmail,
      ].filter(Boolean).map(s => s.toString().toLowerCase()).join(' ');
      return haystack.includes(q);
    };

    // BORROWER pipeline
    const borrowerByStage = { signed_up: [], uploading: [], ready_for_prequal: [], awaiting_deal: [] };
    borrowerSignups.filter(matches).forEach(b => {
      const s = classifyBorrowerStage(b);
      borrowerByStage[s].push(b);
    });
    // Prequalified borrowers awaiting deal: merge follow-ups + recently prequalified, dedupe by id
    const awaitingDealMap = new Map();
    [...intakeListFollowups, ...newDealFollowups, ...recentlyPrequalifiedBorrowers].forEach(b => {
      if (!b?.id) return;
      if (!awaitingDealMap.has(b.id)) awaitingDealMap.set(b.id, b);
    });
    borrowerByStage.awaiting_deal = Array.from(awaitingDealMap.values()).filter(matches);

    // LISTING pipeline
    const listingByStage = { just_created: [], awaiting_files: [], ready_for_prequal: [], awaiting_borrowers: [] };
    businessPrequals.filter(matches).forEach(p => {
      const s = classifyListingStage(p);
      listingByStage[s].push(p);
    });
    // Listing awaiting borrowers: include recently prequalified
    listingByStage.awaiting_borrowers = [
      ...listingByStage.awaiting_borrowers,
      ...recentlyPrequalifiedBusinesses.filter(matches),
    ];

    // Sort within each stage: ready/hot first
    const sortStage = (list, readyFn) => sortReadyAndHotFirst(list, readyFn);
    Object.keys(borrowerByStage).forEach(k => {
      borrowerByStage[k] = sortStage(borrowerByStage[k], isBorrowerReadyForPrequal);
    });
    Object.keys(listingByStage).forEach(k => {
      listingByStage[k] = sortStage(listingByStage[k], isBusinessReadyForPrequal);
    });

    const borrowerStages = [
      { key: 'signed_up', label: 'Just signed up — needs initial files', emptyLabel: 'No new signups awaiting their first upload.',
        icon: UserGroupIcon, iconColor: 'text-amber-600', titleColor: 'text-amber-900', actionColor: 'text-amber-700',
        bg: 'bg-amber-50/40', border: 'border-amber-200', badgeBg: 'bg-amber-200', badgeText: 'text-amber-800',
        action: 'Follow up to upload PFS + 3 yrs tax returns' },
      { key: 'uploading', label: 'Uploading — missing files', emptyLabel: 'No one mid-upload right now.',
        icon: ArrowUpTrayIcon, iconColor: 'text-blue-600', titleColor: 'text-blue-900', actionColor: 'text-blue-700',
        bg: 'bg-blue-50/40', border: 'border-blue-200', badgeBg: 'bg-blue-200', badgeText: 'text-blue-800',
        action: 'Nudge for the remaining docs' },
      { key: 'ready_for_prequal', label: 'Ready for prequal', emptyLabel: 'No borrowers ready to prequal.',
        icon: SparklesIcon, iconColor: 'text-emerald-600', titleColor: 'text-emerald-900', actionColor: 'text-emerald-700',
        bg: 'bg-emerald-50/40', border: 'border-emerald-300', badgeBg: 'bg-emerald-500', badgeText: 'text-white',
        action: 'Prequal them now' },
      { key: 'awaiting_deal', label: 'Prequalified — awaiting a deal', emptyLabel: 'No prequalified borrowers idle.',
        icon: BriefcaseIcon, iconColor: 'text-indigo-600', titleColor: 'text-indigo-900', actionColor: 'text-indigo-700',
        bg: 'bg-indigo-50/40', border: 'border-indigo-200', badgeBg: 'bg-indigo-200', badgeText: 'text-indigo-800',
        action: 'Ask about new deals / intake docs' },
    ];

    const listingStages = [
      { key: 'just_created', label: 'Just created — needs files', emptyLabel: 'No empty listings right now.',
        icon: PlusIcon, iconColor: 'text-amber-600', titleColor: 'text-amber-900', actionColor: 'text-amber-700',
        bg: 'bg-amber-50/40', border: 'border-amber-200', badgeBg: 'bg-amber-200', badgeText: 'text-amber-800',
        action: 'Follow up with broker for initial files' },
      { key: 'awaiting_files', label: 'Awaiting remaining files', emptyLabel: 'No listings mid-upload.',
        icon: ArrowUpTrayIcon, iconColor: 'text-blue-600', titleColor: 'text-blue-900', actionColor: 'text-blue-700',
        bg: 'bg-blue-50/40', border: 'border-blue-200', badgeBg: 'bg-blue-200', badgeText: 'text-blue-800',
        action: 'Nudge broker for remaining docs' },
      { key: 'ready_for_prequal', label: 'Ready for prequal', emptyLabel: 'No listings ready to prequal.',
        icon: SparklesIcon, iconColor: 'text-emerald-600', titleColor: 'text-emerald-900', actionColor: 'text-emerald-700',
        bg: 'bg-emerald-50/40', border: 'border-emerald-300', badgeBg: 'bg-emerald-500', badgeText: 'text-white',
        action: 'Prequal it now' },
      { key: 'awaiting_borrowers', label: 'Prequalified — awaiting borrowers', emptyLabel: 'No listings idle without buyers.',
        icon: UserGroupIcon, iconColor: 'text-purple-600', titleColor: 'text-purple-900', actionColor: 'text-purple-700',
        bg: 'bg-purple-50/40', border: 'border-purple-200', badgeBg: 'bg-purple-200', badgeText: 'text-purple-800',
        action: 'Ask broker if any new borrowers came in' },
    ];

    const borrowerTotal = Object.values(borrowerByStage).reduce((sum, l) => sum + l.length, 0);
    const listingTotal = Object.values(listingByStage).reduce((sum, l) => sum + l.length, 0);
    const totalReady = borrowerByStage.ready_for_prequal.length + listingByStage.ready_for_prequal.length;
    const totalFollowups = borrowerByStage.signed_up.length + borrowerByStage.uploading.length + listingByStage.just_created.length + listingByStage.awaiting_files.length;

    // All items currently visible across both columns — passed to the bulk
    // remover so it can look up the underlying records by selection key.
    const allVisibleItems = [
      ...Object.values(borrowerByStage).flat().map((b) => ({ ...b, _type: 'borrower' })),
      ...Object.values(listingByStage).flat().map((p) => ({ ...p, _type: 'listing' })),
    ];

    return (
      <div className="space-y-4">
        {/* Select-mode toolbar */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-end gap-2 flex-wrap">
          {prequalSelectMode ? (
            <>
              <span className="text-xs font-medium text-gray-700">
                {selectedPrequalKeys.size} selected
              </span>
              <button
                type="button"
                onClick={() => handleBulkRemovePrequals(allVisibleItems)}
                disabled={selectedPrequalKeys.size === 0 || prequalBulkRemoving}
                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {prequalBulkRemoving ? 'Hiding…' : `Hide ${selectedPrequalKeys.size || ''}`.trim()}
              </button>
              <button
                type="button"
                onClick={exitPrequalSelectMode}
                disabled={prequalBulkRemoving}
                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setPrequalSelectMode(true)}
              className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Select &amp; remove
            </button>
          )}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-indigo-500 to-violet-600 text-white rounded-xl p-3 shadow">
            <div className="flex items-center justify-between">
              <UserGroupIcon className="h-5 w-5 opacity-80" />
              <span className="text-[10px] uppercase tracking-wider opacity-80">Borrowers</span>
            </div>
            <p className="mt-1 text-2xl font-bold leading-none">{borrowerTotal}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-xl p-3 shadow">
            <div className="flex items-center justify-between">
              <BuildingOfficeIcon className="h-5 w-5 opacity-80" />
              <span className="text-[10px] uppercase tracking-wider opacity-80">Listings</span>
            </div>
            <p className="mt-1 text-2xl font-bold leading-none">{listingTotal}</p>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-xl p-3 shadow">
            <div className="flex items-center justify-between">
              <SparklesIcon className="h-5 w-5 opacity-80" />
              <span className="text-[10px] uppercase tracking-wider opacity-80">Ready to prequal</span>
            </div>
            <p className="mt-1 text-2xl font-bold leading-none">{totalReady}</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-xl p-3 shadow">
            <div className="flex items-center justify-between">
              <PhoneIcon className="h-5 w-5 opacity-80" />
              <span className="text-[10px] uppercase tracking-wider opacity-80">Need a nudge</span>
            </div>
            <p className="mt-1 text-2xl font-bold leading-none">{totalFollowups}</p>
          </div>
        </div>

        {/* Two columns: Borrower pipeline | Listing pipeline */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Borrower column */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded-lg bg-indigo-100">
                <UserGroupIcon className="h-4 w-4 text-indigo-700" />
              </div>
              <h3 className="text-base font-bold text-gray-900">Borrowers</h3>
              <span className="text-xs text-gray-500">— in the pipeline</span>
            </div>
            {borrowerStages.map(stage => (
              <StageSection key={stage.key} stage={stage} items={borrowerByStage[stage.key]} kind="borrower" />
            ))}
          </div>

          {/* Listing column */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded-lg bg-green-100">
                <BuildingOfficeIcon className="h-4 w-4 text-green-700" />
              </div>
              <h3 className="text-base font-bold text-gray-900">Listings</h3>
              <span className="text-xs text-gray-500">— in the pipeline</span>
            </div>
            {listingStages.map(stage => (
              <StageSection key={stage.key} stage={stage} items={listingByStage[stage.key]} kind="listing" />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const classifyFullDealStage = (deal) => {
    const s = (deal.loanStatus || '').toString().toLowerCase();

    // Already in / past underwriting.
    if (['underwriting', 'in_underwriting', 'in underwriting', 'funded'].includes(s)) return 'signed_underwriting';
    if (s === 'term sheet signed' || deal.termSheetSigned === true || /signed/i.test(deal.termSheetStatus || '')) return 'signed_underwriting';

    // Lender has given us a term sheet — now waiting for the borrower to sign.
    if (s === 'term sheet given' || s === 'term sheet extended' || s === 'term sheet received') return 'awaiting_signing';
    if (deal.termSheets && deal.termSheets.length > 0) return 'awaiting_signing';

    // "Awaiting Term Sheet" = sent to lenders, waiting on their offer.
    // (Match this BEFORE any generic /term sheet/ test so it doesn't get pulled
    // into the signing bucket.)
    if (s === 'awaiting term sheet' || deal.offerExtended === true) return 'awaiting_offer';
    if (deal.workbookComplete === true) return 'awaiting_offer';

    if (!deal.allDocumentsUploaded || !deal.allPersonalFilesUploaded) return 'awaiting_files';
    return 'awaiting_workbook';
  };

  const renderFullDealCard = (deal) => {
    const name = `${deal.firstName || ''} ${deal.lastName || ''}`.trim() || deal.businessName || 'Untitled Deal';
    const status = deal.loanStatus || (deal.allDocumentsUploaded ? 'All Docs Received' : deal.allPersonalFilesUploaded ? 'Personal Files Received' : 'In Progress');
    const statusColor = /underwriting/i.test(status) ? 'bg-purple-100 text-purple-800'
      : /funded/i.test(status) ? 'bg-emerald-100 text-emerald-800'
      : /term sheet/i.test(status) ? 'bg-blue-100 text-blue-800'
      : /rejected/i.test(status) ? 'bg-red-100 text-red-800'
      : 'bg-gray-100 text-gray-800';
    const hot = isHot(deal);
    const missing = [];
    if (!deal.allPersonalFilesUploaded) missing.push('borrower files');
    if (!deal.allDocumentsUploaded) missing.push('deal files');
    const isSelected = selectedFullDealIds.has(deal.id);
    const underLoi = isUnderLoi(deal);
    const handleCardClick = () => {
      if (fullDealsSelectMode) {
        toggleFullDealSelection(deal.id);
      } else {
        navigate(`/loan-details/${deal.id}`);
      }
    };
    return (
      <div
        key={deal.id}
        onClick={handleCardClick}
        className={`group relative bg-white rounded-lg border shadow-sm hover:shadow-md cursor-pointer transition-all ${
          isSelected ? 'border-indigo-500 ring-2 ring-indigo-300' : 'border-gray-200'
        } ${hot && !isSelected ? hotCardClasses : ''}`}
      >
        {fullDealsSelectMode ? (
          <div
            className={`absolute top-2 right-2 h-5 w-5 rounded border-2 flex items-center justify-center z-10 ${
              isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 bg-white'
            }`}
            aria-hidden="true"
          >
            {isSelected && <CheckCircleIcon className="h-3.5 w-3.5" />}
          </div>
        ) : (
          <button
            type="button"
            onClick={(e) => handleDismissFullDeal(deal, e)}
            title="Hide from Full Deals (loan application stays intact)"
            className="absolute top-1.5 right-1.5 p-1 rounded-full text-gray-300 hover:bg-gray-100 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity z-10"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}
        <div className="px-4 py-3 sm:px-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center min-w-0">
              <div className="flex-shrink-0 h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center">
                <BuildingOfficeIcon className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="ml-3 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-emerald-700 truncate">{name}</p>
                  {hot && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-500 text-white font-bold flex items-center gap-0.5">
                      <FireIcon className="h-2.5 w-2.5" /> NEW UPLOAD
                    </span>
                  )}
                  {underLoi && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-600 text-white font-extrabold tracking-wide shadow-sm">
                      UNDER LOI
                    </span>
                  )}
                  {missing.length > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                      Missing: {missing.join(' + ')}
                    </span>
                  )}
                </div>
                <div className="flex items-center text-xs text-gray-500 space-x-2 truncate">
                  {deal.email && <span className="truncate">{deal.email}</span>}
                  {deal.brokerName && <span className="truncate">· Broker: {deal.brokerName}</span>}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>{status}</span>
              <div className="flex items-center text-[11px] text-gray-500">
                <ClockIcon className="flex-shrink-0 mr-1 h-3 w-3 text-gray-400" />
                {formatDate(deal.createdAt)}
              </div>
            </div>
          </div>
          {(deal.purchasePrice || deal.loanAmount || deal.businessState) && (
            <div className="mt-1.5 flex space-x-4 text-[11px] text-gray-500">
              {deal.purchasePrice && (
                <span>Purchase: <span className="font-medium">${Number(deal.purchasePrice).toLocaleString()}</span></span>
              )}
              {deal.loanAmount && (
                <span>Loan: <span className="font-medium">${Number(deal.loanAmount).toLocaleString()}</span></span>
              )}
              {deal.businessState && (
                <span><MapPinIcon className="inline h-3 w-3 mr-0.5 text-gray-400" />{deal.businessState}</span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const FullDealStageSection = ({ stage, items }) => {
    const stageKey = `fulldeal-${stage.key}`;
    const collapsed = collapsedStages.has(stageKey);
    const empty = items.length === 0;
    return (
      <section className={`rounded-xl border ${stage.border} ${stage.bg} overflow-hidden`}>
        <button
          type="button"
          onClick={() => toggleStageCollapsed(stageKey)}
          className={`w-full px-4 py-2.5 flex items-center justify-between gap-2 ${collapsed ? '' : 'border-b ' + stage.border} hover:bg-black/[0.02] transition-colors`}
        >
          <div className="flex items-center gap-2 min-w-0">
            {collapsed ? (
              <ChevronRightIcon className={`h-4 w-4 flex-shrink-0 ${stage.iconColor}`} />
            ) : (
              <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 ${stage.iconColor}`} />
            )}
            <stage.icon className={`h-4 w-4 flex-shrink-0 ${stage.iconColor}`} />
            <h4 className={`text-sm font-semibold ${stage.titleColor} truncate`}>{stage.label}</h4>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${stage.badgeBg} ${stage.badgeText} flex-shrink-0`}>{items.length}</span>
          </div>
          {!collapsed && (
            <span className={`text-[10px] italic ${stage.actionColor} truncate ml-2 hidden md:inline`}>{stage.action}</span>
          )}
        </button>
        {!collapsed && (
          <div className="p-2 space-y-2">
            {empty ? (
              <p className="text-xs text-gray-400 italic text-center py-3">{stage.emptyLabel}</p>
            ) : (
              items.map(renderFullDealCard)
            )}
          </div>
        )}
      </section>
    );
  };

  const renderFullDeals = () => {
    const filteredDeals = filterBySearch(fullDeals, ['firstName', 'lastName', 'email', 'businessName', 'brokerName', 'brokerEmail']);

    const byStage = {
      awaiting_files: [],
      awaiting_workbook: [],
      awaiting_offer: [],
      awaiting_signing: [],
      signed_underwriting: [],
    };
    filteredDeals.forEach(d => {
      const k = classifyFullDealStage(d);
      byStage[k].push(d);
    });
    Object.keys(byStage).forEach(k => { byStage[k] = sortHotFirst(byStage[k]); });
    const underLoiDeals = filteredDeals.filter(isUnderLoi);

    const stages = [
      { key: 'awaiting_files', label: 'Awaiting files (borrower + business)', emptyLabel: 'All deals have their files.',
        icon: ArrowUpTrayIcon, iconColor: 'text-amber-600', titleColor: 'text-amber-900', actionColor: 'text-amber-700',
        bg: 'bg-amber-50/40', border: 'border-amber-200', badgeBg: 'bg-amber-200', badgeText: 'text-amber-800',
        action: 'Chase borrower or broker for remaining docs' },
      { key: 'awaiting_workbook', label: 'Awaiting workbook completion', emptyLabel: 'No deals waiting on the workbook.',
        icon: DocumentIcon, iconColor: 'text-blue-600', titleColor: 'text-blue-900', actionColor: 'text-blue-700',
        bg: 'bg-blue-50/40', border: 'border-blue-200', badgeBg: 'bg-blue-200', badgeText: 'text-blue-800',
        action: 'Build out the financial workbook' },
      { key: 'awaiting_offer', label: 'Awaiting offer', emptyLabel: 'No deals ready for an offer.',
        icon: CurrencyDollarIcon, iconColor: 'text-indigo-600', titleColor: 'text-indigo-900', actionColor: 'text-indigo-700',
        bg: 'bg-indigo-50/40', border: 'border-indigo-200', badgeBg: 'bg-indigo-200', badgeText: 'text-indigo-800',
        action: 'Send to lenders / package the offer' },
      { key: 'awaiting_signing', label: 'Awaiting term sheet signature', emptyLabel: 'No term sheets out for signature.',
        icon: PencilSquareIcon, iconColor: 'text-purple-600', titleColor: 'text-purple-900', actionColor: 'text-purple-700',
        bg: 'bg-purple-50/40', border: 'border-purple-200', badgeBg: 'bg-purple-200', badgeText: 'text-purple-800',
        action: 'Push borrower to sign the term sheet' },
      { key: 'signed_underwriting', label: 'Signed / in underwriting', emptyLabel: 'Nothing in underwriting yet.',
        icon: CheckCircleIcon, iconColor: 'text-emerald-600', titleColor: 'text-emerald-900', actionColor: 'text-emerald-700',
        bg: 'bg-emerald-50/40', border: 'border-emerald-300', badgeBg: 'bg-emerald-500', badgeText: 'text-white',
        action: 'Lender working it — keep tabs on questions' },
    ];

    return (
      <div className="space-y-4">
        {underLoiDeals.length > 0 && (
          <div className="rounded-xl border-2 border-rose-300 bg-rose-50 px-4 py-3 text-rose-900 shadow-sm">
            <div className="flex items-center gap-3"><ExclamationCircleIcon className="h-6 w-6 flex-none text-rose-600" /><div><p className="text-sm font-extrabold">{underLoiDeals.length} active {underLoiDeals.length === 1 ? 'deal is' : 'deals are'} under LOI</p><p className="text-xs text-rose-700">Prioritize these files and prequalification packages.</p></div></div>
          </div>
        )}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-center justify-between gap-3 flex-wrap">
          <span>Full deals are managed by Isak. This view lets Ben see status and files without taking action.</span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {fullDealsSelectMode ? (
              <>
                <span className="text-xs font-medium text-gray-700">
                  {selectedFullDealIds.size} selected
                </span>
                <button
                  type="button"
                  onClick={handleBulkRemoveFullDeals}
                  disabled={selectedFullDealIds.size === 0 || fullDealsBulkRemoving}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {fullDealsBulkRemoving ? 'Hiding…' : `Hide ${selectedFullDealIds.size || ''}`.trim()}
                </button>
                <button
                  type="button"
                  onClick={exitFullDealsSelectMode}
                  disabled={fullDealsBulkRemoving}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setFullDealsSelectMode(true)}
                className="text-xs font-medium px-3 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-800 hover:bg-amber-100"
              >
                Select &amp; remove
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {stages.map(s => (
            <div key={s.key} className={`rounded-lg ${s.bg} border ${s.border} px-3 py-2`}>
              <p className={`text-[10px] uppercase tracking-wider ${s.titleColor} font-semibold truncate`}>{s.label}</p>
              <p className={`mt-1 text-xl font-bold ${s.titleColor} leading-none`}>{byStage[s.key].length}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : filteredDeals.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No full deals yet.</div>
        ) : (
          <div className="space-y-3">
            {stages.map(stage => (
              <FullDealStageSection key={stage.key} stage={stage} items={byStage[stage.key]} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderBorrowerPrequalifications = () => {
    const filteredSignups = sortReadyAndHotFirst(
      filterBySearch(borrowerSignups, ['firstName', 'lastName', 'email', 'businessName']),
      isBorrowerReadyForPrequal
    );
    const filteredRecent = filterBySearch(recentlyPrequalifiedBorrowers, ['firstName', 'lastName', 'email', 'businessName']);

    return (
      <div className="space-y-6">
        {/* Recently Prequalified Section */}
        {filteredRecent.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
              <div className="flex items-center">
                <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Recently Prequalified</h3>
                <span className="ml-3 bg-green-100 text-green-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {filteredRecent.length}
                </span>
                <span className="ml-2 text-xs text-gray-500">(Past 2 days)</span>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredRecent.map((borrower) => (
                  <div
                    key={borrower.id}
                    onClick={() => navigate(`/loan-details/${borrower.id}`)}
                    className="border border-green-200 bg-green-50 rounded-lg p-4 cursor-pointer hover:bg-green-100 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{borrower.firstName} {borrower.lastName}</p>
                        <p className="text-xs text-gray-500">{borrower.email}</p>
                      </div>
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Prequalified: {formatDate(borrower.prequalifiedAt || borrower.lastUpdated)}
                    </div>
                    {borrower.businesses?.filter(b => b.prequalified).length > 0 && (
                      <div className="mt-1 text-xs text-green-700">
                        {borrower.businesses.filter(b => b.prequalified).length} prequalified deal(s)
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Awaiting Prequalification Section */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            Borrower Signups Awaiting Prequalification ({filteredSignups.length})
          </h3>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : filteredSignups.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No borrower signups awaiting prequalification</div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {filteredSignups.map((signup) => {
                const ready = isBorrowerReadyForPrequal(signup);
                const hot = !ready && isHot(signup);
                return (
                <li key={signup.id} className={ready ? readyCardClasses + ' rounded-md' : hot ? hotCardClasses + ' rounded-md' : ''}>
                  <div
                    className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/loan-details/${signup.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${ready ? 'bg-emerald-100' : 'bg-indigo-100'}`}>
                            <UserGroupIcon className={`h-6 w-6 ${ready ? 'text-emerald-600' : 'text-indigo-600'}`} />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-indigo-600">
                              {signup.firstName} {signup.lastName}
                            </p>
                            {ready && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white font-bold flex items-center gap-0.5">
                                <SparklesIcon className="h-2.5 w-2.5" /> NEEDS PREQUAL
                              </span>
                            )}
                            {hot && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-500 text-white font-bold flex items-center gap-0.5">
                                <FireIcon className="h-2.5 w-2.5" /> NEW UPLOAD
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <EnvelopeIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            {signup.email}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <div className="flex items-center text-sm text-gray-500">
                          <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          {formatDate(signup.createdAt)}
                        </div>
                        {signup.businessState && (
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <MapPinIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            {signup.businessState}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex space-x-4">
                        {signup.loanSize && (
                          <p className="text-sm text-gray-500">
                            Loan Size: <span className="font-medium">${Number(signup.loanSize).toLocaleString()}</span>
                          </p>
                        )}
                        {signup.businesses?.length > 0 && (
                          <p className="text-sm text-gray-500">
                            Businesses: <span className="font-medium">{signup.businesses.length}</span>
                          </p>
                        )}
                      </div>
                      <div className="mt-2 sm:mt-0">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          signup.loanStatus === 'New Application'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {signup.loanStatus || 'New Application'}
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderBusinessPrequalifications = () => {
    const filteredPrequals = sortReadyAndHotFirst(
      filterBySearch(businessPrequals, ['businessName', 'brokerName', 'brokerEmail']),
      isBusinessReadyForPrequal
    );
    const filteredRecent = filterBySearch(recentlyPrequalifiedBusinesses, ['businessName', 'brokerName', 'brokerEmail']);

    return (
      <div className="space-y-6">
        {/* Recently Prequalified Section */}
        {filteredRecent.length > 0 && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
              <div className="flex items-center">
                <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Recently Prequalified</h3>
                <span className="ml-3 bg-green-100 text-green-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {filteredRecent.length}
                </span>
                <span className="ml-2 text-xs text-gray-500">(Past 2 days)</span>
              </div>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredRecent.map((prequal) => (
                  <div
                    key={prequal.id}
                    onClick={() => navigate(`/prequalification-details/${prequal.id}`)}
                    className="border border-green-200 bg-green-50 rounded-lg p-4 cursor-pointer hover:bg-green-100 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{prequal.businessName || 'Unnamed Business'}</p>
                        <p className="text-xs text-gray-500">Broker: {prequal.brokerName}</p>
                      </div>
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Prequalified: {formatDate(prequal.prequalifiedAt || prequal.lastUpdated)}
                    </div>
                    {prequal.prequalifiedAmount && (
                      <div className="mt-1 text-xs text-green-700 font-medium">
                        Amount: ${Number(prequal.prequalifiedAmount).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Awaiting Review Section */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            Business Prequalifications Awaiting Review ({filteredPrequals.length})
          </h3>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : filteredPrequals.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No business prequalifications awaiting review</div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {filteredPrequals.map((prequal) => {
                const ready = isBusinessReadyForPrequal(prequal);
                const hot = !ready && isHot(prequal);
                return (
                <li key={prequal.id} className={ready ? readyCardClasses + ' rounded-md' : hot ? hotCardClasses + ' rounded-md' : ''}>
                  <div
                    className={`px-4 py-4 sm:px-6 cursor-pointer ${
                      prequal.source === 'borrower'
                        ? 'bg-blue-50 hover:bg-blue-100 border-l-4 border-blue-400'
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      if (prequal.source === 'borrower') {
                        navigate(`/loan-details/${prequal.loanApplicationId}`);
                      } else {
                        navigate(`/prequalification-details/${prequal.id}`);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                            ready ? 'bg-emerald-100' : prequal.source === 'borrower' ? 'bg-blue-100' : 'bg-green-100'
                          }`}>
                            <BuildingOfficeIcon className={`h-6 w-6 ${
                              ready ? 'text-emerald-600' : prequal.source === 'borrower' ? 'text-blue-600' : 'text-green-600'
                            }`} />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center flex-wrap gap-2">
                            <p className={`text-sm font-medium ${
                              prequal.source === 'borrower' ? 'text-blue-600' : 'text-green-600'
                            }`}>
                              {prequal.businessName || 'Unnamed Business'}
                            </p>
                            {ready && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500 text-white gap-0.5">
                                <SparklesIcon className="h-2.5 w-2.5" /> NEEDS PREQUAL
                              </span>
                            )}
                            {hot && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-500 text-white gap-0.5">
                                <FireIcon className="h-2.5 w-2.5" /> NEW UPLOAD
                              </span>
                            )}
                            {prequal.source === 'borrower' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
                                <UserGroupIcon className="w-3 h-3 mr-1" />
                                Borrower Deal
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {prequal.source === 'borrower'
                              ? `Borrower: ${prequal.borrowerName}`
                              : `Broker: ${prequal.brokerName || 'Unknown'}`
                            }
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <div className="flex items-center text-sm text-gray-500">
                          <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                          {formatDate(prequal.createdAt)}
                        </div>
                        {prequal.businessState && (
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <MapPinIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                            {prequal.businessState}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between">
                      <div className="sm:flex space-x-4">
                        {prequal.projectedSalePrice && (
                          <p className="text-sm text-gray-500">
                            Sale Price: <span className="font-medium">${Number(prequal.projectedSalePrice).toLocaleString()}</span>
                          </p>
                        )}
                        {prequal.ebitda && (
                          <p className="text-sm text-gray-500">
                            EBITDA: <span className="font-medium">${Number(prequal.ebitda).toLocaleString()}</span>
                          </p>
                        )}
                      </div>
                      <div className="mt-2 sm:mt-0 flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          prequal.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {prequal.status || 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Expanded panel for Today's Action Items — fetches uploaded files
  const ExpandedRecordPanel = ({ record, logs, getDaysSince: gds }) => {
    const [files, setFiles] = useState([]);
    const [fileDocs, setFileDocs] = useState([]); // raw snap docs for updating
    const [filesLoaded, setFilesLoaded] = useState(false);
    const [markingSeenLoading, setMarkingSeenLoading] = useState(false);

    const unseenFiles = files.filter(f => f.seenByAdmin !== true);
    const unseenCount = unseenFiles.length;

    useEffect(() => {
      if (!filesLoaded && record.collection === 'loanApplications' && record.id) {
        getDocs(query(collection(db, 'documentMetadata'), where('loanApplicationId', '==', record.id)))
          .then(snap => { setFiles(snap.docs.map(d => ({ ...d.data(), _docId: d.id }))); setFileDocs(snap.docs); setFilesLoaded(true); })
          .catch(() => setFilesLoaded(true));
      } else if (!filesLoaded && record.collection === 'pendingSearcherSignups' && record.id) {
        getDocs(query(collection(db, 'documentMetadata'), where('uploadLinkId', '==', record.id)))
          .then(snap => { setFiles(snap.docs.map(d => ({ ...d.data(), _docId: d.id }))); setFileDocs(snap.docs); setFilesLoaded(true); })
          .catch(() => setFilesLoaded(true));
      } else {
        setFilesLoaded(true);
      }
    }, [record.id, record.collection, filesLoaded]);

    const handleMarkSeen = async () => {
      setMarkingSeenLoading(true);
      try {
        const unseenDocs = fileDocs.filter(d => d.data().seenByAdmin !== true);
        await Promise.all(unseenDocs.map(d => updateDoc(doc(db, 'documentMetadata', d.id), { seenByAdmin: true, seenByAdminAt: serverTimestamp() })));
        setFiles(prev => prev.map(f => ({ ...f, seenByAdmin: true })));
        // Clear the "review files" action by marking files as reviewed on the loanApp
        if (record.collection === 'loanApplications') {
          await updateDoc(doc(db, 'loanApplications', record.id), { filesReviewedAt: serverTimestamp() });
        }
        // Refresh today's action items so the review action goes away
        await fetchTodaysFollowups();
      } catch (e) {
        alert('Error marking files seen: ' + e.message);
      }
      setMarkingSeenLoading(false);
    };

    return (
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
        {/* Notes + Log History */}
        <div className="mb-2">
          <span className="text-xs font-medium text-gray-600">Notes: </span>
          <span className="text-xs text-gray-500">{record.notes || record.lastLogNotes || 'No notes'}</span>
          {logs.length > 0 && (
            <div className="mt-1.5 space-y-1 max-h-32 overflow-y-auto">
              {[...logs].reverse().map((log, idx) => (
                <div key={idx} className="text-xs bg-white rounded p-1.5 border border-gray-100 flex items-center gap-2">
                  <span className="text-gray-400 flex-shrink-0">{log.timestamp ? new Date(log.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                  <span className={`px-1 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${log.type === 'Call' ? 'bg-blue-100 text-blue-700' : log.type === 'Email' ? 'bg-purple-100 text-purple-700' : log.type === 'Text' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{log.type}</span>
                  <span className="text-gray-600 truncate">{log.notes || ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Uploaded files */}
        <div className="mb-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-600">Uploaded Files ({files.length}):</span>
            {unseenCount > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold">{unseenCount} new</span>
            )}
            {unseenCount > 0 && (
              <button onClick={handleMarkSeen} disabled={markingSeenLoading} className="text-[10px] px-2 py-0.5 rounded bg-orange-200 text-orange-800 hover:bg-orange-300 font-medium disabled:opacity-50">
                {markingSeenLoading ? 'Marking...' : 'Mark Seen'}
              </button>
            )}
          </div>
          {files.length > 0 ? (
            <div className="mt-1 flex flex-wrap gap-1">
              {files.map((f, idx) => (
                <span key={idx} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${f.seenByAdmin !== true ? 'bg-orange-100 text-orange-700 ring-1 ring-orange-300' : 'bg-green-100 text-green-700'}`}>
                  {f.label || f.documentName || f.fileName}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs text-gray-400 ml-1">{filesLoaded ? 'None' : 'Loading...'}</span>
          )}
        </div>
        {/* Status line */}
        <div className="mb-2 text-xs text-gray-500">
          <span className="font-medium text-gray-600">Status: </span>
          {record.allDocumentsUploaded ? <span className="text-green-700 font-medium">All files received</span>
            : record.allPersonalFilesUploaded ? <span className="text-amber-700 font-medium">Personal files in, waiting on deal files</span>
            : <span className="text-gray-500">Waiting on files</span>}
          {record.borrowerPrequalAmount > 0 && <span className="text-blue-700 font-medium"> · Prequalified ${`$${Number(record.borrowerPrequalAmount).toLocaleString()}`}</span>}
          {record.sharedUploadRoute && <span className="text-indigo-500"> · Shared upload</span>}
        </div>
        {/* Log history */}
        {logs.length > 0 ? (
          <div>
            <span className="text-xs font-medium text-gray-600">Follow-up Log ({logs.length}):</span>
            <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
              {[...logs].reverse().map((log, idx) => (
                <div key={idx} className="text-xs bg-white rounded p-1.5 border border-gray-100 flex items-center gap-2">
                  <span className="text-gray-400">{log.timestamp ? new Date(log.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                  <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${log.type === 'Call' ? 'bg-blue-100 text-blue-700' : log.type === 'Email' ? 'bg-purple-100 text-purple-700' : log.type === 'Text' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{log.type}</span>
                  <span className="text-gray-600 truncate">{log.notes || ''}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <span className="text-xs text-gray-400 italic">No follow-ups logged yet</span>
        )}
        {/* Broker-specific: show listings */}
        {record.type === 'Broker' && record.totalListings > 0 && (
          <div className="mt-2">
            <span className="text-xs font-medium text-gray-600">Listings: {record.totalListings}</span>
            <span className="text-xs text-gray-400 ml-2">Last activity: {gds(record.lastActivity)}</span>
          </div>
        )}
      </div>
    );
  };

  const getDaysSince = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    const days = Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  const renderFollowups = () => {
    const totalFollowups = initialPrequalFilesNeeded.length + intakeListFollowups.length + newDealFollowups.length + businessFollowups.length + brokerFollowups.length + offMarketSearchers.length + pendingSearchers.length + pendingBrokers.length + pendingMeetings.length;

    return (
      <div className="space-y-8">
        {/* TODAY'S ACTION ITEMS */}
        {(() => {
          // Merge all follow-up categories into today's list
          // Removed the old "actionsCutoffForBrokers" March 27 cutoff — it
          // hid valid brokers from Today's Action Items. fetchFollowups already
          // applies DATA_CUTOFF_DATE and the weekly cadence rule, which is the
          // right gate for this list.
          const brokerItems = brokerFollowups
            .filter(b => {
              if (todaysFollowups.some(t => t.id === b.id && t.collection === 'brokers')) return false;
              if (b.followUpStatus === 'newsletter') return false;
              return true;
            })
            .map(b => ({
              ...b, id: b.id, collection: 'brokers',
              name: (b.firstName && b.lastName ? `${b.firstName} ${b.lastName}` : '') || b.brokerAgeName || b.name || b.businessName || b.companyName || 'Unknown Broker', type: 'Broker',
              stage: `${b.totalListings || 0} listing(s)`, category: 'broker',
              nextAction: `No activity in ${Math.floor((new Date() - (b.lastActivity || new Date())) / (1000*60*60*24))} days — follow up on listings`,
              email: b.email, phoneNumber: b.phoneNumber || b.phone,
            }));
          // Tag existing items with categories
          const taggedToday = todaysFollowups.map(r => ({
            ...r,
            category: r.stage === 'Franchise' ? 'franchise'
              : r.stage === 'Startup' ? 'startup'
              : r.stage === 'Pending Signup' || r.stage === 'New Pending Signup' ? 'pending'
              : r.stage === 'Pending Meeting' ? 'pending_meeting'
              : r.stage === 'New Signup' ? 'prequal_files'
              : r.stage === 'Recent Upload' ? 'intake_files'
              : r.type === 'Broker' ? 'broker'
              : r.type === 'Listing' ? 'listing'
              : r.stage === 'Active' ? 'deal_upload'
              : 'other'
          }));
          // Add pending brokers (only created after cutoff and FU is due)
          const actionsCutoff = new Date('2026-03-27T00:00:00');
          const todayDate = new Date(); todayDate.setHours(0,0,0,0);
          const pendingBrokerItems = pendingBrokers
            .filter(b => {
              const created = b.createdAt?.toDate ? b.createdAt.toDate() : null;
              if (!created || created < actionsCutoff) return false;
              if (b.followUpStatus === 'newsletter') return false;
              // Honor a scheduled NextActionDate when set.
              if (b.NextActionDate?.toDate) {
                return b.NextActionDate.toDate() <= todayDate;
              }
              // Otherwise: anyone who hasn't been contacted yet is fair game,
              // including same-day adds. Previously this required created < todayDate
              // which hid newly-added pending brokers until the next day.
              return !b.lastContactDate;
            })
            .map(b => {
              const displayName = (b.firstName && b.lastName ? `${b.firstName} ${b.lastName}` : '')
                || b.name
                || b.brokerageName
                || b.email
                || 'Unknown Broker';
              return {
                ...b, id: b.id, collection: 'pendingBrokerSignups',
                name: displayName, type: 'Broker',
                stage: 'Pending Signup', category: 'pending_broker',
                email: b.email, phoneNumber: b.phoneNumber || b.phone,
              };
            });
          const allItems = [...taggedToday, ...brokerItems, ...pendingBrokerItems];

          // Tag items that need Ben's action (computed from getDetailedAction)
          const allItemsWithBenTag = allItems.map(item => {
            const detail = getDetailedAction(item);
            return { ...item, _benAction: detail?.benAction || false, _detailCache: detail };
          });

          const filters = [
            { key: 'all', label: 'All' },
            { key: 'ben_action', label: 'Ben Action' },
            { key: 'pending', label: 'Pending Signups' },
            { key: 'franchise', label: 'Franchise' },
            { key: 'startup', label: 'Startup' },
            { key: 'pending_meeting', label: 'Pending Meetings' },
            { key: 'prequal_files', label: 'Need Prequal Files' },
            { key: 'intake_files', label: 'Recent Uploads' },
            { key: 'deal_upload', label: 'Need Deal Upload' },
            { key: 'broker', label: 'Brokers' },
            { key: 'pending_broker', label: 'Pending Brokers' },
            { key: 'listing', label: 'Listings' },
          ];
          const filteredItems = todayFilter === 'ben_action'
            ? allItemsWithBenTag.filter(r => r._benAction)
            : todayFilter === 'all' ? allItemsWithBenTag : allItemsWithBenTag.filter(r => r.category === todayFilter);

          return (
            <div className="bg-white shadow-lg rounded-lg overflow-hidden border-l-4 border-red-500">
              <div className="px-4 py-3 bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                    <h3 className="text-base font-bold text-gray-900">TODAY'S ACTION ITEMS</h3>
                    <span className="ml-2 bg-red-600 text-white py-0.5 px-2 rounded-full text-xs font-bold">{allItems.length}</span>
                  </div>
                </div>
                {/* Filters */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {filters.map(f => {
                    const count = f.key === 'all' ? allItemsWithBenTag.length : f.key === 'ben_action' ? allItemsWithBenTag.filter(r => r._benAction).length : allItemsWithBenTag.filter(r => r.category === f.key).length;
                    if (count === 0 && f.key !== 'all') return null;
                    return (
                      <button key={f.key} onClick={() => setTodayFilter(f.key)}
                        className={`text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors ${todayFilter === f.key ? 'bg-red-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
                        {f.label} ({count})
                      </button>
                    );
                  })}
                </div>
              </div>
              {filteredItems.length === 0 ? (
                <div className="text-center py-4">
                  <CheckCircleIcon className="h-8 w-8 text-green-400 mx-auto mb-1" />
                  <p className="text-sm text-gray-500">No items{todayFilter !== 'all' ? ' in this filter' : ''}.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredItems.map((record) => {
                    const recordEmail = record.email || record.brokerEmail;
                    const recordPhone = record.phoneNumber || record.phone;
                    const lastLog = record.followUpLog?.length > 0 ? record.followUpLog[record.followUpLog.length - 1] : null;
                    const logs = record.followUpLog || [];
                    const isExpanded = expandedTodayId === `${record.collection}-${record.id}`;
                    const detailed = getDetailedAction(record);
                    if (!detailed) return null; // Not due for follow-up
                    const priorityBorder = detailed.priority === 'high' ? 'border-l-3 border-l-red-400' : detailed.priority === 'low' ? 'border-l-3 border-l-gray-300' : 'border-l-3 border-l-yellow-400';
                    const typeBadge = record.collection === 'pendingMeetings' ? 'bg-yellow-100 text-yellow-700' : record.collection === 'pendingBrokerSignups' ? 'bg-purple-100 text-purple-700' : record.type === 'Broker' ? 'bg-purple-100 text-purple-700' : record.type === 'Listing' ? 'bg-teal-100 text-teal-700' : record.collection === 'pendingSearcherSignups' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700';
                    // Check for recent user activity (uploads in last 24h)
                    const hasRecentActivity = (() => { const lu = record.lastUserUpdate?.toDate ? record.lastUserUpdate.toDate() : record.lastUserUpdate ? new Date(record.lastUserUpdate) : null; return lu && (new Date() - lu) < 24 * 60 * 60 * 1000; })();
                    // Row background — activity rows pulse, ben action red, type colors
                    const rowBg = hasRecentActivity ? 'bg-orange-100 border-l-4 border-l-orange-500'
                      : record._benAction ? 'bg-red-50/70'
                      : (record.collection === 'pendingSearcherSignups' && record.noShow) ? 'bg-red-50/60'
                      : record.collection === 'pendingSearcherSignups' ? 'bg-orange-50/60'
                      : record.collection === 'pendingMeetings' ? 'bg-yellow-50/60'
                      : record.collection === 'pendingBrokerSignups' ? 'bg-purple-50/60'
                      : record.type === 'Broker' ? 'bg-purple-50/60'
                      : record.type === 'Listing' ? 'bg-teal-50/60'
                      : record.collection === 'loanApplications' ? 'bg-blue-50/40'
                      : 'bg-white';

                    return (
                      <div key={`${record.collection}-${record.id}`}>
                        <div className={`px-4 py-2.5 hover:brightness-95 cursor-pointer ${priorityBorder} ${rowBg}`}
                          onClick={() => {
                            if (record.type === 'Broker') {
                              setExpandedTodayId(isExpanded ? null : `${record.collection}-${record.id}`);
                            } else if (record.collection === 'loanApplications') {
                              navigate(`/loan-details/${record.id}`);
                            } else if (record.collection === 'prequalificationApplications') {
                              navigate(`/prequalification-details/${record.id}`);
                            } else {
                              setExpandedTodayId(isExpanded ? null : `${record.collection}-${record.id}`);
                            }
                          }}>
                          {/* Row 1: Name + type + contact */}
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1.5 min-w-0" style={{ minWidth: '160px', maxWidth: '220px' }}>
                              <span className="font-medium text-sm text-gray-900 truncate">{record.name || 'Unknown'}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${record.noShow ? 'bg-red-100 text-red-700' : typeBadge}`}>{record.noShow ? 'No Show' : record.type === 'Searcher' && record.collection === 'pendingSearcherSignups' ? 'Pending' : record.collection === 'pendingMeetings' ? 'Meeting' : record.type}</span>
                              {hasRecentActivity && <span className="flex-shrink-0 h-2 w-2 rounded-full bg-orange-500"></span>}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500 min-w-0" style={{ minWidth: '180px', maxWidth: '260px' }}>
                              {recordEmail && <a href={`mailto:${recordEmail}`} onClick={e => e.stopPropagation()} className="hover:text-blue-600 truncate flex items-center"><EnvelopeIcon className="h-3 w-3 mr-0.5 flex-shrink-0" />{recordEmail}</a>}
                              {recordPhone && <a href={`tel:${recordPhone}`} onClick={e => e.stopPropagation()} className="hover:text-blue-600 flex items-center flex-shrink-0"><PhoneIcon className="h-3 w-3 mr-0.5" />{recordPhone}</a>}
                            </div>
                            {/* Action needed */}
                            <div className="flex-1 min-w-0">
                              <span className={`text-xs font-semibold ${detailed.color}`}>{detailed.action}</span>
                            </div>
                            {/* Last contact */}
                            <div className="flex-shrink-0 text-xs text-gray-400 w-24 text-right">
                              {lastLog ? <span>{lastLog.type} {lastLog.timestamp ? new Date(lastLog.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span> : <span>{getDaysSince(record.createdAt)}</span>}
                            </div>
                            {/* Buttons */}
                            <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                              <button onClick={() => openLogModal({ ...record, collection: record.collection })} className="text-[10px] px-2 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 font-medium">Log</button>
                              {recordEmail && <button onClick={() => { const fn = record.name?.split(' ')[0] || record.name; const seqType = detailed?.sequenceType; const fuNum = detailed?.fuNumber; if (seqType && fuNum) { const tpl = getEmailTemplate(seqType, fuNum, record); openEmailModal(recordEmail, fn, 'custom', record.businessName || '', tpl.subject, tpl.body, { ...record, collection: record.collection }); } else { const tpl = record.type === 'Broker' ? 'broker' : record.collection === 'prequalificationApplications' ? 'business' : 'newDeal'; openEmailModal(recordEmail, fn, tpl, record.businessName || '', '', '', { ...record, collection: record.collection }); } }} className="text-[10px] px-2 py-1 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 font-medium">Email</button>}
                              {recordPhone && <button onClick={() => openTextModal({ ...record, collection: record.collection })} className="text-[10px] px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 font-medium">Text</button>}
                              {recordPhone && <button onClick={() => handleInitiateCall({ ...record, collection: record.collection })} className="text-[10px] px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 font-medium"><PhoneArrowUpRightIcon className="h-3 w-3 inline mr-0.5" />Call</button>}
                              {record.notes && <button onClick={() => openNotesModal(record.name, record.notes)} className="text-[10px] px-2 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 font-medium">Notes</button>}
                              {(record.collection === 'loanApplications' || record.collection === 'pendingSearcherSignups') && (
                                <button onClick={() => { const linkId = record.sharedUploadPendingId || record.id; navigator.clipboard.writeText(`https://lended.ai/upload/${linkId}`); alert('Upload link copied!'); }} className="text-[10px] px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 font-medium">Link</button>
                              )}
                              {record.collection === 'loanApplications' && (!record.allPersonalFilesUploaded || (record.allPersonalFilesUploaded && !record.allDocumentsUploaded)) && (
                                <div className="relative">
                                  <button onClick={() => setMarkCompleteDropdown(markCompleteDropdown === record.id ? null : record.id)} className="text-[10px] px-2 py-1 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 font-medium flex items-center gap-0.5">
                                    Mark Complete <ChevronDownIcon className="h-3 w-3" />
                                  </button>
                                  {markCompleteDropdown === record.id && (
                                    <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px]">
                                      {!record.allPersonalFilesUploaded && (
                                        <button onClick={async () => { setMarkCompleteDropdown(null); if (!window.confirm('Mark personal files complete?')) return; try { const r = doc(db, 'loanApplications', record.id); await updateDoc(r, { allPersonalFilesUploaded: true, personalFilesMarkedBy: 'Ben', personalFilesMarkedAt: serverTimestamp() }); if (record.email && !record.personalFilesEmailSent) { await addDoc(collection(db, 'email'), { to: record.email, from: 'Isak @ Lended <isak@lended.ai>', replyTo: 'isak@lended.ai', subject: 'Personal Files Received — Prequalification Coming Shortly', message: { subject: 'Personal Files Received — Prequalification Coming Shortly', html: `<p>Hi ${record.firstName || ''},</p><p>We've received all your personal financial documents — thank you! We'll have your prequalification ready shortly.</p><p>Best,</p><p>Isak<br>(914) 414-1106<br>isak@lended.ai</p>` } }); await updateDoc(r, { personalFilesEmailSent: true }); } await Promise.all([fetchFollowups(), fetchTodaysFollowups()]); } catch(e) { alert('Error: ' + e.message); } }} className="block w-full text-left text-[11px] px-3 py-1.5 hover:bg-amber-50 text-amber-700 font-medium">Personal Files Complete</button>
                                      )}
                                      {record.allPersonalFilesUploaded && !record.allDocumentsUploaded && (
                                        <button onClick={async () => { setMarkCompleteDropdown(null); if (!window.confirm('Mark ALL deal files complete?')) return; try { const r = doc(db, 'loanApplications', record.id); await updateDoc(r, { allDocumentsUploaded: true, markedCompleteBy: 'Ben', markedCompleteAt: serverTimestamp() }); if (record.email && !record.allFilesEmailSent) { await addDoc(collection(db, 'email'), { to: record.email, from: 'Isak @ Lended <isak@lended.ai>', replyTo: 'isak@lended.ai', subject: 'All Documents Received — We Are On It', message: { subject: 'All Documents Received — We Are On It', html: `<p>Hi ${record.firstName || ''},</p><p>We've received all your documents. We're working on your full analysis now and will be in touch soon.</p><p>Best,</p><p>Isak<br>(914) 414-1106<br>isak@lended.ai</p>` } }); await updateDoc(r, { allFilesEmailSent: true }); } await Promise.all([fetchFollowups(), fetchTodaysFollowups()]); } catch(e) { alert('Error: ' + e.message); } }} className="block w-full text-left text-[11px] px-3 py-1.5 hover:bg-red-50 text-red-700 font-medium">All Deal Files Complete</button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              <button onClick={async () => { if (!window.confirm(`End all follow-ups for ${record.name || 'this contact'}? They won't appear in Today's Action Items anymore.`)) return; try { await updateDoc(doc(db, record.collection, record.id), { followUpStatus: 'newsletter', followUpEndedBy: 'Ben', followUpEndedAt: serverTimestamp() }); await Promise.all([fetchFollowups(), fetchPendingSearchers(), fetchPendingBrokers(), fetchPendingMeetings(), fetchTodaysFollowups(), fetchOffMarketSearchers()]); } catch(e) { alert('Error: ' + e.message); } }} className="text-[10px] px-2 py-1 bg-gray-100 text-gray-500 rounded hover:bg-red-100 hover:text-red-600 font-medium" title="End follow-ups for this contact">End</button>
                              <button onClick={() => setExpandedTodayId(isExpanded ? null : `${record.collection}-${record.id}`)} className="text-[10px] px-1.5 py-1 text-gray-400 hover:text-gray-600">
                                {isExpanded ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />}
                              </button>
                            </div>
                          </div>
                          {/* Row 2: Detail line + shared upload badge */}
                          <div className="mt-0.5 ml-0.5 flex items-center gap-2">
                            <span className="text-[11px] text-gray-400">{detailed.detail}</span>
                            {record.sharedUploadRoute && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Shared Upload</span>}
                          </div>
                        </div>
                        {/* Expanded detail panel */}
                        {isExpanded && (
                          <ExpandedRecordPanel record={record} logs={logs} getDaysSince={getDaysSince} />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* Section 1: Pending Signups */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-orange-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <UserGroupIcon className="h-6 w-6 text-orange-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Pending Signups</h3>
                <span className="ml-3 bg-orange-100 text-orange-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {pendingSearchers.length}
                </span>
              </div>
              <button
                onClick={() => setShowPendingSearcherModal(true)}
                className="flex items-center px-3 py-1.5 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Searcher
              </button>
            </div>
          </div>
          <div className="p-6">
            <p className="text-xs text-gray-500 mb-3">Searchers who haven't created an account yet. When they sign up, they'll move to Initial Prequal Files Needed.</p>
            {pendingSearchers.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No pending signups</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {pendingSearchers.map((searcher) => (
                  <div
                    key={searcher.id}
                    className="border border-orange-200 bg-orange-50 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{searcher.name}</p>
                        <p className="text-xs text-gray-500">{searcher.email}</p>
                        {searcher.phoneNumber && (
                          <p className="text-xs text-gray-500">{searcher.phoneNumber}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {searcher.searcherType && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            searcher.searcherType === 'franchise' ? 'bg-purple-100 text-purple-700'
                            : searcher.searcherType === 'startup' ? 'bg-cyan-100 text-cyan-700'
                            : 'bg-gray-100 text-gray-700'
                          }`}>
                            {searcher.searcherType === 'franchise' ? 'Franchise' : searcher.searcherType === 'startup' ? 'Startup' : searcher.searcherType}
                          </span>
                        )}
                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                          {searcher.reminderCount || 0} reminder(s)
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Added: {formatDate(searcher.createdAt)}
                      {searcher.lastReminderSentAt && (
                        <span> • Last reminder: {formatDate(searcher.lastReminderSentAt)}</span>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSendPendingReminder(searcher);
                        }}
                        className="flex items-center justify-center text-xs px-2 py-1.5 bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                      >
                        <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                        Send Reminder
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (searcher.notes) {
                            openNotesModal(searcher.name, searcher.notes);
                          }
                        }}
                        disabled={!searcher.notes}
                        title={!searcher.notes ? 'No notes available' : 'View Granola notes'}
                        className={`flex items-center text-xs px-2 py-1.5 rounded ${
                          searcher.notes
                            ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 cursor-pointer'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <ChatBubbleLeftEllipsisIcon className="h-3.5 w-3.5 mr-1" />
                        Notes
                      </button>
                      {searcher.phoneNumber && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openTextModal({ ...searcher, collection: 'pendingSearcherSignups' }); }}
                          className="flex items-center text-xs px-2 py-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          Text
                        </button>
                      )}
                      {searcher.phoneNumber && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleInitiateCall({ ...searcher, collection: 'pendingSearcherSignups' }); }}
                          className="flex items-center text-xs px-2 py-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          <PhoneArrowUpRightIcon className="h-3.5 w-3.5 mr-1" />
                          Call
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePendingSearcher(searcher.id, true);
                        }}
                        className="text-xs px-2 py-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        Mark Signed Up
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(`https://lended.ai/upload/${searcher.id}`);
                          alert('Upload link copied!');
                        }}
                        className="text-xs px-2 py-1.5 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
                      >
                        Copy Upload Link
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEmailHistory(searcher.email, searcher.name?.split(' ')[0] || searcher.name);
                        }}
                        className="text-xs px-2 py-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                      >
                        History
                      </button>
                    </div>
                    {renderLogSection(searcher, 'pendingSearcherSignups')}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section: Pending Brokers (cold-called brokers who haven't signed up yet) */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-purple-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <BriefcaseIcon className="h-6 w-6 text-purple-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Pending Brokers</h3>
                <span className="ml-3 bg-purple-100 text-purple-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {pendingBrokers.length}
                </span>
              </div>
            </div>
          </div>
          <div className="p-6">
            <p className="text-xs text-gray-500 mb-3">Brokers who got the intro call but haven't created their broker-portal account. Once they sign up, they roll into the broker follow-up list to push them to upload listings.</p>
            {pendingBrokers.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No pending brokers</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {pendingBrokers.map((broker) => {
                  const displayName = (broker.firstName && broker.lastName ? `${broker.firstName} ${broker.lastName}` : '')
                    || broker.name
                    || broker.brokerageName
                    || broker.email
                    || 'Unknown';
                  return (
                    <div key={broker.id} className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{displayName}</p>
                          <p className="text-xs text-gray-500 truncate">{broker.email}</p>
                          {(broker.phoneNumber || broker.phone) && (
                            <p className="text-xs text-gray-500">{broker.phoneNumber || broker.phone}</p>
                          )}
                          {broker.brokerageName && displayName !== broker.brokerageName && (
                            <p className="text-xs text-gray-500 truncate">Brokerage: {broker.brokerageName}</p>
                          )}
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 flex-shrink-0">
                          {broker.reminderCount || 0} reminder(s)
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        Added: {formatDate(broker.createdAt)}
                        {broker.lastReminderSentAt && (
                          <span> • Last reminder: {formatDate(broker.lastReminderSentAt)}</span>
                        )}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSendPendingBrokerReminder(broker); }}
                          className="flex items-center justify-center text-xs px-2 py-1.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                        >
                          <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                          Send Reminder
                        </button>
                        {broker.notes && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openNotesModal(displayName, broker.notes); }}
                            className="flex items-center text-xs px-2 py-1.5 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                          >
                            <ChatBubbleLeftEllipsisIcon className="h-3.5 w-3.5 mr-1" />
                            Notes
                          </button>
                        )}
                        {(broker.phoneNumber || broker.phone) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openTextModal({ ...broker, collection: 'pendingBrokerSignups' }); }}
                            className="flex items-center text-xs px-2 py-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            Text
                          </button>
                        )}
                        {(broker.phoneNumber || broker.phone) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleInitiateCall({ ...broker, collection: 'pendingBrokerSignups' }); }}
                            className="flex items-center text-xs px-2 py-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                          >
                            <PhoneArrowUpRightIcon className="h-3.5 w-3.5 mr-1" />
                            Call
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemovePendingBroker(broker.id, true); }}
                          className="text-xs px-2 py-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          Mark Signed Up
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); openEmailHistory(broker.email, displayName.split(' ')[0]); }}
                          className="text-xs px-2 py-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        >
                          History
                        </button>
                      </div>
                      {renderLogSection(broker, 'pendingBrokerSignups')}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Section: Pending Meetings (cold call contacts needing to book) */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CalendarDaysIcon className="h-6 w-6 text-yellow-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Pending Meetings</h3>
                <span className="ml-3 bg-yellow-100 text-yellow-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {pendingMeetings.length}
                </span>
              </div>
            </div>
          </div>
          <div className="p-6">
            <p className="text-xs text-gray-500 mb-3">Cold call contacts who need follow-up to book a strategy call. Auto-created from Slack via Ahmed's cold call logs.</p>
            {pendingMeetings.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No pending meetings</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {pendingMeetings.map((meeting) => (
                  <div
                    key={meeting.id}
                    className="border border-yellow-200 bg-yellow-50 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{meeting.name}</p>
                        {meeting.email && <p className="text-xs text-gray-500">{meeting.email}</p>}
                        {meeting.phone && <p className="text-xs text-gray-500">{meeting.phone}</p>}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        meeting.outreachChannel === 'email' ? 'bg-purple-100 text-purple-700'
                        : meeting.outreachChannel === 'both' ? 'bg-blue-100 text-blue-700'
                        : 'bg-green-100 text-green-700'
                      }`}>
                        {meeting.outreachChannel || 'text'}
                      </span>
                    </div>
                    {meeting.notes && (
                      <p className="text-xs text-gray-600 mt-2">{meeting.notes}</p>
                    )}
                    <div className="mt-2 text-xs text-gray-500">
                      Added: {formatDate(meeting.createdAt)}
                      {meeting.outreachResults && (
                        <span> • Outreach: {meeting.outreachResults.email === 'sent' ? 'Email sent' : ''}{meeting.outreachResults.email === 'sent' && meeting.outreachResults.text === 'sent' ? ' + ' : ''}{meeting.outreachResults.text === 'sent' ? 'Text sent' : ''}</span>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openLogModal({ ...meeting, collection: 'pendingMeetings' });
                        }}
                        className="flex items-center text-xs px-2 py-1.5 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
                      >
                        <PencilSquareIcon className="h-3.5 w-3.5 mr-1" />
                        Log
                      </button>
                      {meeting.email && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const fn = meeting.name?.split(' ')[0] || meeting.name;
                            const contactCount = meeting.followUpCount || (meeting.followUpLog?.length || 0);
                            const fuNum = Math.min(contactCount + 1, 5);
                            const tpl = getEmailTemplate('pending_meeting', fuNum, meeting);
                            openEmailModal(meeting.email, fn, 'custom', '', tpl?.subject || '', tpl?.body || '', { ...meeting, collection: 'pendingMeetings' });
                          }}
                          className="flex items-center text-xs px-2 py-1.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                        >
                          <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                          Email
                        </button>
                      )}
                      {meeting.phone && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openTextModal({ ...meeting, collection: 'pendingMeetings' }); }}
                          className="flex items-center text-xs px-2 py-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          Text
                        </button>
                      )}
                      {meeting.phone && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleInitiateCall({ ...meeting, collection: 'pendingMeetings', phoneNumber: meeting.phone }); }}
                          className="flex items-center text-xs px-2 py-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          <PhoneArrowUpRightIcon className="h-3.5 w-3.5 mr-1" />
                          Call
                        </button>
                      )}
                      {meeting.notes && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openNotesModal(meeting.name, meeting.notes);
                          }}
                          className="flex items-center text-xs px-2 py-1.5 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                        >
                          <ChatBubbleLeftEllipsisIcon className="h-3.5 w-3.5 mr-1" />
                          Notes
                        </button>
                      )}
                      {(meeting.emailBody || meeting.textBody) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const parts = [];
                            if (meeting.emailBody) parts.push(`EMAIL (${meeting.outreachResults?.email === 'sent' ? 'Sent' : 'Not sent'})\nSubject: ${meeting.emailSubject || '—'}\n\n${meeting.emailBody}`);
                            if (meeting.textBody) parts.push(`TEXT (${meeting.outreachResults?.text === 'sent' ? 'Sent' : 'Not sent'})\n\n${meeting.textBody}`);
                            openNotesModal(meeting.name + ' — Outreach Sent', parts.join('\n\n─────────────────\n\n'));
                          }}
                          className="flex items-center text-xs px-2 py-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                          Sent
                        </button>
                      )}
                      {meeting.rawSlackMessage && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openNotesModal(meeting.name + ' — Slack Message', meeting.rawSlackMessage);
                          }}
                          className="flex items-center text-xs px-2 py-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        >
                          Slack
                        </button>
                      )}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`Mark ${meeting.name} as booked? They'll be removed from this list.`)) return;
                          try {
                            await updateDoc(doc(db, 'pendingMeetings', meeting.id), { status: 'booked', bookedAt: serverTimestamp() });
                            await fetchPendingMeetings();
                          } catch(err) { alert('Error: ' + err.message); }
                        }}
                        className="text-xs px-2 py-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        Mark Booked
                      </button>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`End all follow-ups for ${meeting.name}?`)) return;
                          try {
                            await updateDoc(doc(db, 'pendingMeetings', meeting.id), { followUpStatus: 'newsletter', followUpEndedBy: 'Ben', followUpEndedAt: serverTimestamp() });
                            await Promise.all([fetchPendingMeetings(), fetchTodaysFollowups()]);
                          } catch(err) { alert('Error: ' + err.message); }
                        }}
                        className="text-xs px-2 py-1.5 bg-gray-100 text-gray-500 rounded hover:bg-red-100 hover:text-red-600"
                        title="End follow-ups"
                      >
                        End
                      </button>
                    </div>
                    {renderLogSection(meeting, 'pendingMeetings')}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section 2: Off-Market Searchers */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-teal-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <GlobeAltIcon className="h-6 w-6 text-teal-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Off-Market Searchers</h3>
                <span className="ml-3 bg-teal-100 text-teal-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                  {offMarketSearchers.length}
                </span>
              </div>
              <button
                onClick={() => setShowOffMarketModal(true)}
                className="flex items-center px-3 py-1.5 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Searcher
              </button>
            </div>
          </div>
          <div className="p-6">
            <p className="text-xs text-gray-500 mb-3">Off-market deal sourcing contacts who haven't created an account</p>
            {offMarketSearchers.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No off-market searchers added yet</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {offMarketSearchers.map((searcher) => (
                  <div
                    key={searcher.id}
                    className={`border rounded-lg p-4 ${
                      searcher.status === 'responded'
                        ? 'border-green-200 bg-green-50'
                        : searcher.status === 'awaiting_response'
                        ? 'border-teal-200 bg-teal-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{searcher.name}</p>
                        <p className="text-xs text-gray-500">{searcher.email}</p>
                        {searcher.phoneNumber && (
                          <p className="text-xs text-gray-500">{searcher.phoneNumber}</p>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        searcher.status === 'responded'
                          ? 'bg-green-100 text-green-700'
                          : searcher.status === 'awaiting_response'
                          ? 'bg-teal-100 text-teal-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {searcher.status === 'responded' ? 'Responded' :
                         searcher.status === 'awaiting_response' ? 'Awaiting' : 'Pending'}
                      </span>
                    </div>
                    {searcher.notes && (
                      <p className="text-xs text-gray-600 mt-2">{searcher.notes}</p>
                    )}
                    <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                      <div>
                        {searcher.emailCount || 0} email(s) sent
                        {searcher.lastEmailSentAt && (
                          <span> • Last sent: {formatDate(searcher.lastEmailSentAt)}</span>
                        )}
                      </div>
                      {emailStatuses[searcher.email]?.replied && (
                        <div className="text-green-600 font-medium">
                          <CheckCircleIcon className="h-3.5 w-3.5 inline mr-1" />
                          Email replied {emailStatuses[searcher.email].repliedAt ? new Date(emailStatuses[searcher.email].repliedAt.toDate?.() || emailStatuses[searcher.email].repliedAt).toLocaleDateString() : ''}
                        </div>
                      )}
                      {searcher.lastRespondedAt && (
                        <div className="text-green-600">
                          Last response: {formatDate(searcher.lastRespondedAt)}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOffMarketFollowUp(searcher);
                        }}
                        className="flex-1 text-xs px-2 py-1 bg-teal-100 text-teal-700 rounded hover:bg-teal-200"
                      >
                        Follow Up
                      </button>
                      {searcher.phoneNumber && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openTextModal({ ...searcher, collection: 'offMarketSearchers' }); }}
                          className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          Text
                        </button>
                      )}
                      {searcher.phoneNumber && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleInitiateCall({ ...searcher, collection: 'offMarketSearchers' }); }}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          <PhoneArrowUpRightIcon className="h-3 w-3 inline mr-0.5" />Call
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkResponded(searcher.id);
                        }}
                        className="flex-1 text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        Mark Responded
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseOffMarket(searcher.id);
                        }}
                        className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                      >
                        Close
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEmailHistory(searcher.email, searcher.name?.split(' ')[0] || searcher.name);
                        }}
                        className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                      >
                        History
                      </button>
                    </div>
                    {renderLogSection(searcher, 'offMarketSearchers')}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section 3: Borrower Follow Up */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-indigo-50">
            <div className="flex items-center">
              <UserGroupIcon className="h-6 w-6 text-indigo-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Borrower Follow Up</h3>
              <span className="ml-3 bg-indigo-100 text-indigo-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {initialPrequalFilesNeeded.length + intakeListFollowups.length + newDealFollowups.length}
              </span>
            </div>
          </div>
          <div className="p-6 space-y-6">
            {/* Initial Prequal Files Needed - NEW SECTION */}
            <div>
              <div className="flex items-center mb-3">
                <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-2" />
                <h4 className="text-sm font-semibold text-gray-700">Initial Prequal Files Needed</h4>
                <span className="ml-2 text-xs text-gray-500">({initialPrequalFilesNeeded.length})</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">Borrowers who need to upload personal files (Personal Financial Statement) before prequalification</p>
              {initialPrequalFilesNeeded.length === 0 ? (
                <p className="text-sm text-gray-400 italic">All borrowers have uploaded initial files</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {initialPrequalFilesNeeded.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => navigate(`/loan-details/${item.id}`)}
                      className="border border-red-200 bg-red-50 rounded-lg p-4 cursor-pointer hover:bg-red-100 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{item.firstName} {item.lastName}</p>
                          <p className="text-xs text-gray-500">{item.email}</p>
                        </div>
                        <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                      </div>
                      <p className="text-xs text-red-700 mt-2">{item.reason}</p>
                      <div className="mt-2 text-xs text-gray-500">
                        {item.uploadedDocsCount || 0} document(s) uploaded so far
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Intake List Follow Up */}
            <div>
              <div className="flex items-center mb-3">
                <DocumentIcon className="h-5 w-5 text-amber-500 mr-2" />
                <h4 className="text-sm font-semibold text-gray-700">Intake List Follow Up</h4>
                <span className="ml-2 text-xs text-gray-500">({intakeListFollowups.length})</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">Prequalified borrowers who need to collect full intake documents</p>
              {intakeListFollowups.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No intake follow-ups needed</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {intakeListFollowups.map((item) => (
                    <div
                      key={item.id}
                      className="border border-amber-200 bg-amber-50 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between cursor-pointer" onClick={() => navigate(`/loan-details/${item.id}`)}>
                        <div>
                          <p className="font-medium text-gray-900">{item.firstName} {item.lastName}</p>
                          <p className="text-xs text-gray-500">{item.email}</p>
                        </div>
                        <ExclamationCircleIcon className="h-5 w-5 text-amber-500" />
                      </div>
                      <p className="text-xs text-amber-700 mt-2">{item.reason}</p>
                      <div className="mt-2 text-xs text-green-600 font-medium">
                        Prequalified: ${item.prequalAmount?.toLocaleString() || item.borrowerPrequalAmount?.toLocaleString() || '0'}
                      </div>
                      {emailStatuses[item.email] && (
                        <div className={`mt-2 text-xs font-medium ${emailStatuses[item.email].replied ? 'text-green-600' : 'text-gray-500'}`}>
                          {emailStatuses[item.email].replied ? (
                            <>
                              <CheckCircleIcon className="h-3.5 w-3.5 inline mr-1" />
                              Replied {emailStatuses[item.email].repliedAt ? new Date(emailStatuses[item.email].repliedAt.toDate?.() || emailStatuses[item.email].repliedAt).toLocaleDateString() : ''}
                            </>
                          ) : (
                            <>
                              <ClockIcon className="h-3.5 w-3.5 inline mr-1" />
                              Emailed {emailStatuses[item.email].lastSentAt ? new Date(emailStatuses[item.email].lastSentAt).toLocaleDateString() : ''}
                            </>
                          )}
                        </div>
                      )}
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEmailModal(item.email, item.firstName, 'intake');
                          }}
                          className="flex-1 flex items-center justify-center text-xs px-2 py-1.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
                        >
                          <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                          {emailStatuses[item.email] ? 'Send Another' : 'Send Email'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEmailHistory(item.email, item.firstName);
                          }}
                          className="text-xs px-2 py-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        >
                          History
                        </button>
                      </div>
                      {renderLogSection(item, 'loanApplications')}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* New Deal Follow Up */}
            <div>
              <div className="flex items-center mb-3">
                <BriefcaseIcon className="h-5 w-5 text-blue-500 mr-2" />
                <h4 className="text-sm font-semibold text-gray-700">New Deal Follow Up</h4>
                <span className="ml-2 text-xs text-gray-500">({newDealFollowups.length})</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">Borrowers who haven't uploaded a new deal in a while</p>
              {newDealFollowups.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No new deal follow-ups needed</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {newDealFollowups.map((item) => (
                    <div
                      key={item.id}
                      className="border border-blue-200 bg-blue-50 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between cursor-pointer" onClick={() => navigate(`/loan-details/${item.id}`)}>
                        <div>
                          <p className="font-medium text-gray-900">{item.firstName} {item.lastName}</p>
                          <p className="text-xs text-gray-500">{item.email}</p>
                        </div>
                        <ClockIcon className="h-5 w-5 text-blue-500" />
                      </div>
                      <p className="text-xs text-blue-700 mt-2">{item.reason}</p>
                      <div className="mt-2 text-xs text-gray-500">
                        Last activity: {getDaysSince(item.lastActivity)}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEmailModal(item.email, item.firstName, 'newDeal');
                          }}
                          className="flex-1 flex items-center justify-center text-xs px-2 py-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                          Send Email
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEmailHistory(item.email, item.firstName);
                          }}
                          className="text-xs px-2 py-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        >
                          History
                        </button>
                      </div>
                      {renderLogSection(item, 'loanApplications')}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section 2: Business Follow Up */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-6 w-6 text-green-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Business Follow Up</h3>
              <span className="ml-3 bg-green-100 text-green-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {businessFollowups.length}
              </span>
            </div>
          </div>
          <div className="p-6">
            <p className="text-xs text-gray-500 mb-3">Prequalified businesses where borrower intake has not been collected yet</p>
            {businessFollowups.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No business follow-ups needed</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {businessFollowups.map((item) => (
                  <div
                    key={item.id}
                    className="border border-green-200 bg-green-50 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between cursor-pointer" onClick={() => navigate(`/prequalification-details/${item.id}`)}>
                      <div>
                        <p className="font-medium text-gray-900">{item.businessName || 'Unnamed Business'}</p>
                        <p className="text-xs text-gray-500">Broker: {item.brokerName}</p>
                      </div>
                      <ExclamationCircleIcon className="h-5 w-5 text-green-500" />
                    </div>
                    <p className="text-xs text-green-700 mt-2">{item.reason}</p>
                    {item.projectedSalePrice && (
                      <div className="mt-2 text-xs text-gray-500">
                        Sale Price: ${Number(item.projectedSalePrice).toLocaleString()}
                      </div>
                    )}
                    {item.brokerEmail && (
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEmailModal(item.brokerEmail, item.brokerName?.split(' ')[0] || 'Broker', 'business', item.businessName);
                          }}
                          className="flex-1 flex items-center justify-center text-xs px-2 py-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                          Email Broker
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEmailHistory(item.brokerEmail, item.brokerName?.split(' ')[0] || 'Broker');
                          }}
                          className="text-xs px-2 py-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        >
                          History
                        </button>
                      </div>
                    )}
                    {renderLogSection(item, 'prequalificationApplications')}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section 3: Broker Follow Up */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-purple-50">
            <div className="flex items-center">
              <UserGroupIcon className="h-6 w-6 text-purple-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Broker Follow Up</h3>
              <span className="ml-3 bg-purple-100 text-purple-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {brokerFollowups.length}
              </span>
            </div>
          </div>
          <div className="p-6">
            <p className="text-xs text-gray-500 mb-3">Brokers who haven't uploaded new borrowers or deals in a while</p>
            {brokerFollowups.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No broker follow-ups needed</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {brokerFollowups.map((item) => (
                  <div
                    key={item.id}
                    className="border border-purple-200 bg-purple-50 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-3">
                      {/* Logo or upload placeholder */}
                      <div className="flex-shrink-0">
                        {item.logoUrl ? (
                          <div className="relative group">
                            <img
                              src={item.logoUrl}
                              alt="Broker Logo"
                              className="h-12 w-12 rounded-md object-cover border border-gray-200"
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBrokerLogoClick(item.id);
                              }}
                              className="absolute inset-0 bg-black bg-opacity-50 rounded-md opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
                              title="Replace logo"
                            >
                              <PhotoIcon className="h-5 w-5 text-white" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBrokerLogoClick(item.id);
                            }}
                            className="h-12 w-12 rounded-md border-2 border-dashed border-purple-300 flex items-center justify-center hover:border-purple-400 hover:bg-purple-100 transition-colors"
                            title="Upload logo"
                          >
                            <PhotoIcon className="h-5 w-5 text-purple-400" />
                          </button>
                        )}
                      </div>
                      {/* Broker info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{(item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : '') || item.brokerAgeName || item.name || item.businessName || item.companyName || 'Unknown Broker'}</p>
                            <p className="text-xs text-gray-500 truncate">{item.email}</p>
                          </div>
                          <ClockIcon className="h-5 w-5 text-purple-500 flex-shrink-0" />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-purple-700 mt-2">{item.reason}</p>
                    <div className="mt-2 flex justify-between text-xs text-gray-500">
                      <span>Listings: {item.totalListings}</span>
                      <span>Last activity: {getDaysSince(item.lastActivity)}</span>
                    </div>
                    {item.email && (
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const brokerName = item.name || item.businessName || 'Broker';
                            openEmailModal(item.email, brokerName.split(' ')[0], 'broker');
                          }}
                          className="flex-1 flex items-center justify-center text-xs px-2 py-1.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                        >
                          <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                          Send Email
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const brokerName = item.name || item.businessName || 'Broker';
                            openEmailHistory(item.email, brokerName.split(' ')[0]);
                          }}
                          className="text-xs px-2 py-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        >
                          History
                        </button>
                      </div>
                    )}
                    {renderLogSection(item, 'brokers')}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {totalFollowups === 0 && !loading && (
          <div className="text-center py-12">
            <PhoneIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">All caught up!</h3>
            <p className="mt-1 text-sm text-gray-500">No follow-ups needed at this time.</p>
          </div>
        )}
      </div>
    );
  };

  // Scraper functions
  const SCRAPER_API_URL = 'https://feisty-renewal-production-c9f9.up.railway.app/api/scraper';

  const connectToScraperStream = () => {
    const eventSource = new EventSource(`${SCRAPER_API_URL}/stream`);

    eventSource.onopen = () => {
      setScraperConnected(true);
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'status') {
          setIsScraperRunning(data.running);
        } else if (data.type === 'clear') {
          setScraperLogs([]);
          setTextedBrokersToday([]);
        } else if (data.type === 'output' || data.type === 'error' || data.type === 'info') {
          setScraperLogs(prev => [...prev.slice(-499), data]);

          // Detect texted brokers from log messages
          const msg = data.message || '';

          // Parse structured format: TEXTED_BROKER|name|phone|listingId|url
          if (msg.includes('TEXTED_BROKER|')) {
            const parts = msg.split('TEXTED_BROKER|')[1]?.split('|');
            if (parts && parts.length >= 4) {
              const [name, phone, listingId, ...urlParts] = parts;
              const listingUrl = urlParts.join('|'); // URL might contain |

              const brokerInfo = {
                name: name?.trim() || 'Unknown',
                phone: phone?.trim() || 'N/A',
                listingId: listingId?.trim() || 'N/A',
                listingUrl: listingUrl?.trim() || null,
                timestamp: new Date().toLocaleTimeString()
              };

              // Avoid duplicates
              setTextedBrokersToday(prev => {
                const exists = prev.some(b => b.phone === brokerInfo.phone && b.listingId === brokerInfo.listingId);
                if (exists) return prev;
                return [...prev, brokerInfo];
              });
            }
          }
        }
      } catch (e) {
        console.error('Error parsing SSE message:', e);
      }
    };

    eventSource.onerror = () => {
      setScraperConnected(false);
      eventSource.close();
    };

    return eventSource;
  };

  const runScraper = async () => {
    try {
      const body = startListingId ? { startListingId: parseInt(startListingId) } : {};
      const response = await fetch(`${SCRAPER_API_URL}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        const error = await response.json();
        alert(error.error || 'Failed to start scraper');
      }
    } catch (error) {
      alert('Failed to connect to scraper server. Make sure the server is running on port 3001.');
    }
  };

  const stopScraper = async () => {
    try {
      await fetch(`${SCRAPER_API_URL}/stop`, { method: 'POST' });
    } catch (error) {
      console.error('Failed to stop scraper:', error);
    }
  };

  const clearScraperLogs = async () => {
    try {
      await fetch(`${SCRAPER_API_URL}/clear-logs`, { method: 'POST' });
      setScraperLogs([]);
    } catch (error) {
      console.error('Failed to clear logs:', error);
    }
  };

  const fetchSavedStartId = async () => {
    try {
      const response = await fetch(`${SCRAPER_API_URL}/get-start-id`);
      const data = await response.json();
      if (data.startId) {
        setSavedStartId(data.startId);
      }
    } catch (error) {
      console.error('Failed to fetch saved start ID:', error);
    }
  };

  const setStartIdOnServer = async () => {
    if (!startListingId) {
      alert('Please enter a listing ID');
      return;
    }
    try {
      const response = await fetch(`${SCRAPER_API_URL}/set-start-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startListingId: parseInt(startListingId) })
      });
      const data = await response.json();
      if (response.ok) {
        setSavedStartId(parseInt(startListingId));
        alert(`Starting position set to ${startListingId}`);
      } else {
        alert(data.error || 'Failed to set starting position');
      }
    } catch (error) {
      alert('Failed to connect to server');
    }
  };

  // Auto-scroll logs to bottom (only within the terminal container)
  React.useEffect(() => {
    if (scraperTerminalRef.current) {
      scraperTerminalRef.current.scrollTop = scraperTerminalRef.current.scrollHeight;
    }
  }, [scraperLogs]);

  // Connect to scraper stream when scraper tab is active
  React.useEffect(() => {
    if (activeSection === 'scraper') {
      const eventSource = connectToScraperStream();
      fetchSavedStartId(); // Get current saved position
      return () => eventSource.close();
    }
  }, [activeSection]);

  const renderScraper = () => {
    const getLogColor = (type) => {
      switch (type) {
        case 'error': return 'text-red-400';
        case 'info': return 'text-blue-400';
        default: return 'text-green-400';
      }
    };

    return (
      <div className="space-y-6">
        {/* Control Panel */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <CommandLineIcon className="h-8 w-8 text-indigo-600 mr-3" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">BizBuySell Scraper</h2>
                <p className="text-sm text-gray-500">
                  Scrapes listings and reaches out to brokers via text/email
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* Connection Status */}
              <div className={`flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                scraperConnected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                <span className={`w-2 h-2 rounded-full mr-2 ${
                  scraperConnected ? 'bg-green-500' : 'bg-red-500'
                }`}></span>
                {scraperConnected ? 'Server Connected' : 'Server Disconnected'}
              </div>

              {/* Run/Stop Buttons - Both always visible */}
              <button
                onClick={runScraper}
                disabled={!scraperConnected || isScraperRunning}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                Run Scraper
              </button>
              <button
                onClick={stopScraper}
                disabled={!isScraperRunning}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                </svg>
                Stop Scraper
              </button>

              <button
                onClick={clearScraperLogs}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Clear Logs
              </button>
            </div>
          </div>

          {/* Starting Listing ID */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Start from Listing ID:</label>
              <input
                type="number"
                value={startListingId}
                onChange={(e) => setStartListingId(e.target.value)}
                placeholder="e.g. 2368600"
                disabled={isScraperRunning}
                className="w-48 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
              />
              <span className="text-sm text-gray-500">
                {startListingId ? `Will start from ${parseInt(startListingId).toLocaleString()}` : 'Enter ID then click Run Scraper'}
              </span>
            </div>
          </div>

          {/* Server Instructions */}
          {!scraperConnected && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <div className="flex">
                <ExclamationCircleIcon className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Scraper server not running</p>
                  <p>Start the server by running in terminal:</p>
                  <code className="block mt-2 bg-amber-100 px-3 py-2 rounded font-mono text-xs">
                    cd /Users/maxkrakow/Desktop/sba/my-app/server && npm install && npm start
                  </code>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Terminal Output */}
        <div className="bg-gray-900 shadow rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
            <div className="flex items-center">
              <div className="flex space-x-2 mr-4">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
              <span className="text-gray-400 text-sm font-mono">Scraper Output</span>
            </div>
            {isScraperRunning && (
              <div className="flex items-center text-green-400 text-sm">
                <span className="animate-pulse mr-2">●</span>
                Running...
              </div>
            )}
          </div>
          <div ref={scraperTerminalRef} className="h-96 overflow-y-auto p-4 font-mono text-sm">
            {scraperLogs.length === 0 ? (
              <div className="text-gray-500 text-center py-8">
                No logs yet. Click "Run Scraper" to start.
              </div>
            ) : (
              scraperLogs.map((log, idx) => (
                <div key={idx} className={`${getLogColor(log.type)} mb-1`}>
                  <span className="text-gray-500 mr-2">
                    [{new Date(log.timestamp).toLocaleTimeString()}]
                  </span>
                  {log.message}
                </div>
              ))
            )}
            <div ref={scraperLogsEndRef} />
          </div>
        </div>

        {/* Brokers Texted Today - Collapsible Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <button
            onClick={() => setShowTextedBrokers(!showTextedBrokers)}
            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50"
          >
            <div className="flex items-center">
              <PhoneIcon className="h-6 w-6 text-green-600 mr-3" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Brokers Texted Today
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {textedBrokersToday.length}
                  </span>
                </h3>
                <p className="text-sm text-gray-500">Brokers contacted during this scraping session</p>
              </div>
            </div>
            <ChevronDownIcon className={`h-5 w-5 text-gray-400 transform transition-transform ${showTextedBrokers ? 'rotate-180' : ''}`} />
          </button>

          {showTextedBrokers && (
            <div className="border-t border-gray-200">
              {textedBrokersToday.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <PhoneIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p>No brokers texted yet in this session.</p>
                  <p className="text-sm mt-1">Run the scraper to see texted brokers here.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Listing ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Link</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {textedBrokersToday.map((broker, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {broker.timestamp}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {broker.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                            {broker.phone}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                            {broker.listingId || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {broker.listingUrl ? (
                              <a
                                href={broker.listingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 hover:text-indigo-900 hover:underline"
                              >
                                View
                              </a>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* GoHighLevel Conversations Toggle */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <button
            onClick={() => setShowConversations(!showConversations)}
            className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50"
          >
            <div className="flex items-center">
              <EnvelopeIcon className="h-6 w-6 text-indigo-600 mr-3" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">GoHighLevel Conversations</h3>
                <p className="text-sm text-gray-500">View and respond to messages from scraped contacts</p>
              </div>
            </div>
            <ChevronDownIcon className={`h-5 w-5 text-gray-400 transform transition-transform ${showConversations ? 'rotate-180' : ''}`} />
          </button>

          {showConversations && (
            <div className="border-t border-gray-200">
              <div className="p-4 bg-gray-50 text-center">
                <a
                  href="https://app.cerumsolutions.com/v2/location/jt2cla2yKK13pavtLwNJ/conversations/conversations"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Open GoHighLevel Conversations
                  <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <p className="mt-3 text-xs text-gray-500">
                  Opens in a new tab. Use this to view and reply to text messages sent by the scraper.
                </p>
              </div>
              {/* Iframe embed - Note: This may be blocked by GoHighLevel's X-Frame-Options */}
              <div className="relative" style={{ height: '600px' }}>
                <iframe
                  src="https://app.cerumsolutions.com/v2/location/jt2cla2yKK13pavtLwNJ/conversations/conversations"
                  className="w-full h-full border-0"
                  title="GoHighLevel Conversations"
                  onError={() => console.log('Iframe blocked by X-Frame-Options')}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 pointer-events-none opacity-0 hover:opacity-100 transition-opacity">
                  <p className="text-gray-500 text-sm">
                    If conversations don't load, use the button above to open in a new tab
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'brief':
        return renderTodaysBrief();
      case 'brokerHub':
        return <BrokerHub onOpenSection={setActiveSection} />;
      case 'prequalifications':
        return renderCombinedPrequalifications();
      case 'fullDeals':
        return renderFullDeals();
      case 'brokers':
        return (
          <BrokersTab
            pendingBrokers={pendingBrokers}
            onOpenFollowups={() => setActiveSection('followups')}
            onHidePendingBrokers={(ids) => {
              const hidden = new Set(ids);
              setPendingBrokers((prev) => prev.filter((b) => !hidden.has(b.id)));
            }}
          />
        );
      case 'commissions':
        return <SalesCommissionHub canManageReps />;
      case 'followups':
        return renderFollowups();
      case 'scraper':
        return renderScraper();
      default:
        return renderTodaysBrief();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {/* Header with title and buttons */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Prequalification Portal</h1>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowUploadPrequalModal(true)}
                className="inline-flex items-center px-4 py-2 border border-blue-200 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
              >
                <ArrowUpTrayIcon className="h-5 w-5 mr-1" />
                Upload Prequalification
              </button>
              <button
                onClick={openNewListingModal}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <PlusIcon className="h-5 w-5 mr-1" />
                New Listing
              </button>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.name}
                    onClick={() => setActiveSection(tab.section)}
                    className={classNames(
                      activeSection === tab.section
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                      'group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium'
                    )}
                  >
                    <Icon
                      className={classNames(
                        activeSection === tab.section ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500',
                        '-ml-0.5 mr-2 h-5 w-5'
                      )}
                    />
                    {tab.name}
                    {tab.section === 'prequalifications' && (borrowerSignups.length + businessPrequals.length) > 0 && (
                      <span className="ml-2 bg-indigo-100 text-indigo-600 py-0.5 px-2 rounded-full text-xs">
                        {borrowerSignups.length + businessPrequals.length}
                      </span>
                    )}
                    {tab.section === 'fullDeals' && fullDeals.length > 0 && (
                      <span className="ml-2 bg-emerald-100 text-emerald-700 py-0.5 px-2 rounded-full text-xs">
                        {fullDeals.length}
                      </span>
                    )}
                    {tab.section === 'followups' && (initialPrequalFilesNeeded.length + intakeListFollowups.length + newDealFollowups.length + businessFollowups.length + brokerFollowups.length + offMarketSearchers.length + pendingSearchers.length + pendingBrokers.length + pendingMeetings.length) > 0 && (
                      <span className="ml-2 bg-orange-100 text-orange-600 py-0.5 px-2 rounded-full text-xs">
                        {initialPrequalFilesNeeded.length + intakeListFollowups.length + newDealFollowups.length + businessFollowups.length + brokerFollowups.length + offMarketSearchers.length + pendingSearchers.length + pendingBrokers.length + pendingMeetings.length}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Search */}
          <div className="mt-4">
            <div className="relative rounded-md shadow-sm max-w-md">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                className="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {renderSection()}
      </main>

      <UploadBrokerPrequalificationModal
        open={showUploadPrequalModal}
        onClose={() => setShowUploadPrequalModal(false)}
        onUploaded={() => {
          fetchBusinessPrequals();
          setActiveSection('prequalifications');
        }}
      />

      {/* New Listing Modal */}
      {showNewListingModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowNewListingModal(false)}
            />

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowNewListingModal(false)}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div>
                <div className="text-center">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Create New Listing
                  </h3>

                  {/* Step 1: Choose type */}
                  {!newListingType && (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-500 mb-4">
                        Who is this listing for?
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => setNewListingType('broker')}
                          className="flex flex-col items-center p-6 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
                        >
                          <BuildingOfficeIcon className="h-12 w-12 text-indigo-600 mb-2" />
                          <span className="font-medium text-gray-900">Broker</span>
                          <span className="text-xs text-gray-500 mt-1">Prequalification listing</span>
                        </button>
                        <button
                          onClick={() => setNewListingType('borrower')}
                          className="flex flex-col items-center p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
                        >
                          <UserGroupIcon className="h-12 w-12 text-green-600 mb-2" />
                          <span className="font-medium text-gray-900">Borrower</span>
                          <span className="text-xs text-gray-500 mt-1">Add to borrower's deals</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Broker selection */}
                  {newListingType === 'broker' && (
                    <div className="space-y-4">
                      <button
                        onClick={() => setNewListingType(null)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 mb-2"
                      >
                        &larr; Back
                      </button>
                      <p className="text-sm text-gray-500 mb-4">
                        Select a broker for this listing
                      </p>
                      <div className="text-left">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Broker
                        </label>
                        <select
                          value={selectedBrokerId}
                          onChange={(e) => setSelectedBrokerId(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="">Select a broker...</option>
                          {allBrokers.map((broker) => (
                            <option key={broker.id} value={broker.id}>
                              {broker.name} ({broker.email})
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={createBrokerListing}
                        disabled={!selectedBrokerId || isCreatingListing}
                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {isCreatingListing ? 'Creating...' : 'Create Broker Listing'}
                      </button>
                    </div>
                  )}

                  {/* Step 2: Borrower selection */}
                  {newListingType === 'borrower' && (
                    <div className="space-y-4">
                      <button
                        onClick={() => setNewListingType(null)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 mb-2"
                      >
                        &larr; Back
                      </button>
                      <p className="text-sm text-gray-500 mb-4">
                        Select a borrower to add a listing to
                      </p>
                      <div className="text-left">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Borrower
                        </label>
                        <select
                          value={selectedBorrowerId}
                          onChange={(e) => setSelectedBorrowerId(e.target.value)}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="">Select a borrower...</option>
                          {allBorrowers.map((borrower) => (
                            <option key={borrower.id} value={borrower.id}>
                              {borrower.displayName} ({borrower.email})
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={createBorrowerListing}
                        disabled={!selectedBorrowerId || isCreatingListing}
                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {isCreatingListing ? 'Navigating...' : 'Go to Borrower Profile'}
                      </button>
                      <p className="text-xs text-gray-500">
                        You'll be taken to the borrower's profile to add a new business listing.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Off-Market Modal */}
      <OffMarketModal
        isOpen={showOffMarketModal}
        onClose={() => setShowOffMarketModal(false)}
        onSuccess={fetchOffMarketSearchers}
      />

      {/* Follow-up Email Modal */}
      <FollowUpEmailModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setCurrentEmailSearcher(null);
        }}
        recipientEmail={emailModalData.recipientEmail}
        recipientName={emailModalData.recipientName}
        templateType={emailModalData.templateType}
        businessName={emailModalData.businessName}
        customSubject={emailModalData.customSubject}
        customBody={emailModalData.customBody}
        onSuccess={async () => {
          if (currentEmailSearcher) await handleOffMarketEmailSent();
          // Auto-log follow-up when email sent from Today's Action Items
          const autoRec = emailModalData.autoLogRecord;
          if (autoRec && autoRec.collection && autoRec.id) {
            try {
              const recRef = doc(db, autoRec.collection, autoRec.id);
              const currentLogs = autoRec.followUpLog || [];
              const seqType = getSequenceType(autoRec);
              const interval = AUTO_INTERVAL[seqType] || DEFAULT_FOLLOWUP_INTERVAL_DAYS;
              const nextDate = new Date();
              nextDate.setDate(nextDate.getDate() + interval);
              nextDate.setHours(9, 0, 0, 0);
              const logEntry = {
                timestamp: new Date().toISOString(),
                type: 'Email',
                notes: `Email sent: ${emailModalData.customSubject || emailModalData.templateType || 'follow-up'}`,
                loggedBy: 'Ben',
                nextDateSet: nextDate.toISOString().split('T')[0]
              };
              await updateDoc(recRef, {
                followUpLog: arrayUnion(logEntry),
                lastContactDate: serverTimestamp(),
                lastContactType: 'Email',
                NextActionDate: Timestamp.fromDate(nextDate),
                lastLogNotes: logEntry.notes,
                followUpCount: (autoRec.followUpCount || currentLogs.length) + 1,
              });
              await Promise.all([fetchFollowups(), fetchPendingSearchers(), fetchPendingBrokers(), fetchPendingMeetings(), fetchTodaysFollowups(), fetchOffMarketSearchers()]);
            } catch (e) { console.error('Auto-log error:', e); }
          }
        }}
      />

      {/* Email History Modal */}
      {showEmailHistoryModal && emailHistoryTarget && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4 pb-3 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Email History</h3>
                <p className="text-sm text-gray-500">{emailHistoryTarget.name} — {emailHistoryTarget.email}</p>
              </div>
              <button
                onClick={() => { setShowEmailHistoryModal(false); setEmailHistoryTarget(null); }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <EmailHistoryPanel
              recipientEmail={emailHistoryTarget.email}
              maxItems={20}
              className="max-h-[60vh] overflow-y-auto"
            />
          </div>
        </div>
      )}

      {/* Pending Searcher Modal */}
      <PendingSearcherModal
        isOpen={showPendingSearcherModal}
        onClose={() => setShowPendingSearcherModal(false)}
        onSuccess={fetchPendingSearchers}
      />

      {/* Hidden file input for broker logo upload */}
      <input
        type="file"
        ref={brokerLogoInputRef}
        onChange={handleBrokerLogoUpload}
        accept="image/*"
        className="hidden"
      />

      {/* Follow-Up Log Modal */}
      {showLogModal && logModalRecord && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Log Follow-Up</h3>
              <button
                onClick={() => { setShowLogModal(false); setLogModalRecord(null); }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {logModalRecord.name || logModalRecord.firstName || 'Record'}
            </p>

            <div className="space-y-4">
              {/* Contact Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Type</label>
                <select
                  value={logFormData.contactType}
                  onChange={(e) => setLogFormData(prev => ({ ...prev, contactType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="Call">Call</option>
                  <option value="Email">Email</option>
                  <option value="Text">Text</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={logFormData.notes}
                  onChange={(e) => setLogFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="What happened, any relevant info..."
                />
              </div>

              {/* Next Follow-Up Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Next Follow-Up Date <span className="text-gray-400 font-normal">(optional — defaults to +{DEFAULT_FOLLOWUP_INTERVAL_DAYS} days)</span>
                </label>
                <input
                  type="date"
                  value={logFormData.nextFollowUp}
                  onChange={(e) => setLogFormData(prev => ({ ...prev, nextFollowUp: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Save Button */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => { setShowLogModal(false); setLogModalRecord(null); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveLog}
                  disabled={savingLog}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
                >
                  {savingLog ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Granola Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Notes — {notesModalName}</h3>
              <button
                onClick={() => setShowNotesModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{notesModalContent || 'No notes available.'}</p>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Text Modal (Project Blue) */}
      {showTextModal && textModalRecord && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Text — {textModalRecord.name || 'Unknown'}</h3>
                <p className="text-xs text-gray-500">{textModalRecord.phoneNumber || textModalRecord.phone}</p>
              </div>
              <button onClick={() => setShowTextModal(false)} className="text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Text history */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 max-h-60 overflow-y-auto">
              <p className="text-xs font-medium text-gray-500 mb-2">Recent Texts</p>
              {loadingTextHistory ? (
                <p className="text-xs text-gray-400 italic">Loading...</p>
              ) : textHistory.length === 0 ? (
                <p className="text-xs text-gray-400 italic">No text history yet</p>
              ) : (
                <div className="space-y-2">
                  {textHistory.map((log, idx) => (
                    <div key={idx} className="bg-white rounded p-2 border border-gray-100">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[10px] text-gray-400">
                          {log.sentAt ? new Date(log.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">{log.sentBy || 'Ben'}</span>
                      </div>
                      <p className="text-xs text-gray-700">{log.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Compose */}
            <div className="space-y-3">
              <textarea
                value={textMessage}
                onChange={(e) => setTextMessage(e.target.value)}
                placeholder="Type your text message..."
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-400">{textMessage.length} chars</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowTextModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendText}
                    disabled={sendingText || !textMessage.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingText ? 'Sending...' : 'Send Text'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BenPortal;
